import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, logActivity } from '../lib/supabase';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
// v3.5.67: per-user quote-type permissions
import { userCanCreateQuoteType } from '../lib/quotePermissions';
import { calcQuote, lookupZip, fmt$, fmt$0, fmtPct, gmColor, gmBg, getBurdenedRate, getSeniorBurdenedRate, getContractTermOptions } from '../lib/pricing';
// v3.5.92.0: unified totals utility — single source of truth for
// quotes.totals shape across all five quote types. See CLAUDE.md §10b.
// This deploy migrates Managed IT only; Voice/Bundle/Multi-Site/FlexIT
// migrate in v3.5.92.1–.2.
import { buildTotals } from '../lib/quoteTotals';
import { applyManualDiscounts, discountedResult } from '../lib/manualDiscounts';
import { applyOneTimeDiscounts } from '../lib/oneTimeDiscounts';
import { loadPackageIncludes } from '../lib/packageIncludes';
import { loadPackageCostComponents } from '../lib/packageCostComponents';
import PackageIncludes from '../components/PackageIncludes';
import AddendumsModal from '../components/AddendumsModal';
import ServiceOrderPreviewModal from '../components/ServiceOrderPreviewModal';
import EngagementPanel from '../components/EngagementPanel';
// v3.5.99.7.13: ETF Calculator (per-quote)
import ETFCalculatorModal from '../components/ETFCalculatorModal';
import { loadAddendumTemplates, validateAddendums } from '../lib/addendums';
import GMBadge from '../components/GMBadge';
import { calcFlexBlock } from '../lib/flexTime';
import { writeQuoteUrlToDeal, searchDeals, getDealFull, createDeal, updateDeal, updateDealDescription, captureHubspotSnapshot, pushHubspotGapFill, pushHubspotOverwrite } from '../lib/hubspot';
import QuoteNotes    from '../components/QuoteNotes';
import QuoteHistory  from '../components/QuoteHistory';
import { saveQuoteVersion } from '../lib/quoteVersions';
import { ReviewBanner } from '../components/SendForReview';
import QuoteSaveBar from '../components/QuoteSaveBar';
import RepSelector from '../components/RepSelector';
import ThreatProfileInfo from '../components/ThreatProfileInfo';
import DomainsInfo from '../components/DomainsInfo';
import HubSpotConnect from '../components/HubSpotConnect';
import SPTConnect    from '../components/SPTConnect';
import MarketRateCard from '../components/MarketRateCard';
import MarketStatusBadge from '../components/MarketStatusBadge';
import { staticTierToMILabel, getOrAnalyzeMarket, getRateSheet, tierColor, tierBg } from '../lib/marketRates';
import { DocumentsPanel } from '../components/RateSheetModal';
import FlexTimeSelector from '../components/FlexTimeSelector';
import FlexTimeMeter    from '../components/FlexTimeMeter';
import OnboardingIncentive, { formatIncentiveForExport } from '../components/OnboardingIncentive';
import ManualDiscounts from '../components/ManualDiscounts';
import OneTimeDiscounts from '../components/OneTimeDiscounts';
// v3.8.0 — Unified-UX workspace shell (flag-gated, default off). Legacy layout
// is untouched; when ferrumiq_v3_quote is enabled the page renders via these.
import { WorkspaceShell, QuoteHeader, LiveTotalCard, RailCard, SubCard, BigNumber, Button as V3Button, Pill as V3Pill } from '../components/v3';
import { isQuoteLocked, QUOTE_LOCK_REASON } from '../lib/quoteLock';
import LockedQuoteBanner from '../components/LockedQuoteBanner';
import QuoteDocuments from '../components/QuoteDocuments';
import { v3 } from '../lib/styleTokens';
import AgreementEffectiveDate from '../components/AgreementEffectiveDate';
import RecommendedDeclinedEditor from '../components/RecommendedDeclinedEditor';
import AssumptionsModal from '../components/AssumptionsModal';

const DEF_INPUTS = {
  users:0, sharedMailboxes:0, workstations:0, endpoints:0,
  mobileDevices:0,
  servers:0, locations:0, cloudTenants:0, vendors:0,
  // v3.5.96.0: domains as a new infrastructure input alongside cloud
  // tenants. Default 0; rep enters count manually. Onboarding contribution
  // = domains × pricing_settings.onboarding_per_domain (default $99 per
  // pricing.js fallback). Products can use 'domain' as a qty_driver to
  // bill per-domain (DNS management, certificate renewal, monitoring,
  // etc.). Existing drafts saved pre-v3.5.96.0 load with domains
  // undefined and the merge in the load handler will fill 0 from this
  // default — no migration needed.
  domains:0,
  requestedCoverage:'business_hours', compliance:'none',
  // v3.5.58: Industry Risk default is 'none' (no ITSO uplift) so a fresh
  // quote starts at zero advisory cost. Reps explicitly select Low / Medium /
  // High to surface the ITSO Advisory line. Existing saved drafts default to
  // 'low' for back-compat (see allInputs merge in load handler).
  industryRisk:'none', complexity:'low', contractTerm:24,
  // v3.5.58: execReporting removed — Executive Reporting Required toggle was
  // retired alongside the flat-rate Strategic Advisory products. Any saved
  // quote with execReporting:true in inputs is harmless (engine no longer
  // reads it).
  selectedProducts:[],
};

export default function QuotePage() {
  const { id } = useParams();
  const { packages, products, marketTiers, settings, productsByCategory, exclusiveGroups, loading: configLoading } = useConfig();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // v3.5.67: quote-type permission gate. New quotes (no id) require the
  // permission; existing quotes are loaded read-only when not permitted so
  // reps don't lose access to in-flight work mid-stream during permission
  // changes. Admins always pass. RLS enforces server-side independently.
  // v3.5.69: moved below useNavigate() — the previous placement (between
  // useAuth and useNavigate) caused a temporal-dead-zone ReferenceError
  // because the gate's useEffect dependency array referenced `navigate`
  // before its const declaration. Reading navigate INSIDE the effect
  // callback is fine (closures capture by name at call time) but listing
  // it in the deps array forces evaluation at hook-setup, which is during
  // the TDZ window. Order of operations: hooks first, derived values and
  // effects after.
  const _isNewQuote = !id;
  const _hasPermission = userCanCreateQuoteType(profile, 'managed_it');
  const isReadOnly = !_isNewQuote && profile != null && !_hasPermission;
  useEffect(() => {
    if (_isNewQuote && profile != null && !_hasPermission) {
      navigate('/quotes', { replace: true, state: { permissionDenied: 'managed_it' } });
    }
  }, [profile, _isNewQuote, _hasPermission, navigate]);

  // ── Handle conversion from Bundle (unbundle → IT) ────────────────────────
  useEffect(() => {
    const from = location.state?.fromBundle;
    if (!from || from.type !== 'it' || configLoading || !packages.length || !marketTiers.length) return;
    setRecipientBiz(from.clientName       || '');
    setProposalName(from.proposalName     || '');
    setRecipientContact(from.recipientContact || '');
    setRecipientEmail(from.recipientEmail     || '');
    setRecipientAddress(from.recipientAddress || '');
    setRecipientPhone(from.recipientPhone || '');
    setRecipientMobilePhone(from.recipientMobilePhone || '');
    setHubContactId(from.hubspotContactId || '');
    setHubCompanyId(from.hubspotCompanyId || '');
    setHubspotSnapshot(from.hubspotSnapshot || null);
    setNotificationCc(from.notificationCc || '');
    setClientZip(from.clientZip || '');
    setDealDescription(from.notes || '');
    setHubDealId(from.hubDealId   || '');
    setHubDealUrl(from.hubDealUrl  || '');
    setHubDealName(from.hubDealName || '');
    if (from.marketTier) { const t = marketTiers.find(t => t.tier_key === from.marketTier); if (t) setSelectedMkt(t); }
    if (from.packageName) { const p = packages.find(p => p.name === from.packageName); if (p) setSelectedPkg(p); }
    if (from.inputs) setInputs(prev => ({ ...prev, ...from.inputs }));
  }, [location.state, configLoading, packages, marketTiers]);

  // ── Quote metadata ────────────────────────────────────────────────────────
  const [proposalName,    setProposalName]    = useState('');
  const [recipientBiz,    setRecipientBiz]    = useState('');
  const [recipientContact,setRecipientContact]= useState('');
  const [recipientEmail,  setRecipientEmail]  = useState('');
  const [recipientAddress,setRecipientAddress]= useState('');
  // v3.5.99.7.3: client phone/mobile pulled from HubSpot in connectDeal.
  // Stored separately so Twilio SMS can target mobile specifically.
  const [recipientPhone,  setRecipientPhone]  = useState('');
  const [recipientMobilePhone, setRecipientMobilePhone] = useState('');
  // v3.5.99.7.4: HubSpot write-back state. contactId/companyId route
  // updateContact/updateCompany calls. snapshot captures HubSpot's original
  // values at connectDeal time so gap-fill knows which fields to push.
  const [hubContactId, setHubContactId] = useState('');
  const [hubCompanyId, setHubCompanyId] = useState('');
  const [hubspotSnapshot, setHubspotSnapshot] = useState(null);
  // v3.5.99.7.13: ETF Calculator modal visibility
  const [showEtfModal, setShowEtfModal] = useState(false);
  // v3.5.99.7.6: manual full-overwrite push state. Distinct from the
  // existing hubMsg/hubSyncing state (which is QuotePage-only and used
  // by the older syncToDeal button). hubPushing/hubPushMsg track the
  // new pushAllToHubspot flow.
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushMsg, setHubPushMsg] = useState('');

  // v3.5.99.7.6: manual full-overwrite push to HubSpot. Unlike the
  // automatic gap-fill (which only pushes fields HubSpot was originally
  // missing), this pushes ALL current Ferrum values, overwriting whatever
  // is in HubSpot. Use when rep wants to force HubSpot to match Ferrum's
  // current state — typically after correcting a phone number or address
  // in Ferrum that HubSpot has stale.
  async function pushAllToHubspot() {
    if (!hubContactId && !hubCompanyId) {
      setHubPushMsg('No HubSpot deal linked — connect a deal first.');
      return;
    }
    setHubPushing(true); setHubPushMsg('');
    try {
      const r = await pushHubspotOverwrite({
        contactId: hubContactId, companyId: hubCompanyId,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      });
      if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
      const total = r.contactFields.length + r.companyFields.length;
      if (total > 0) {
        setHubPushMsg(`✓ Pushed ${total} field${total > 1 ? 's' : ''} (${r.contactFields.length} contact, ${r.companyFields.length} company)`);
      } else {
        setHubPushMsg('Nothing to push — all recipient fields are empty.');
      }
      if (r.errors?.length) {
        console.warn('HubSpot push errors:', r.errors);
        setHubPushMsg(prev => prev + ' · ⚠ some errors (see console)');
      }
    } catch (e) {
      setHubPushMsg('✗ ' + (e.message || 'Push failed'));
    }
    setHubPushing(false);
  }
  // v3.5.99.0: per-quote override for the proposal-approved notification
  // CC list. Comma-separated email string; empty falls back to the global
  // pricing_settings.notify_approval_cc_global default. Resolution +
  // validation happen server-side in netlify/functions/proposal.js
  // (resolveNotificationCc + parseEmailList). Not visible to the customer.
  const [notificationCc,  setNotificationCc] = useState('');
  // v3.5.99.4: per-quote addendums — array of {template_id, placeholder_values, enabled_clauses}.
  // Edited via AddendumsModal (button + count badge below the Recipient section).
  // Validated on save: every declared placeholder on every attached template must be non-empty.
  const [addendums,        setAddendums]        = useState([]);
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [addendumsModalOpen, setAddendumsModalOpen] = useState(false);
  // v3.5.99.5: Service Order preview modal — opens the full SO PDF inline so
  // reps can sanity-check the document before sending to SignWell. The modal
  // calls /.netlify/functions/buildSignatureDocument directly (same backend
  // path the actual send uses), so what's previewed is exactly what gets sent.
  const [soPreviewModalOpen, setSoPreviewModalOpen] = useState(false);
  // v3.5.99.4: load active addendum_templates once on mount. Used by the
  // modal picker + the save-time validator + the (future v3.5.99.5) SO PDF
  // renderer which needs template names and bodies to substitute placeholders.
  useEffect(() => { loadAddendumTemplates().then(setAddendumTemplates); }, []);
  const [clientZip,       setClientZip]       = useState('');
  const [zipResult,       setZipResult]       = useState(null);
  const [zipApplied,      setZipApplied]      = useState(false);
  const [marketCity,      setMarketCity]      = useState('');
  const [marketState,     setMarketState]     = useState('');
  // v3.5.43: load full market analysis row so the out-of-scope rate sheet
  // and other consumers can render real city/state/rates instead of
  // synthesizing from selectedMkt (which has no .rates field). When the
  // quote is locked, we prefer the saved rate sheet; otherwise we resolve
  // from market_rate_analyses by zip.
  const [marketAnalysis,  setMarketAnalysis]  = useState(null);
  const [repId,              setRepId]              = useState(null);
  const [pricingSnapshot,    setPricingSnapshot]    = useState(null);
  const [sptProposalId,      setSptProposalId]      = useState(null);
  const [flexHours,          setFlexHours]          = useState(null);  // frozen rates when locked
  const [priceLockDate,      setPriceLockDate]      = useState(null);
  const [showUnlockModal,    setShowUnlockModal]    = useState(false);
  const [repProfile,         setRepProfile]         = useState(null);
  const [teamMembers,        setTeamMembers]        = useState([]);
  const [acceptedMktTier,    setAcceptedMktTier]    = useState(null);
  const [acceptedRates,      setAcceptedRates]      = useState(null);  // remote_support, onsite_*, dev_crm, etc — the locked-in market rates
  const [aiMultiplier,       setAiMultiplier]       = useState(null); // from accepted market analysis
  const [aiMultiplierTier,   setAiMultiplierTier]   = useState(null); // label e.g. "Standard"
  const [showMktRecommend,   setShowMktRecommend]   = useState(false);
  const [pendingMultiplier,  setPendingMultiplier]  = useState(null); // waiting for user to accept
  const [obIncentive,        setObIncentive]        = useState(null);
  // v3.5.99.7.23: manual discount lines [{ type:'pct'|'fixed', value, note }]
  const [manualDiscounts,    setManualDiscounts]    = useState([]);
  const [oneTimeDiscounts,   setOneTimeDiscounts]   = useState([]);
  // v3.8.0 — Unified-UX workspace: which section is focused in the v3 layout.
  // Default is set once config/inputs load (infrastructure for a fresh quote,
  // else products) and can be seeded from ?section=. Legacy layout ignores it.
  const [focusedSection,     setFocusedSection]     = useState(null);
  // v3.8.0 — search-first product picker (v3 Products pane only).
  const [productSearch,      setProductSearch]      = useState('');
  // v3.5.99.7.24: optional back-dated agreement effective date (YYYY-MM-DD)
  const [effectiveDate,      setEffectiveDate]      = useState('');

  // ── Quote config ──────────────────────────────────────────────────────────
  const [inputs,      setInputs]      = useState(DEF_INPUTS);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedMkt, setSelectedMkt] = useState(null);
  const [quoteStatus, setQuoteStatus] = useState('draft');
  const [dealDescription, setDealDescription] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState('');
  // v3.5.94.5: dedicated state for Flex Time Add-On save feedback. The
  // existing saveMsg renders inside QuoteSaveBar at the bottom of the
  // page — but when the rep clicks Apply & Save on the Flex Time block
  // (which lives in the right column near the middle of the page), the
  // save bar is far off-screen, so they had no visible confirmation
  // their save took. Reported May 12 2026 — quote
  // f18f9cfb-bf50-407c-a044-5facbc5619d0. Separate state lets us render
  // a banner directly beneath the FlexTimeSelector, right where the rep
  // is looking when they click. Also carries warning/error states for
  // the two paths that previously silently no-op'd (no existingQuote.id
  // → supabase update skipped; supabase error → no catch).
  const [flexSaveMsg, setFlexSaveMsg] = useState('');
  const [existingQuote, setExistingQuote] = useState(null);

  // v3.5.81: Recommended-but-Declined items.
  // Shape: [{product_id, name, recommendation_reason, decline_reason}]
  // Persisted on inputs.declinedItems and snapshotted onto proposals.declined_items
  // when the rep clicks Send Proposal.
  const [declinedItems, setDeclinedItems] = useState([]);

  // v3.5.81: Active proposal for this quote (most recent non-superseded).
  // Loaded alongside the quote on edit; null if no proposal has been sent yet.
  const [activeProposal, setActiveProposal] = useState(null);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [proposalSending, setProposalSending] = useState(false);
  const [proposalSendMsg, setProposalSendMsg] = useState('');

  // v3.5.81.6: Customer Story & Assumptions modal state.
  // The AssumptionsModal exists since v3.5.x but had no UI entry point on
  // the Managed IT QuotePage before now. We open/close it via this flag,
  // mirror the saved data into local state for the Send Proposal gate
  // (assumptionsStory must be non-empty before a proposal can be sent —
  // the customer-narrative is the legal record we stand on if a post-sign
  // dispute claims we were told something we weren't), and refresh state
  // when the modal saves so the gate reflects the new state immediately.
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [assumptionsStory, setAssumptionsStory] = useState('');
  const [assumptionsSavedAt, setAssumptionsSavedAt] = useState(null);
  const assumptionsComplete = !!(assumptionsStory && assumptionsStory.trim().length > 0);

  // ── Package Includes (v3.5.31) ────────────────────────────────────────────
  // Live-loaded from package_includes table when selectedPkg changes.
  // excludedIncludeIds tracks which swappable includes the rep has unchecked.
  // includeSwaps (v3.5.33) maps include_id -> substitute product_id; entries
  // here always coexist with the same include_id in excludedIncludeIds.
  const [packageIncludes,    setPackageIncludes]    = useState([]);
  const [includesLoading,    setIncludesLoading]    = useState(false);
  const [excludedIncludeIds, setExcludedIncludeIds] = useState([]);
  const [includeSwaps,       setIncludeSwaps]       = useState({});

  // ── Package Cost Components (v3.5.41 / v3.5.57) ───────────────────────────
  // Live-loaded from package_cost_components when selectedPkg changes. The
  // engine reads these unconditionally to compute toolingCost (legacy
  // aggregate stack_cost_per_* path was removed in v3.5.57). An empty array
  // → toolingCost of 0 (correct: no stack = no cost).
  const [costComponents, setCostComponents] = useState([]);

  // ── HubSpot ───────────────────────────────────────────────────────────────
  const [hubModal,    setHubModal]    = useState(false);
  const [hubSearch,   setHubSearch]   = useState('');
  const [hubResults,  setHubResults]  = useState([]);
  const [hubLoading,  setHubLoading]  = useState(false);
  const [hubMsg,      setHubMsg]      = useState('');
  const [hubDealId,   setHubDealId]   = useState('');
  const [hubDealName, setHubDealName] = useState('');
  const [hubDealUrl,  setHubDealUrl]  = useState('');
  const [hubSyncing,  setHubSyncing]  = useState(false);

  // ── v3.5.83.2: Lock-Rates-First UI guard ────────────────────────────────
  // The v3.5.82.4.1 backend block enforces that pricing_snapshot.acceptedRates
  // be populated before a Service Order can be generated. v3.5.82.4.1.1 + .1.2
  // surfaced those errors after the rep clicked. v3.5.83.2 adds preventive
  // UI guards so reps see the issue BEFORE clicking:
  //   1. Banner at the top of the page when rates aren't locked
  //   2. Yellow border + ⚠️ on Send Proposal / Send Service Order buttons
  //   3. Confirmation modal when those buttons are clicked while unlocked
  //      ("Lock Rates Now" scrolls to the OOS card, "Send Anyway" allows
  //       the override for cases where the rep has a business reason)
  // showLockRatesModal tracks which action the modal is gating: null when
  // closed, 'send_proposal' or 'send_service_order' when open.
  const [showLockRatesModal, setShowLockRatesModal] = useState(null);

  // ── v3.5.82.4.1.1: Manual Service Order send ─────────────────────────────
  // The yellow "Send Service Order →" indicator on approved quotes was a
  // <span> with no onClick — visually a button but functionally inert. When
  // proposal_autofire_enabled is 'false' (default) or auto-fire fails for
  // any reason (rates not locked, build error, SignWell down, etc.), the
  // rep had no way to manually trigger Service Order generation.
  //
  // Now wired to /.netlify/functions/sendServiceOrder which mirrors the
  // server-side maybeAutoFireServiceOrder helper. State below tracks the
  // in-flight + error + success states so the UI can render appropriate
  // feedback. Errors are surfaced to the rep with the upstream `error` +
  // `detail` from the function response (e.g. rates_not_locked → tells
  // rep to click Accept All Rates on the OOS card).
  const [soSending,  setSoSending]  = useState(false);
  const [soError,    setSoError]    = useState(null);   // { message, detail, code } or null
  const [soSuccess,  setSoSuccess]  = useState(false);  // transient — clears on next page action

  // ── Load team members for rep selector ──────────────────────────────────────
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, commission_rate').order('full_name')
      .then(({ data }) => setTeamMembers(data || []));
  }, []);

  useEffect(() => {
    if (!repId && profile?.id && !id) setRepId(profile.id);
  }, [profile, id]);

  useEffect(() => {
    if (!repId || !teamMembers.length) return;
    const rep = teamMembers.find(m => m.id === repId);
    if (rep) setRepProfile(rep);
  }, [repId, teamMembers]);

  // Load existing quote
  useEffect(() => {
    if (!id || id === 'new' || configLoading) return;
    supabase.from('quotes').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return;
      setExistingQuote(data);
      setProposalName(data.inputs?.proposalName || '');
      setRecipientBiz(data.client_name || '');
      setRecipientContact(data.inputs?.recipientContact || '');
      setRecipientEmail(data.inputs?.recipientEmail || '');
      setRecipientAddress(data.inputs?.recipientAddress || '');
      setRecipientPhone(data.inputs?.recipientPhone || '');
      setRecipientMobilePhone(data.inputs?.recipientMobilePhone || '');
      setHubContactId(data.inputs?.hubspotContactId || '');
      setHubCompanyId(data.inputs?.hubspotCompanyId || '');
      setHubspotSnapshot(data.inputs?.hubspotSnapshot || null);
      setNotificationCc(data.inputs?.notificationCc || '');
      setAddendums(Array.isArray(data.inputs?.addendums) ? data.inputs.addendums : []);
      setManualDiscounts(Array.isArray(data.inputs?.manualDiscounts) ? data.inputs.manualDiscounts : []);
      setOneTimeDiscounts(Array.isArray(data.inputs?.oneTimeDiscounts) ? data.inputs.oneTimeDiscounts : []);
      setEffectiveDate(data.inputs?.effectiveDate || '');
      setClientZip(data.client_zip || '');
      if (data.inputs?.marketCity)       setMarketCity(data.inputs.marketCity);
      if (data.inputs?.marketState)      setMarketState(data.inputs.marketState);
      if (data.inputs?.aiMultiplier != null) { setAiMultiplier(data.inputs.aiMultiplier); setAiMultiplierTier(data.inputs.aiMultiplierTier || null); }
      if (data.onboarding_incentive?.mode) setObIncentive(data.onboarding_incentive);
      if (data.rep_id) setRepId(data.rep_id);
      if (data.pricing_snapshot) {
        setPricingSnapshot(data.pricing_snapshot);
        setPriceLockDate(data.price_locked_at);
        // Restore accepted rates from snapshot so the flex block picks them up
        // immediately on reopen, before MarketRateCard re-fires onRatesAccepted.
        if (data.pricing_snapshot.acceptedRates) setAcceptedRates(data.pricing_snapshot.acceptedRates);
      }
      if (data.spt_proposal_id) setSptProposalId(data.spt_proposal_id);
      if (data.inputs?.flexHours) setFlexHours(data.inputs.flexHours);
      setQuoteStatus(data.status || 'draft');
      setDealDescription(data.notes || '');
      setHubDealId(data.hubspot_deal_id || '');
      setHubDealUrl(data.hubspot_deal_url || '');
      setHubDealName(data.inputs?.hubspotDealName || '');
      if (data.inputs) {
        // v3.5.58: auto-strip product IDs that no longer resolve to an active
        // product. This cleans up legacy flat-rate Strategic Advisory line
        // items (vCIO Essentials/Business Plus/Enterprise) on first save under
        // the new ITSO model — those products were retired in the v3.5.58 SQL
        // migration and the ITSO line is now derived from Industry Risk. Same
        // mechanism cleans up any future product retirements automatically.
        const stripped = { ...DEF_INPUTS, ...data.inputs };
        if (Array.isArray(stripped.selectedProducts) && products.length > 0) {
          const validIds = new Set(products.filter(p => p.active).map(p => p.id));
          const before = stripped.selectedProducts.length;
          stripped.selectedProducts = stripped.selectedProducts.filter(pid => validIds.has(pid));
          if (stripped.selectedProducts.length < before) {
            console.info(`[v3.5.58] Auto-stripped ${before - stripped.selectedProducts.length} retired product ID(s) from quote ${data.quote_number || data.id}`);
          }
        }
        setInputs(stripped);
      }
      // v3.5.31: restore rep's swappable-include exclusions if any
      if (Array.isArray(data.inputs?.excludedIncludeIds)) {
        setExcludedIncludeIds(data.inputs.excludedIncludeIds);
      }
      // v3.5.33: restore swap mappings if any
      if (data.inputs?.includeSwaps && typeof data.inputs.includeSwaps === 'object') {
        setIncludeSwaps(data.inputs.includeSwaps);
      }
      // v3.5.81: restore declined items from inputs (rep flags from prior session)
      if (Array.isArray(data.inputs?.declinedItems)) {
        setDeclinedItems(data.inputs.declinedItems);
      }
      // v3.5.81.6: restore assumptions story for the Send Proposal gate.
      // Only the story field is needed for gating; the modal loads its own
      // full state independently when opened.
      if (data.inputs?.assumptions) {
        setAssumptionsStory(data.inputs.assumptions.story || '');
        setAssumptionsSavedAt(data.inputs.assumptions.savedAt || null);
      }
      if (data.package_name && packages.length) setSelectedPkg(packages.find(p => p.name === data.package_name));
      if (data.market_tier  && marketTiers.length) setSelectedMkt(marketTiers.find(t => t.tier_key === data.market_tier));

      // v3.5.81: load most recent active proposal for this quote (if any).
      // 'active' = anything other than superseded; we surface the latest one
      // (sent_at desc) so the rep sees current state.
      supabase.from('proposals')
        .select('*')
        .eq('quote_id', data.id)
        .neq('status', 'superseded')
        .order('sent_at', { ascending: false })
        .limit(1)
        .maybeSingle()
        .then(({ data: prop }) => {
          if (prop) setActiveProposal(prop);
        });
    });
  }, [id, configLoading, packages, marketTiers, products]);

  // Defaults
  useEffect(() => {
    if (!selectedPkg && packages.length) setSelectedPkg(packages[1] || packages[0]);
    if (!selectedMkt && marketTiers.length) setSelectedMkt(marketTiers.find(t => t.tier_key === 'mid_market') || marketTiers[0]);
  }, [packages, marketTiers]);

  // Load package includes whenever the selected package changes (v3.5.31).
  // For an existing quote, this runs once after selectedPkg is restored from the
  // saved quote_data. The rep's saved excludedIncludeIds (loaded separately from
  // inputs) may target includes that no longer exist if admin changed the config —
  // those are pruned below so they don't accumulate forever in saved quotes.
  useEffect(() => {
    if (!selectedPkg?.id) { setPackageIncludes([]); return; }
    setIncludesLoading(true);
    loadPackageIncludes(selectedPkg.id).then(rows => {
      setPackageIncludes(rows);
      setIncludesLoading(false);
      // Prune stale exclusions/swaps that don't match any current include
      const validIds = new Set(rows.map(r => r.id));
      setExcludedIncludeIds(prev => prev.filter(id => validIds.has(id)));
      setIncludeSwaps(prev => {
        const next = {};
        for (const [includeId, subId] of Object.entries(prev || {})) {
          if (validIds.has(includeId)) next[includeId] = subId;
        }
        return next;
      });
    });
  }, [selectedPkg?.id]);

  // v3.5.41: Load cost components alongside includes when selectedPkg changes.
  // v3.5.57: engine reads these unconditionally; no toggle to gate them.
  useEffect(() => {
    if (!selectedPkg?.id) { setCostComponents([]); return; }
    loadPackageCostComponents(selectedPkg.id).then(rows => {
      setCostComponents(rows);
    });
  }, [selectedPkg?.id]);

  // v3.5.43: load real market analysis row so rate sheet renders correctly.
  // Strategy:
  //   1. If quote is saved AND has a locked rate sheet, use the rate sheet's
  //      city/state/zip + accepted_rates as the analysis source. Locked rates
  //      ARE the analysis for that quote.
  //   2. Otherwise, look up by clientZip in market_rate_analyses (read-only —
  //      don't trigger AI analysis from the page; that's MarketRateCard's job).
  //   3. Otherwise leave null. Consumers (DocumentsPanel) check for null.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Path 1: locked rate sheet — synthesize an analysis-shaped object from it
      if (existingQuote?.id) {
        try {
          const sheet = await getRateSheet(existingQuote.id);
          if (cancelled) return;
          if (sheet) {
            setMarketAnalysis({
              city:        sheet.market_city  || '',
              state:       sheet.market_state || '',
              zip:         sheet.market_zip   || null,
              market_tier: 'locked',
              pricing_multiplier: 1,
              rates:       sheet.accepted_rates || {},
              analysis_source: 'locked',
              analyzed_at: sheet.accepted_at || null,
            });
            return;
          }
        } catch { /* fall through to zip lookup */ }
      }
      // Path 2: live zip lookup, read-only (no AI)
      if (clientZip && clientZip.length >= 5) {
        try {
          const res = await getOrAnalyzeMarket(clientZip, false, marketCity || undefined, marketState || undefined, true);
          if (cancelled) return;
          if (res?.analysis) {
            setMarketAnalysis(res.analysis);
            return;
          }
        } catch { /* fall through to null */ }
      }
      if (!cancelled) setMarketAnalysis(null);
    }
    load();
    return () => { cancelled = true; };
  }, [existingQuote?.id, clientZip, marketCity, marketState]);

  const set = useCallback((k, v) => setInputs(prev => ({ ...prev, [k]: v })), []);

  // Zip lookup & auto-apply
  function handleZipChange(val) {
    setClientZip(val); setZipApplied(false);
    const r = val.length >= 3 ? lookupZip(val) : null;
    setZipResult(r);
  }

  function applyZip(result) {
    const r = result || zipResult;
    if (!r) return;
    const tier = marketTiers.find(t => t.tier_key === r.tier);
    if (tier) { setSelectedMkt(tier); setZipApplied(true); }
    // Capture city/state for market rate analysis
    if (r.city) setMarketCity(r.city);
    if (r.state) setMarketState(r.state);
  }

  // Product toggle with exclusive group handling
  function toggleProduct(productId) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    setInputs(prev => {
      let selected = [...(prev.selectedProducts || [])];
      if (selected.includes(productId)) {
        selected = selected.filter(id => id !== productId);
      } else {
        if (product.exclusive_group) {
          const groupIds = exclusiveGroups[product.exclusive_group] || [];
          selected = selected.filter(id => !groupIds.includes(id));
        }
        selected.push(productId);
      }
      return { ...prev, selectedProducts: selected };
    });
  }
  function isSelected(pid) { return (inputs.selectedProducts || []).includes(pid); }

  // ── HubSpot: search open deals ────────────────────────────────────────────
  async function searchHubspot() {
    setHubLoading(true); setHubMsg(''); setHubResults([]);
    try {
      const results = await searchDeals(hubSearch);
      setHubResults(results);
      if (results.length === 0) setHubMsg('No open deals found. Try a different search term.');
    } catch (err) { setHubMsg('✗ ' + err.message); }
    setHubLoading(false);
  }

  // ── HubSpot: select deal → pull all data ──────────────────────────────────
  async function connectDeal(deal) {
    setHubLoading(true); setHubMsg('Pulling deal details...');
    try {
      const full = await getDealFull(deal.id);

      // Link the deal
      setHubDealId(full.dealId);
      setHubDealUrl(full.dealUrl);
      setHubDealName(full.deal.dealname);

      // Populate client fields from company
      if (full.company) {
        if (full.company.name)    setRecipientBiz(full.company.name);
        // Build address string
        const addrParts = [
          full.company.address,
          full.company.address2,
          full.company.city,
          full.company.state,
          full.company.zip,
          full.company.country !== 'United States' ? full.company.country : null
        ].filter(Boolean);
        const addrStr = addrParts.join(', ');
        if (addrStr) setRecipientAddress(addrStr);

        // Auto-apply zip code
        const zip = full.company.zip;
        if (zip) {
          setClientZip(zip);
          const zr = lookupZip(zip);
          setZipResult(zr);
          if (zr) applyZip(zr);
        }
      } else {
        // No company associated with this deal in HubSpot
        // Extract business name from deal name (e.g. "Acme Corp - Email Security" → "Acme Corp")
        const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
        if (extracted) setRecipientBiz(extracted);
        setHubMsg('⚠ No company linked to this deal in HubSpot — business name extracted from deal name. Add address manually or link a company in HubSpot.');
      }

      // Populate contact
      if (full.contact) {
        const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
        if (name)              setRecipientContact(name);
        if (full.contact.email) setRecipientEmail(full.contact.email);
        if (full.contact.phone) setRecipientPhone(full.contact.phone);
        if (full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone);
      }
      // v3.5.99.7.4: capture write-back routing IDs + original-state snapshot
      if (full.contactId) setHubContactId(full.contactId);
      if (full.companyId) setHubCompanyId(full.companyId);
      setHubspotSnapshot(captureHubspotSnapshot(full));

      // Auto-set proposal name if empty
      if (!proposalName && full.deal.dealname) {
        setProposalName(`FerrumIT Managed IT Services — ${full.company?.name || full.deal.dealname}`);
      }

      setHubMsg(`✓ Connected: ${full.deal.dealname}`);
      setHubResults([]);

      await logActivity({ action: 'HUBSPOT_CONNECT', entityType: 'quote', entityId: existingQuote?.id, entityName: recipientBiz, changes: { deal_id: full.dealId, deal_name: full.deal.dealname } });
    } catch (err) { setHubMsg('✗ ' + err.message); }
    setHubLoading(false);
  }

  // ── HubSpot: sync quote data back to deal ─────────────────────────────────
  async function syncToDeal() {
    if (!result || !hubDealId) return;
    setHubSyncing(true);
    try {
      await updateDeal(hubDealId, {
        mrr: discountedFinalMRR,
        contractValue: discountedFinalMRR * inputs.contractTerm + result.onboarding,
        packageName: selectedPkg?.name,
        quoteNumber: existingQuote?.quote_number || 'DRAFT',
        contractTerm: inputs.contractTerm
      });
      setHubMsg('✓ Deal updated in HubSpot');
      await logActivity({ action: 'HUBSPOT_SYNC', entityType: 'quote', entityId: existingQuote?.id, entityName: recipientBiz });
    } catch (err) { setHubMsg('✗ ' + err.message); }
    setHubSyncing(false);
  }

  // ── Save quote ────────────────────────────────────────────────────────────
  async function saveQuote() {
    // v3.5.99.4: block save if any addendum has unfilled required placeholders.
    // Defense-in-depth — the modal also validates on its own Save button, but
    // a stale tab or a future code path could mutate addendums outside the modal,
    // so the quote-save handler runs the same check.
    const addendumErrors = validateAddendums(addendums, addendumTemplates);
    if (addendumErrors.length > 0) {
      const summary = addendumErrors.slice(0, 3).map(e => `\u2022 ${e.template_name}: ${e.placeholder_label || e.reason}`).join('\n');
      const more = addendumErrors.length > 3 ? `\n\u2026and ${addendumErrors.length - 3} more.` : '';
      alert(`Can't save \u2014 ${addendumErrors.length} addendum issue${addendumErrors.length === 1 ? '' : 's'}:\n\n${summary}${more}\n\nOpen Special Terms to fix or remove the addendum.`);
      return;
    }
    // v3.5.67: read-only mode (user lost permission for this quote type but
    // is viewing an existing quote). Refuse silently with a clear message.
    // RLS will reject the save server-side too — this is just the friendly UX.
    if (isReadOnly) {
      setSaveMsg('You don\'t have permission to save Managed IT quotes. This quote is view-only.');
      return;
    }
    // v3.9.9.2: post-signature lock — a signed agreement freezes terms; persist ONLY notes.
    if (isQuoteLocked(existingQuote)) {
      setSaving(true); setSaveMsg('');
      try {
        if (existingQuote?.id) {
          await supabase.from('quotes').update({ notes: dealDescription }).eq('id', existingQuote.id);
          if (hubDealId && dealDescription) { try { await updateDealDescription(hubDealId, dealDescription); } catch { /* non-blocking */ } }
        }
        setSaveMsg('🔒 Locked — only internal notes were saved.');
      } catch (e) { setSaveMsg('✗ ' + (e?.message || 'Save failed')); }
      setSaving(false);
      return;
    }
    if (!recipientBiz.trim()) { setSaveMsg('Please enter a recipient business name.'); return; }
    setSaving(true); setSaveMsg('');
    const allInputs = { ...inputs, proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, hubspotDealName: hubDealName, marketCity, marketState, aiMultiplier: aiMultiplier ?? null, aiMultiplierTier: aiMultiplierTier ?? null, repId: repId || null, repName: repProfile?.full_name || repProfile?.email || null, flexHours: flexHours || null,
      // v3.5.31: persist rep's swappable-include exclusions
      excludedIncludeIds: excludedIncludeIds || [],
      // v3.5.33: persist swap mappings
      includeSwaps: includeSwaps || {},
      // v3.5.81: persist rep-flagged declined items so they survive across
      // sessions and feed the proposal page when one is sent.
      declinedItems: declinedItems || [],
      // v3.5.99.7.23: persist manual discount lines
      manualDiscounts: manualDiscounts || [],
      // v3.7.8: persist one-time (non-recurring) discount lines
      oneTimeDiscounts: oneTimeDiscounts || [],
      // v3.5.99.7.24: persist back-dated agreement effective date
      effectiveDate: effectiveDate || null,
    };
    // v3.5.81.6: persist itSubtotal + ITSO fields so the public proposal
    // renderer (netlify/functions/proposal.js) can display the exact base
    // service rate. Pre-v3.5.81.6 quotes hit the proposal renderer's
    // fallback path (finalMRR - itsoMRR - sum(line_items.revenue)), which
    // is approximate; new quotes will get exact values straight from
    // engine output.
    //
    // v3.5.81.9: also persist compMult and discRate so the proposal
    // renderer's fallback can compute the exact post-multiplier
    // post-discount base for legacy quotes (saved before v3.5.81.6,
    // when totals.itSubtotal wasn't being captured). The fallback was
    // approximating with finalMRR - itsoMRR - sum(line_items.revenue),
    // which under-counts when there's a contract discount baked into
    // finalMRR or a compMult > 1 (e.g. the FIT-2026-1007 case where
    // the proposal was showing $988 instead of the correct $1011.08
    // — a $23.08 gap from the contract discount math).
    //
    // v3.5.81.9: also persist effectiveOnboarding (post-incentive) +
    // incentive metadata. Pre-v3.5.81.9 the proposal page and Stripe
    // Prepayment both used totals.onboarding (the raw fee) and never
    // applied the discount, so a quote with 50% off onboarding still
    // showed and charged the full amount. Persisting effectiveOnboarding
    // alongside the raw onboarding lets downstream consumers display
    // both ($1,085 standard fee minus $542.50 incentive equals $542.50
    // net) without re-running the incentive math.
    // v3.5.92.0: totals construction migrated to the unified utility at
    // src/lib/quoteTotals.js. The same object is now passed to BOTH the
    // upsert payload AND saveQuoteVersion below — eliminates the save-vs-
    // snapshot mismatches that affected Bundle and FlexIT pre-v3.5.92.x
    // (CLAUDE.md §3 history).
    //
    // What this changes for Managed IT saves:
    //   • New persisted fields: effectiveMRR (= finalMRR + flexBlockMRR),
    //     nrc (= onboarding on IT; non-zero on Voice/Bundle from .92.2 on),
    //     hardwareNRC / activationNRC / portingNRC (all 0 for IT —
    //     populated by Voice/Bundle in .92.2), flexBlockHours / flexBlock*
    //     detail (when a block is selected).
    //   • Behavior change: persisted impliedGM and contractValue now
    //     include flex block in the math, matching what reps see in the
    //     on-page KPI strip (line ~2125 pre-v3.5.92.0 already computed
    //     this way for display; only the saved value was lagging). Quotes
    //     without a flex block: identical numbers. Quotes with a flex
    //     block: GM compresses by ~1-3 points on re-save, TCV ticks up by
    //     flexBlockMRR × contractTerm.
    //   • Field set is a strict superset — every consumer reading
    //     totals.finalMRR / totals.impliedGM / totals.onboarding / etc.
    //     still resolves correctly. The §10b cleanup deploy (v3.5.92.3)
    //     migrates consumers to read the new fields where helpful (e.g.
    //     QuotesPage NRC column will gain the proper total-NRC vs labor-
    //     subset distinction the §147 column comment has been promising
    //     since v3.5.62).
    //
    // The incentive resolution that used to be inlined here (obEffective /
    // obDiscountAmt computation) now lives inside resolveOnboardingIncentive
    // in quoteTotals.js — same math, single source for all four types that
    // use OnboardingIncentive once Voice/Bundle/Multi-Site migrate.
    const totals = buildTotals('managed_it', result, {
      obIncentive,
      flexBlock,
      contractTerm: inputs.contractTerm,
      settings,
      manualDiscounts,
      oneTimeDiscounts,
    });
    const payload = {
      client_name: recipientBiz, client_zip: clientZip,
      market_tier: selectedMkt?.tier_key, package_name: selectedPkg?.name,
      // v3.5.91.0: dual-write quote_type to the new column. Readers still
      // use prefix-matching on package_name in this deploy; they migrate
      // in v3.5.91.1. Canonical key matches src/lib/quotePermissions.js
      // QUOTE_TYPES — must be 'managed_it' (not 'it') because the same
      // string is already used by profiles.allowed_quote_types and RLS.
      quote_type: 'managed_it',
      status: quoteStatus, notes: dealDescription,
      // Auto-lock pricing when moving to approved
      ...(quoteStatus === 'approved' && !pricingSnapshot && calcPkg ? {
        pricing_snapshot: buildSnapshot(),
        price_locked_at:  new Date().toISOString(),
        price_locked_by:  profile?.id,
      } : {}),
      ...(pricingSnapshot ? {
        pricing_snapshot: pricingSnapshot,
        price_locked_at:  priceLockDate,
      } : {}), inputs: allInputs,
      line_items: result?.lineItems || [], totals,
      hubspot_deal_id: hubDealId || null,
      hubspot_deal_url: hubDealUrl || null,
      // v3.5.44: persist onboarding incentive — column added in same release.
      // Mirrors the JSON shape used in exportSPT (see line 517 in this file).
      onboarding_incentive: obIncentive?.mode ? {
        mode:           obIncentive.mode,
        effectiveFee:   obIncentive.effectiveFee,
        monthlyAdd:     obIncentive.monthlyAdd   || 0,
        splitMonths:    obIncentive.splitMonths  || 0,
        discountPct:    obIncentive.discountPct  || 0,
        discountAmount: obIncentive.discountAmount || 0,
        fullFee:        result?.onboarding ?? null,
      } : null,
      rep_id:    repId || profile?.id || null,
      updated_by: profile?.id,
    };
    if (!existingQuote) payload.created_by = profile?.id;

    const { data, error } = existingQuote
      ? await supabase.from('quotes').update(payload).eq('id', existingQuote.id).select().single()
      : await supabase.from('quotes').insert(payload).select().single();

    if (error) { setSaveMsg('Error: ' + error.message); setSaving(false); return; }

    // Push deal description to HubSpot if linked
    if (hubDealId && dealDescription) {
      try {
        await updateDealDescription(hubDealId, dealDescription);
      } catch (err) {
        console.warn('HubSpot description sync failed:', err.message);
      }
    }

    await logActivity({ action: existingQuote ? 'UPDATE' : 'CREATE', entityType: 'quote', entityId: data.id, entityName: recipientBiz,
      changes: { status: quoteStatus, mrr: totals.finalMRR, package: selectedPkg?.name } });
    // Write quote URL back to HubSpot deal if a field is configured
    if (hubDealId && data?.id) {
      try {
        const { data: fieldData } = await supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single();
        const fieldKey = fieldData?.value;
        if (fieldKey) {
          const quoteUrl = `${window.location.origin}/quotes/${data.id}`;
          await writeQuoteUrlToDeal(hubDealId, quoteUrl, fieldKey);
        }
      } catch (e) { console.warn('HubSpot quote URL write failed:', e.message); }
    }

    // Save version snapshot
    await saveQuoteVersion({
      quoteId: data.id,
      quoteData: { client_name: recipientBiz, client_zip: clientZip, market_tier: selectedMkt?.tier_key, package_name: selectedPkg?.name, status: quoteStatus },
      inputs: { ...inputs, proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums },
      totals,
      lineItems: result?.lineItems || [],
      profile,
    });

    setSaveMsg(`Saved as ${data.quote_number}${hubDealId && dealDescription ? ' · HubSpot updated' : ''}`);
    // v3.5.99.7.4: gap-fill push to HubSpot. Fire-and-forget after save
    // success — pushes only fields where HubSpot was empty when connectDeal
    // pulled (per hubspotSnapshot). Errors logged but don't block UI.
    // The manual full-overwrite push button comes in v3.5.99.7.5.
    if ((hubContactId || hubCompanyId) && hubspotSnapshot) {
      pushHubspotGapFill({
        contactId: hubContactId, companyId: hubCompanyId, snapshot: hubspotSnapshot,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      }).then(r => {
        if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
        if (r.errors?.length) console.warn('HubSpot gap-fill errors:', r.errors);
      }).catch(e => console.warn('HubSpot gap-fill failed:', e));
    }
    setSaving(false);
    if (!existingQuote) navigate(`/quotes/${data.id}`, { replace: true });
  }

  // v3.5.81: Send Proposal — creates a proposals row with a fresh public_token
  // and surfaces the URL in a modal. The rep distributes the URL to the
  // prospect (via their preferred channel — email, Slack, etc).
  //
  // Behavior:
  //  - The quote MUST be saved first (we need an existingQuote.id to FK to).
  //    If unsaved, we prompt the rep to save first.
  //  - If a non-superseded proposal already exists for this quote, the new
  //    one supersedes it (old one's status flips to 'superseded'). Audit
  //    trail is preserved — every proposal sent has its own row.
  //  - declinedItems flow from the rep's editor state into proposals.declined_items.
  //    Once approved, they snapshot into proposals.frozen_snapshot too.
  async function sendProposal() {
    if (!result) {
      setProposalSendMsg('Cannot send — quote pricing has not loaded.');
      return;
    }
    if (!recipientBiz.trim()) {
      setProposalSendMsg('Please enter a recipient business name before sending.');
      return;
    }
    if (!existingQuote?.id) {
      setProposalSendMsg('Please save the quote first, then click Send Proposal.');
      return;
    }
    // v3.5.81.6: require Customer Story before sending. The customer
    // narrative captured in AssumptionsModal is the legal record we point
    // at for breach defense — without it we have nothing to demonstrate
    // 'we understood your ask and priced based on what you told us.'
    // Open the modal directly so the rep can fill it out without hunting.
    if (!assumptionsComplete) {
      setProposalSendMsg('Customer Story is required before sending a proposal. Opening the form now…');
      setShowAssumptions(true);
      return;
    }
    setProposalSending(true);
    setProposalSendMsg('');

    try {
      // Supersede any existing active proposal for this quote.
      if (activeProposal?.id && activeProposal.status !== 'superseded') {
        await supabase.from('proposals')
          .update({ status: 'superseded' })
          .eq('id', activeProposal.id);
      }

      // Validity — read default from pricing_settings.
      let validityDays = 30;
      const { data: vRow } = await supabase
        .from('pricing_settings').select('value')
        .eq('key', 'proposal_validity_days_default').maybeSingle();
      if (vRow?.value && !isNaN(parseInt(vRow.value))) {
        validityDays = parseInt(vRow.value);
      }
      const expiresAt = new Date(Date.now() + validityDays * 86400 * 1000).toISOString();

      // Generate token — 32 bytes of CSPRNG, base64url encoded.
      const tokenBytes = new Uint8Array(32);
      window.crypto.getRandomValues(tokenBytes);
      const publicToken = btoa(String.fromCharCode(...tokenBytes))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

      // Insert proposal.
      const { data: created, error: insertErr } = await supabase
        .from('proposals')
        .insert({
          quote_id: existingQuote.id,
          public_token: publicToken,
          status: 'sent_for_review',
          declined_items: declinedItems || [],
          validity_days: validityDays,
          expires_at: expiresAt,
          prepared_by_id: profile?.id || null,
        })
        .select()
        .single();

      if (insertErr) {
        setProposalSendMsg('Could not create proposal: ' + insertErr.message);
        setProposalSending(false);
        return;
      }

      setActiveProposal(created);
      setProposalModalOpen(true);

      await logActivity({
        action: 'PROPOSAL_SENT',
        entityType: 'proposal',
        entityId: created.id,
        entityName: recipientBiz,
        metadata: { quote_id: existingQuote.id, quote_number: existingQuote.quote_number, validity_days: validityDays },
      });
    } catch (e) {
      setProposalSendMsg('Error: ' + (e?.message || String(e)));
    } finally {
      setProposalSending(false);
    }
  }

  function proposalPublicUrl(token) {
    return `${window.location.origin}/.netlify/functions/proposal?token=${token}`;
  }

  // ── v3.5.82.4.1.1: Manual Service Order send handler ────────────────────
  // Wired to the new netlify/functions/sendServiceOrder endpoint. The flow:
  //   1. Get the rep's JWT from supabase.auth.getSession() so the function
  //      can authenticate the caller and record sent_by_user_id in the
  //      audit log
  //   2. POST to /sendServiceOrder with quote_id + doc_type + proposal_id
  //      (lets the function fall back to activeProposal.approver_email
  //      when needed) + test_mode (we honor whatever the server-side
  //      pricing_settings say by default; not overriding here)
  //   3. On success, the function returns { fired: true, signwell_doc_id }.
  //      We don't push a signwellDocuments record locally because the
  //      function already persisted it to the quote — instead we re-fetch
  //      the quote so the UI transitions from "Send Service Order" yellow
  //      indicator to "Service Order Sent ✓" green indicator
  //   4. On error, we store the error in soError state which the UI
  //      surfaces below the indicator. Specific codes get specific guidance:
  //      'rates_not_locked' → tells rep to click Accept All Rates
  async function handleSendServiceOrder() {
    if (!existingQuote?.id) {
      setSoError({ message: 'Save the quote first.', code: 'no_quote' });
      return;
    }
    setSoSending(true);
    setSoError(null);
    setSoSuccess(false);

    try {
      // Get JWT from current session for the Authorization header
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) {
        setSoError({
          message: 'Authentication required',
          detail: 'Your session has expired. Please refresh the page and try again.',
          code: 'no_session',
        });
        setSoSending(false);
        return;
      }

      // POST to the new endpoint
      const res = await fetch('/.netlify/functions/sendServiceOrder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          quote_id:       existingQuote.id,
          doc_type:       'managed_it_quote',
          proposal_id:    activeProposal?.id || null,
          approver_name:  activeProposal?.approver_name || recipientContact || null,
          approver_email: activeProposal?.approver_email || recipientEmail || null,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setSoError({
          message: data?.error || `Service Order send failed (HTTP ${res.status})`,
          detail:  data?.detail || null,
          code:    data?.code   || 'send_failed',
        });
        return;
      }

      // Success — re-fetch the quote so the UI sees the new
      // signwellDocuments[] entry and transitions the indicator state.
      // This is also when we'd log a "service order sent" toast in a
      // future toast system. For now the indicator transition IS the
      // feedback.
      setSoSuccess(true);
      const { data: refreshed } = await supabase
        .from('quotes').select('*').eq('id', existingQuote.id).single();
      if (refreshed) setExistingQuote(refreshed);
    } catch (e) {
      setSoError({
        message: 'Service Order send failed (network error)',
        detail:  e?.message || String(e),
        code:    'network_error',
      });
    } finally {
      setSoSending(false);
    }
  }

  // ── Export SPT JSON ───────────────────────────────────────────────────────
  function exportSPT() {
    if (!result) return;
    const json = {
      quote_number:    existingQuote?.quote_number || 'DRAFT',
      proposal_name:   proposalName,
      recipient: {
        business_name: recipientBiz,
        contact_name:  recipientContact,
        email:         recipientEmail,
        address:       recipientAddress,
        zip:           clientZip,
      },
      date:            new Date().toISOString().split('T')[0],
      package:         selectedPkg?.name,
      market:          selectedMkt?.name,
      contract_term_months: inputs.contractTerm,
      line_items: [
        { description: `Managed IT — ${selectedPkg?.name}`, quantity: 1, unit_price: result.itSubtotal, total: result.itSubtotal, recurring: true },
        ...result.lineItems.map(li => ({ description: li.product_name, quantity: li.qty, unit_price: li.sell_price, total: li.revenue, recurring: true })),
      ],
      monthly_mrr:      discountedFinalMRR,
      onboarding_fee:        result.onboarding,
      onboarding_incentive:  obIncentive?.mode ? {
        mode:         obIncentive.mode,
        effectiveFee: obIncentive.effectiveFee,
        monthlyAdd:   obIncentive.monthlyAdd   || 0,
        splitMonths:  obIncentive.splitMonths  || 0,
        discountPct:  obIncentive.discountPct  || 0,
        discountAmount: obIncentive.discountAmount || 0,
        fullFee:      result.onboarding,
        export:       formatIncentiveForExport(obIncentive, result.onboarding),
      } : null,
      contract_value:   discountedFinalMRR * inputs.contractTerm + (obIncentive?.effectiveFee ?? result.onboarding),
      discount_rate:    result.discRate,
      hubspot_deal_id:  hubDealId,
      dealDescription,
    };
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `${existingQuote?.quote_number || 'quote'}_spt.json`; a.click();
    URL.revokeObjectURL(url);
  }

  // Build snapshot of current pricing rates (for locking)
  const buildSnapshot = () => {
    // v3.5.33: include both rep-selected add-ons AND any swap substitute products
    // so the locked snapshot has all products the engine needs to recompute lineItems.
    const selectedIds = new Set(inputs.selectedProducts || []);
    const swapSubIds  = new Set(Object.values(includeSwaps || {}).filter(Boolean));
    const allProductIds = new Set([...selectedIds, ...swapSubIds]);
    return {
      lockedAt:  new Date().toISOString(),
      package:   selectedPkg  ? { ...selectedPkg }  : null,
      products:  products.filter(p => allProductIds.has(p.id)).map(p => ({ ...p })),
      settings:  { ...settings },
      // The market rates the rep negotiated/accepted at lock time. Without this
      // the flex block falls back to the package's default $165 after reopen,
      // and the rate card has no rates to display when the underlying market
      // analysis row gets evicted.
      acceptedRates: acceptedRates || null,
      // v3.5.31: capture the package's bundled product config at lock time so
      // the quote keeps pricing correctly even if admin changes package_includes later
      packageIncludes: packageIncludes ? [...packageIncludes] : [],
      // v3.5.33: capture swap mappings at lock time. Substitutes are in snapshot.products above.
      includeSwaps: includeSwaps ? { ...includeSwaps } : {},
    };
  };

  // Use snapshot rates if locked, live rates otherwise
  const calcPkg      = pricingSnapshot?.package   || selectedPkg;
  const calcProducts = pricingSnapshot?.products  ? [...products.map(p => {
    const snap = pricingSnapshot.products.find(s => s.id === p.id);
    return snap ? { ...p, sell_price: snap.sell_price, cost_price: snap.cost_price, no_discount: snap.no_discount, no_commission: snap.no_commission } : p;
  })] : products;
  const calcSettings = pricingSnapshot?.settings  || settings;

  // v3.5.83.2: derived flag for the Lock-Rates-First UI guard. Reads the
  // local acceptedRates state which mirrors pricing_snapshot.acceptedRates
  // and is updated live when MarketRateCard fires onRatesAccepted. The
  // "rates are locked" condition matches what the v3.5.82.4.1 backend
  // precondition checks: non-null object with at least one key.
  const ratesLocked = !!(
    acceptedRates &&
    typeof acceptedRates === 'object' &&
    Object.keys(acceptedRates).length > 0
  );

  // Helper to scroll the OOS Rate Schedule card into view (anchored on the
  // <div id="oos-rate-card"> wrapper added in this deploy). Used by the
  // "Lock Rates Now" action in the modal and the inline banner button.
  const scrollToOosRateCard = () => {
    const el = document.getElementById('oos-rate-card');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const result = configLoading || !calcPkg || !selectedMkt ? null
    : calcQuote({ inputs, pkg: calcPkg, marketTier: selectedMkt, products: calcProducts, settings: calcSettings,
        aiMultiplierOverride: aiMultiplier,
        repCommissionRate: repProfile?.commission_rate ?? null,
        packageIncludes,
        excludedIncludes: excludedIncludeIds,
        includeSwaps,
        costComponents, // v3.5.41
      });

  // Flex block add-on cost (added to MRR and TCV)
  // Flex block honors the locked-in labor rate from MarketRateCard accept.
  // Chain: accepted (live) → snapshotted accepted (after lock) → package default → market_tier (legacy) → 165 floor
  const flexBlockRate = acceptedRates?.remote_support
    ?? pricingSnapshot?.acceptedRates?.remote_support
    ?? calcPkg?.rates?.remote_support
    ?? selectedMkt?.rates?.remote_support
    ?? 165;
  const flexBlock = (result && flexHours)
    ? calcFlexBlock(flexHours, flexBlockRate, calcSettings)
    : null;
  const flexBlockMRR    = flexBlock?.blockPrice   || 0;
  // v3.5.99.7.23: apply manual discounts to the live contract MRR so the
  // headline MRR, GM badge, and TCV reflect them before save. Same helper
  // quoteTotals.buildTotals uses, so display and persisted values match.
  const manualDiscountResolved = applyManualDiscounts(result?.finalMRR || 0, manualDiscounts);
  const discountedFinalMRR = result ? manualDiscountResolved.finalMRR : 0;
  const effectiveFinalMRR  = result ? discountedFinalMRR + flexBlockMRR : 0;
  // v3.7.8: one-time discount bases (Onboarding only on Managed IT, post-
  // incentive). resolved drives the live net + the Stripe prepay total.
  const oneTimeBases = {
    onboarding: (obIncentive?.effectiveFee != null ? Number(obIncentive.effectiveFee) : (result?.onboarding || 0)),
    hardware: 0, activation: 0, porting: 0,
  };
  const oneTimeResolved = applyOneTimeDiscounts(oneTimeBases, oneTimeDiscounts);

  if (configLoading) return <div style={{ padding: 24, color: '#6b7280', fontSize: 12 }}>Loading pricing data...</div>;

  // Multi-term preview — calc all 3 terms silently (safe: configLoading already guarded above)
  // v3.5.94.6: uses calcPkg / calcProducts / calcSettings rather than the
  // raw selectedPkg / products / settings so the term comparison cards
  // honor the locked pricing snapshot when the quote has prices locked.
  // Pre-deploy these called calcQuote with the LIVE references — meaning
  // on a locked quote the term cards would re-price at current rates,
  // diverging from the header MRR (which DOES use calcPkg/Products/Settings
  // — see line ~1017). Reported May 12 2026 via quote
  // 8f95586c-2730-4970-98e3-72a59be13d9c where the header showed $2,771
  // (locked at May 8 rates) but the selected 24-mo term card showed
  // $2,820 (current live rates). Reps and customers saw inconsistent
  // numbers; "Prices Locked" affordance lost meaning. Post-deploy the
  // term cards re-price across all three terms using the SAME pricing
  // basis as the header, so on an unlocked quote nothing changes
  // (calcPkg === selectedPkg, calcProducts === products, calcSettings
  // === settings) and on a locked quote everything agrees.
  // v3.5.99.7.23.1: apply the same manual discounts to each term's result so
  // the Term Comparison cards (and their TCV line) match the headline MRR and
  // KPI strip. Without this the cards showed the pre-discount engine MRR while
  // the headline showed the net — a visible $X/mo mismatch on discounted quotes.
  const multiTermResults = (result && calcPkg && selectedMkt) ? [12, 24, 36].map(term => ({
    term,
    result: discountedResult(calcQuote({ inputs: { ...inputs, contractTerm: term }, pkg: calcPkg, marketTier: selectedMkt,
      products: calcProducts, settings: calcSettings, aiMultiplierOverride: aiMultiplier, repCommissionRate: repProfile?.commission_rate ?? null,
      packageIncludes, excludedIncludes: excludedIncludeIds, includeSwaps, costComponents }), manualDiscounts)
  })) : null;

  // Compliance recommendations
  // v3.5.59: 5-value dropdown — each value maps directly to a single
  // compliance_tag, so the recommendation engine can highlight the matching
  // Compliance Program product (e.g. compliance='hipaa' → recommend any
  // product tagged 'hipaa', primarily the HIPAA Compliance Program).
  // Legacy 'moderate'/'high' values still mapped for any test quotes that
  // pre-date the migration.
  const COMPLIANCE_KEY_MAP = {
    hipaa:    ['hipaa'],
    soc2:     ['soc2'],
    pci:      ['pci'],
    cmmc:     ['cmmc'],
    moderate: ['hipaa','soc2'],   // legacy
    high:     ['pci','cmmc'],     // legacy
  };
  const complianceKey = COMPLIANCE_KEY_MAP[inputs.compliance] || [];
  const recommendedProducts = complianceKey.length > 0
    ? products.filter(p => p.compliance_tags?.some(t => complianceKey.includes(t)) && p.active)
    : [];
  // v3.5.81.3: Three-filter recommendation gating. Reps were seeing items
  // recommended that were already on the quote — either explicitly selected
  // OR bundled into the package's includes (the most common false positive,
  // e.g. SaaSAlerts and ThreatLocker showing up in BUSINESS PLUS includes
  // and ALSO in the Compliance Recommendations panel) — plus functional
  // substitutes (e.g. SentinelOne when ThreatLocker already covers the same
  // compliance need). Each false positive becomes friction in the new
  // proposal flow's declined-items review, so we filter aggressively here.
  //
  //   Filter A — drop products the rep has explicitly selected
  //   Filter B — drop products bundled into the package via package_includes
  //              (minus any the rep has unchecked via excludedIncludeIds)
  //   Filter C — drop products that share an exclusive_group with anything
  //              currently selected/included. Only fires when products in
  //              admin have been annotated with exclusive_group; pairs that
  //              aren't grouped pass through Filter C unchanged.
  //
  // Filter C depends on the products admin having marked substitute
  // relationships. If ThreatLocker and SentinelOne aren't both in (e.g.)
  // exclusive_group='endpoint_edr', SentinelOne will still show up in the
  // panel even when ThreatLocker is included. The exclusive_group column
  // already exists and is used by the picker's radio-button toggle (v3.5.59);
  // populating it for substitutable security products is a one-time data
  // task in Admin → Products.
  const selectedSet = new Set(inputs.selectedProducts || []);
  const excludedSet = new Set(excludedIncludeIds || []);
  const includedProductIds = new Set(
    (packageIncludes || [])
      .filter(i => !excludedSet.has(i.id))
      .map(i => i.product_id)
  );
  // Gather exclusive_groups already covered by something on the quote (selected
  // OR included). A product whose group is in this set is a redundant
  // substitute and should not be recommended.
  const coveredGroups = new Set();
  products.forEach(p => {
    if (!p.exclusive_group) return;
    if (selectedSet.has(p.id) || includedProductIds.has(p.id)) {
      coveredGroups.add(p.exclusive_group);
    }
  });
  const unselectedRecommended = recommendedProducts.filter(p => {
    if (selectedSet.has(p.id))        return false; // Filter A
    if (includedProductIds.has(p.id)) return false; // Filter B
    if (p.exclusive_group && coveredGroups.has(p.exclusive_group)) return false; // Filter C
    return true;
  });

  // Payment surcharge settings
  const ccSurcharge = parseFloat(settings?.payment_cc_surcharge) || 0.02;
  const achFee      = parseFloat(settings?.payment_ach_fee)      || 0;
  const checkFee    = parseFloat(settings?.payment_check_fee)    || 10;

  // v3.5.65: local mktColor/mktBg maps removed — use tierColor() and tierBg()
  // from marketRates.js, which know about both modern (premium/standard/
  // adjusted/secondary) and legacy (major_metro/mid_market/small_market/rural)
  // tier keys. Centralizes color choices so badges look identical across
  // QuotePage, BundleQuotePage, VoiceQuotePage, and MarketRateCard.
  const gc = result ? gmColor(result.impliedGM) : '#374151';
  const gb = result ? gmBg(result.impliedGM)    : '#f9fafb';

  // ─────────────────────────────────────────────────────────────────────────
  // v3.8.0 — Unified-UX workspace layout (flag-gated, DEFAULT OFF).
  //
  // Rollout Phase 4. Renders the same quote via the v3 WorkspaceShell (section
  // nav + focus pane + live rail) per the approved section map. It reuses EVERY
  // existing state value, handler, and child component from above — no calc,
  // save, HubSpot, proposal, or SignWell logic is duplicated or changed. The
  // legacy two-pane return below is completely untouched, so with the flag off
  // (production) behaviour is byte-identical. Enable per-browser with
  // localStorage ferrumiq_v3_quote='1' (or ?v3=1 to preview, ?v3=0 to force off).
  // ─────────────────────────────────────────────────────────────────────────
  const v3Enabled = (() => {
    try {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search);
        if (q.get('v3') === '1') return true;
        if (q.get('v3') === '0') return false;                       // per-load escape hatch
        const stored = window.localStorage.getItem('ferrumiq_v3_quote');
        if (stored === '1') return true;
        if (stored === '0') return false;                           // persistent opt-out
        return true;   // v3.8.2 — workspace is now the DEFAULT (Shaun sign-off 2026-08-04).
      }                //          Legacy layout stays reachable via ?v3=0 / stored '0'.
    } catch (e) { /* SSR / disabled storage — fall through to default */ }
    return true;
  })();

  if (v3Enabled) {
    const isNewQuote = (Number(inputs.users) || 0) === 0 && (Number(inputs.workstations) || 0) === 0;
    let seededSection = null;
    try { seededSection = new URLSearchParams(window.location.search).get('section'); } catch (e) { /* ignore */ }
    const activeSection = focusedSection || seededSection || (isNewQuote ? 'infrastructure' : 'products');

    const statusVariant = ({ won: 'green', approved: 'green', sent: 'blue', lost: 'red', expired: 'red', in_review: 'amber', draft: 'amber' })[quoteStatus] || 'gray';

    const v3Sections = [
      { id: 'client',         label: 'Client',        value: recipientBiz ? `${recipientBiz}${hubDealName ? ` · ${hubDealName}` : ''}` : 'Not set', status: recipientBiz ? 'ok' : 'empty' },
      { id: 'basis',          label: 'Service basis', value: (selectedMkt && selectedPkg) ? `${selectedMkt.name} · ${selectedPkg.name} · ${inputs.contractTerm} mo` : 'Not set', status: selectedPkg ? 'ok' : 'empty' },
      { id: 'infrastructure', label: 'Infrastructure',value: `${inputs.users || 0} users · ${inputs.workstations || 0} wks · ${inputs.servers || 0} srv`, status: 'ok' },
      { id: 'products',       label: 'Products',      value: `${(inputs.selectedProducts || []).length} selected`, status: 'ok' },
      { id: 'onboarding',     label: 'Onboarding',    value: result ? `${fmt$0(obIncentive?.effectiveFee ?? result.onboarding)}${obIncentive?.discountPct ? ` · ${obIncentive.discountPct}% off` : ''}` : '—', status: 'ok' },
      { id: 'terms',          label: 'Special terms', value: `${addendums.length} addendum${addendums.length === 1 ? '' : 's'}`, status: addendums.length ? 'ok' : 'empty' },
      { id: 'documents',      label: 'Documents',     value: 'Sent agreements & files', status: 'ok' },
      { id: 'notes',          label: 'Notes',         value: dealDescription ? dealDescription.slice(0, 32) : '—', status: 'ok' },
      { id: 'engagement',     label: 'Engagement',    value: existingQuote?.id ? 'View activity' : 'Not sent', status: 'ok' },
      { id: 'history',        label: 'History',       value: existingQuote?.id ? 'Versions' : '—', status: existingQuote?.id ? 'ok' : 'empty' },
    ];

    const burdenedRateV3 = getBurdenedRate(settings);
    const flexLaborCostV3 = flexBlock ? flexBlock.hours * burdenedRateV3 : 0;
    const totalCostV3 = result ? result.totalCost + flexLaborCostV3 : 0;
    const effectiveGMV3 = result ? (effectiveFinalMRR > 0 ? 1 - totalCostV3 / effectiveFinalMRR : 0) : 0;
    const targetGMV3 = parseFloat(settings?.target_gross_margin);
    const effectiveNRCV3 = obIncentive?.effectiveFee ?? (result?.onboarding || 0);

    const v3Header = (
      <QuoteHeader
        id={existingQuote?.quote_number || 'New quote'}
        title={proposalName || recipientBiz || 'Managed IT quote'}
        status={quoteStatus}
        statusVariant={statusVariant}
        extraPills={hubDealId ? [{ variant: 'green', label: 'HubSpot linked' }] : []}
        savedLabel={saving ? 'Saving…' : (saveMsg || undefined)}
      >
        <V3Button variant="secondary" size="sm" onClick={() => setShowAssumptions(true)} disabled={!existingQuote?.id}>
          {assumptionsComplete ? '✓' : '⚠'} Customer Story
        </V3Button>
        <V3Button variant="secondary" size="sm" onClick={() => setSoPreviewModalOpen(true)}>Preview SO</V3Button>
        <V3Button
          variant="primary" size="sm"
          onClick={() => { if (!ratesLocked) { setShowLockRatesModal('send_proposal'); return; } sendProposal(); }}
          disabled={proposalSending || !existingQuote?.id || !assumptionsComplete}
        >
          {proposalSending ? 'Sending…' : (activeProposal && activeProposal.status !== 'superseded' ? 'Resend Proposal' : 'Send Proposal')}
        </V3Button>
        <V3Button variant="dark" size="sm" onClick={saveQuote} disabled={saving || isReadOnly}>{saving ? 'Saving…' : 'Save'}</V3Button>
      </QuoteHeader>
    );

    const v3Rail = (
      <>
        <LiveTotalCard
          headline={result ? fmt$0(effectiveFinalMRR) : '—'}
          rows={result ? [
            { label: 'Onboarding',     value: fmt$0(effectiveNRCV3) },
            { label: 'Contract value', value: fmt$0(effectiveFinalMRR * inputs.contractTerm + effectiveNRCV3) },
            { label: 'Gross margin',   value: fmtPct(effectiveGMV3), color: v3.color.moneyPositive },
            ...(result.itsoMRR > 0 ? [{ label: 'ITSO Advisory', value: fmt$0(result.itsoMRR) }] : []),
          ] : []}
        />
        {result && (
          <RailCard title="Cost model — monthly">
            <LI lbl="Tooling / stack" v={result.toolingCost} bold/>
            <LI lbl={`Labor (${(result.svcHrs || 0).toFixed(1)} hrs × ${fmt$(burdenedRateV3)}/hr)`} v={result.svcCost} ind/>
            <LI lbl="Add-on delivery cost" v={result.addonCost} ind/>
            {result.itsoMRR > 0 && result.itsoLabel && (
              <LI lbl={`${result.itsoLabel} labor`} v={result.itsoCost} ind/>
            )}
            {flexBlock && <LI lbl={`Flex Time labor (${flexBlock.hours} hrs)`} v={flexLaborCostV3} ind/>}
            <LI lbl="Estimated Total Cost" v={totalCostV3} bold/>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 6px', background: gmBg(effectiveGMV3), borderRadius: 4, marginTop: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: gmColor(effectiveGMV3) }}>Implied Gross Margin</span>
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(effectiveGMV3) }}>{fmtPct(effectiveGMV3)}</span>
            </div>
            <div style={{ fontSize: 10, color: v3.color.mutedSoft, marginTop: 4, fontStyle: 'italic' }}>Recurring revenue and cost only — excludes one-time fees.</div>
          </RailCard>
        )}
        {result && (
          <RailCard title="Margin floor">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <V3Pill variant={isFinite(targetGMV3) && effectiveGMV3 >= targetGMV3 ? 'green' : 'amber'}>
                {isFinite(targetGMV3) && effectiveGMV3 >= targetGMV3 ? 'Pass' : 'Below target'}
              </V3Pill>
              <span style={{ fontSize: 13, color: v3.color.muted }}>
                {fmtPct(effectiveGMV3)}{isFinite(targetGMV3) ? ` · target ${fmtPct(targetGMV3)}` : ''}
              </span>
            </div>
          </RailCard>
        )}
        {result && (
          <RailCard title="Ready to send?">
            {[
              ['Recipient + email', !!(recipientBiz && recipientEmail)],
              ['Customer Story captured', !!assumptionsComplete],
              ['Margin above floor', !isFinite(targetGMV3) || effectiveGMV3 >= targetGMV3],
              ['OOS rates locked', !!ratesLocked],
            ].map(([label, ok]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0', fontSize: 13 }}>
                <span style={{ width: 16, height: 16, borderRadius: '50%', flex: '0 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
                  background: ok ? v3.color.status.greenBg : v3.color.status.amberBg, color: ok ? v3.color.status.greenText : v3.color.status.amberText }}>{ok ? '✓' : '!'}</span>
                <span style={{ flex: 1, color: ok ? v3.color.bodySoft : v3.color.status.amberText }}>{label}</span>
              </div>
            ))}
          </RailCard>
        )}
      </>
    );

    // Proposal-status banner + Send/Resend Service Order arc (§10C). Reproduced
    // for the workspace header but wired to the SAME shared handlers as the
    // legacy banner — handleSendServiceOrder / setShowLockRatesModal / ratesLocked
    // / soSending·soSuccess·soError — so the binding-document flow is identical.
    const v3ProposalBanner = (activeProposal && activeProposal.status !== 'superseded') ? (() => {
      const s = activeProposal.status;
      const url = proposalPublicUrl(activeProposal.public_token);
      const tone = s === 'approved' ? 'green' : s === 'declined' ? 'red' : s === 'expired' ? 'gray' : s === 'viewed' ? 'amber' : 'blue';
      const bg = v3.color.status[`${tone}Bg`];
      const fg = v3.color.status[`${tone}Text`];
      const headline = s === 'approved' ? `Approved by ${activeProposal.approver_name || 'client'}`
        : s === 'declined' ? 'Proposal declined'
        : s === 'expired' ? 'Proposal expired — resend to refresh'
        : s === 'viewed' ? `Client viewed ${activeProposal.view_count || 1}× — last ${new Date(activeProposal.last_viewed_at).toLocaleDateString()}`
        : 'Proposal sent — awaiting client review';
      const swDocs = (existingQuote?.inputs?.signwellDocuments) || [];
      const swDocMatches = swDocs.filter(d => d.type === 'managed_it_quote' && (d.triggered_by === 'proposal_approval' || d.triggered_by === 'manual_send') && d.proposal_id === activeProposal.id);
      const swDoc = swDocMatches.length ? swDocMatches[swDocMatches.length - 1] : null;
      const swSigned = swDoc && (swDoc.status === 'signed' || swDoc.status === 'completed' || !!swDoc.signed_at);
      const swDeclined = swDoc && swDoc.status === 'declined';
      const showSoCta = s !== 'declined' && s !== 'expired';
      const soClick = () => { if (!ratesLocked) { setShowLockRatesModal('send_service_order'); return; } handleSendServiceOrder(); };
      return (
        <div style={{ padding: '10px 24px', background: bg, borderBottom: `1px solid ${v3.color.border}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: v3.font.size.label, fontWeight: 700, color: fg }}>{headline}</div>
            <div style={{ fontSize: v3.font.size.small, color: v3.color.muted, marginTop: 2 }}>Sent {new Date(activeProposal.sent_at).toLocaleDateString()}</div>
          </div>
          <V3Button variant="secondary" size="sm" onClick={() => navigator.clipboard?.writeText(url).then(() => setProposalSendMsg('URL copied')).catch(() => {})}>Copy URL</V3Button>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 12px', background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: 600, color: v3.color.body, textDecoration: 'none' }}>↗ Open</a>
          {showSoCta && !swDoc && (
            <V3Button variant="primary" size="sm" onClick={soClick} disabled={soSending || soSuccess}>
              {soSending ? 'Sending…' : soSuccess ? 'Sent ✓ refreshing…' : `${!ratesLocked ? '⚠ ' : ''}Send Service Order →`}
            </V3Button>
          )}
          {showSoCta && swDoc && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <V3Pill variant={swDeclined ? 'red' : swSigned ? 'green' : 'blue'}>{swDeclined ? 'Service Order Declined' : swSigned ? 'Service Order Signed' : 'Service Order Sent'}</V3Pill>
              {!swSigned && <V3Button variant="secondary" size="sm" onClick={soClick} disabled={soSending || soSuccess}>{soSending ? 'Resending…' : soSuccess ? 'Sent ✓' : '↻ Resend'}</V3Button>}
            </span>
          )}
          {soError && <span style={{ fontSize: v3.font.size.small, color: v3.color.status.redText }}>{soError.message}{soError.detail ? ` — ${soError.detail}` : ''}</span>}
        </div>
      );
    })() : null;

    const v3UnlockModal = showUnlockModal ? (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 440, boxShadow: v3.shadow.modal }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: v3.color.navy, margin: '0 0 10px' }}>⚠ Unlock Pricing</h3>
          <p style={{ fontSize: 12, color: v3.color.bodySoft, lineHeight: 1.6, margin: '0 0 14px' }}>Unlocking will allow rates to update based on current package pricing. If you already sent this quote, consider a new revision instead.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <V3Button variant="secondary" size="sm" onClick={() => setShowUnlockModal(false)}>Cancel</V3Button>
            <V3Button variant="primary" size="sm" onClick={async () => {
              setPricingSnapshot(null); setPriceLockDate(null); setShowUnlockModal(false);
              if (existingQuote?.id) {
                await supabase.from('quotes').update({ pricing_snapshot: null, price_locked_at: null, price_locked_by: null }).eq('id', existingQuote.id);
                await saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: { ...inputs, proposalName }, totals: { finalMRR: result?.finalMRR }, lineItems: [], profile, note: 'Pricing unlocked — rates now reflect current package pricing' });
              }
            }}>Unlock &amp; Recalculate</V3Button>
          </div>
        </div>
      </div>
    ) : null;

    const v3LockRatesModal = showLockRatesModal ? (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 480, boxShadow: v3.shadow.modal }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: v3.color.status.amberText, margin: '0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
          <p style={{ fontSize: 12, color: v3.color.bodySoft, lineHeight: 1.6, margin: '0 0 14px' }}>
            The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. {showLockRatesModal === 'send_proposal' ? 'If the client approves, Service Order generation will fail until rates are locked.' : 'The Service Order can’t be generated until OOS rates are locked.'} Open <strong>Service basis</strong> and click <em>Accept All Rates</em>.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <V3Button variant="secondary" size="sm" onClick={() => setShowLockRatesModal(null)}>Cancel</V3Button>
            <V3Button variant="secondary" size="sm" onClick={() => { const action = showLockRatesModal; setShowLockRatesModal(null); if (action === 'send_proposal') sendProposal(); else if (action === 'send_service_order') handleSendServiceOrder(); }}>Send Anyway</V3Button>
            <V3Button variant="dark" size="sm" onClick={() => { setShowLockRatesModal(null); setFocusedSection('basis'); setTimeout(scrollToOosRateCard, 60); }}>Go to Rates ↓</V3Button>
          </div>
        </div>
      </div>
    ) : null;

    const renderV3Focus = () => {
      switch (activeSection) {
        case 'client':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Client</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Who the proposal is addressed to, and the linked HubSpot deal.</p></div>
              <SubCard eyebrow="HubSpot & sync">
                <HubSpotConnect
                  dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName} description={dealDescription}
                  onDescriptionChange={setDealDescription} quoteNumber={existingQuote?.quote_number}
                  mrr={effectiveFinalMRR || result?.finalMRR}
                  contractValue={result ? effectiveFinalMRR * inputs.contractTerm + result.onboarding : 0}
                  packageName={selectedPkg?.name} contractTerm={inputs.contractTerm} existingQuoteId={existingQuote?.id}
                  clientName={recipientBiz}
                  onConnect={full => {
                    setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname);
                    if (full.company) {
                      if (full.company.name) setRecipientBiz(full.company.name);
                      const addr = [full.company.address, full.company.address2, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
                      if (addr) setRecipientAddress(addr);
                      if (full.company.zip) { setClientZip(full.company.zip); const zr = lookupZip(full.company.zip); setZipResult(zr); if (zr) applyZip(zr); }
                    } else {
                      const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
                      if (extracted) setRecipientBiz(extracted);
                    }
                    if (full.contact) {
                      const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
                      if (name) setRecipientContact(name);
                      if (full.contact.email) setRecipientEmail(full.contact.email);
                    }
                    if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Managed IT Services — ${full.company?.name || full.deal.dealname}`);
                  }}
                  onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
                  onSync={syncToDeal}
                />
                <div style={{ marginTop: v3.space.md }}>
                  <SPTConnect proposalId={sptProposalId} quoteId={existingQuote?.id} clientName={recipientBiz} quoteNumber={existingQuote?.quote_number} settings={settings} onConnect={(pid) => setSptProposalId(pid)} onDisconnect={() => setSptProposalId(null)} />
                </div>
              </SubCard>
              <SubCard eyebrow="Recipient">
                <Fld lbl="Assigned Sales Rep"><RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} padding="4px 7px" fontSize={10} includeTextColor={false} /></Fld>
                <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="e.g. FerrumIT Managed IT — Acme Corp" /></Fld>
                <Fld lbl="Recipient Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp" /></Fld>
                <Grid2>
                  <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith" /></Fld>
                  <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com" /></Fld>
                </Grid2>
                <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601" /></Fld>
                <Grid2>
                  <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567" /></Fld>
                  <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321" /></Fld>
                </Grid2>
                {(hubContactId || hubCompanyId) && (
                  <div style={{ marginTop: 8 }}>
                    <V3Button variant="secondary" size="sm" onClick={pushAllToHubspot} disabled={hubPushing}>{hubPushing ? '⏳ Pushing…' : '↑ Push to HubSpot'}</V3Button>
                    {hubPushMsg && <span style={{ fontSize: 11, color: v3.color.muted, marginLeft: 8 }}>{hubPushMsg}</span>}
                  </div>
                )}
              </SubCard>
              <SubCard eyebrow="Internal notifications" hint="Not visible to the customer">
                <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com" /></Fld>
              </SubCard>
            </div>
          );
        case 'basis':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Service basis</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Market, package, and contract term — the pricing foundation.</p></div>
              <SubCard eyebrow="Client location">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input value={clientZip} onChange={e => handleZipChange(e.target.value)} placeholder="Zip code — auto-filled from address"
                    style={{ flex: 1, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, fontFamily: v3.font.mono, fontWeight: 500, outline: 'none' }} />
                  {zipResult && !zipApplied && <V3Button variant="dark" size="sm" onClick={() => applyZip()}>Apply →</V3Button>}
                </div>
                {zipResult && (
                  <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: v3.radius.input, background: tierBg(zipResult.tier), fontSize: v3.font.size.small }}>
                    <span style={{ fontWeight: 700, color: tierColor(zipResult.tier) }}>{zipResult.name || `ZIP ${zipResult.zip}`} → {marketTiers.find(t => t.tier_key === zipResult.tier)?.name}</span>
                    {zipApplied && <span style={{ marginLeft: 6, color: v3.color.status.greenText, fontWeight: 700 }}>✓ Applied</span>}
                  </div>
                )}
              </SubCard>
              <SubCard eyebrow="Market tier">
                {showMktRecommend && pendingMultiplier && (
                  <div style={{ background: v3.color.inkBg, border: `1px solid ${v3.color.inkBorder}`, borderRadius: v3.radius.input, padding: '8px 10px', marginBottom: 8 }}>
                    <div style={{ fontSize: v3.font.size.small, fontWeight: 700, color: v3.color.ink, marginBottom: 4 }}>📊 AI Market Recommendation</div>
                    <div style={{ fontSize: v3.font.size.small, color: v3.color.bodySoft, marginBottom: 6 }}>
                      <strong>{pendingMultiplier.city}, {pendingMultiplier.state}</strong> — {pendingMultiplier.multiplier === 1 ? 'No adjustment' : pendingMultiplier.multiplier > 1 ? `+${Math.round((pendingMultiplier.multiplier - 1) * 100)}% pricing` : `-${Math.round((1 - pendingMultiplier.multiplier) * 100)}% pricing`}
                    </div>
                    <V3Button variant="primary" size="sm" onClick={() => { setAiMultiplier(pendingMultiplier.multiplier); setAiMultiplierTier(pendingMultiplier.tier); setShowMktRecommend(false); }}>Apply AI Rate</V3Button>
                    <V3Button variant="ghost" size="sm" onClick={() => setShowMktRecommend(false)} style={{ marginLeft: 6 }}>Keep Manual</V3Button>
                  </div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <MarketStatusBadge aiMultiplier={aiMultiplier} aiMultiplierTier={aiMultiplierTier} selectedMkt={selectedMkt} marketCity={marketCity} marketState={marketState} locked={!!pricingSnapshot}
                    onRunAnalysis={aiMultiplier != null ? (() => { setAiMultiplier(null); setAiMultiplierTier(null); }) : undefined} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {marketTiers.map(t => {
                    const sel = selectedMkt?.id === t.id;
                    return (
                      <button key={t.id} type="button" onClick={() => { setSelectedMkt(t); setAiMultiplier(null); setAiMultiplierTier(null); }}
                        style={{ padding: '7px 14px', borderRadius: v3.radius.input, cursor: 'pointer', fontSize: v3.font.size.label, fontWeight: 600,
                          border: `1px solid ${sel ? v3.color.navy : v3.color.border}`, background: sel ? v3.color.navy : v3.color.card, color: sel ? '#fff' : v3.color.body, opacity: aiMultiplier != null ? 0.55 : 1 }}>
                        {staticTierToMILabel(t.tier_key)}
                      </button>
                    );
                  })}
                </div>
              </SubCard>
              <SubCard eyebrow="Package & term">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {packages.map(p => {
                    const sel = selectedPkg?.id === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => setSelectedPkg(p)}
                        style={{ textAlign: 'left', padding: '10px 12px', borderRadius: v3.radius.input, cursor: 'pointer',
                          border: `1px solid ${sel ? v3.color.ink : v3.color.border}`, background: sel ? v3.color.inkBg : v3.color.card }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: v3.font.size.label, fontWeight: 600, color: sel ? v3.color.ink : v3.color.body }}>{p.name}</span>
                          <span style={{ fontSize: v3.font.size.small, fontFamily: v3.font.mono, color: v3.color.muted }}>${p.ws_rate}/WS · ${p.user_rate}/US</span>
                        </div>
                        {p.ideal_desc && <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, marginTop: 2 }}>{p.ideal_desc}</div>}
                      </button>
                    );
                  })}
                </div>
                {result?.recommended && result.recommended !== selectedPkg?.name && (
                  <div style={{ marginTop: 8, padding: '6px 10px', background: v3.color.status.amberBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.amberText }}>⚡ Recommended: <strong>{result.recommended}</strong></div>
                )}
                <div style={{ marginTop: 12 }}>
                  <Grid2>
                    <Fld lbl="Contract Term"><SI v={inputs.contractTerm} s={v => set('contractTerm', +v)} opts={getContractTermOptions(settings)} /></Fld>
                    <Fld lbl="Quote Status"><SI v={quoteStatus} s={setQuoteStatus} opts={[['draft', 'Draft'], ['in_review', 'In Review'], ['approved', 'Approved'], ['sent', 'Sent'], ['won', 'Won'], ['lost', 'Lost'], ['expired', 'Expired']]} /></Fld>
                  </Grid2>
                </div>
              </SubCard>
              {multiTermResults && (
                <SubCard eyebrow="Term comparison" hint="click a term to switch">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    {multiTermResults.map(({ term, result: r }) => {
                      const isCurrent = term === inputs.contractTerm;
                      const termMRR = r.finalMRR + flexBlockMRR;
                      return (
                        <button key={term} type="button" onClick={() => set('contractTerm', term)}
                          style={{ textAlign: 'center', cursor: 'pointer', borderRadius: v3.radius.input, padding: '12px 10px',
                            border: `1px solid ${isCurrent ? v3.color.ink : v3.color.border}`, background: isCurrent ? v3.color.inkBg : v3.color.card }}>
                          <div style={{ fontSize: v3.font.size.small, fontWeight: 600, color: v3.color.muted }}>{term} months</div>
                          <div style={{ fontFamily: v3.font.mono, fontSize: 20, fontWeight: 600, marginTop: 4, letterSpacing: '-0.4px', color: isCurrent ? v3.color.ink : v3.color.body }}>{fmt$0(termMRR)}</div>
                          <div style={{ fontSize: v3.font.size.micro, color: v3.color.mutedSoft, marginTop: 4 }}>TCV {fmt$0(termMRR * term + r.onboarding)}</div>
                        </button>
                      );
                    })}
                  </div>
                  {(() => {
                    const r36 = multiTermResults.find(t => t.term === 36)?.result;
                    if (!r36) return null;
                    const subtotal = r36.discountableSubtotal || 0;
                    const floor = r36.floor || 0;
                    const headlineRate = r36.discRate || 0;
                    const aboveFloor = Math.max(subtotal - floor, 0);
                    if (subtotal <= 0 || headlineRate <= 0) return null;
                    const effectiveRate = aboveFloor > 0 ? (aboveFloor * headlineRate) / subtotal : 0;
                    const constraining = effectiveRate < headlineRate * 0.70;
                    const rates = multiTermResults.map(({ term, result: r }) => `${term}mo ${((r?.discRate || 0) * 100).toFixed(0)}%`).join(' · ');
                    return constraining ? (
                      <div style={{ marginTop: 12, padding: '10px 12px', background: v3.color.status.amberBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.amberText, lineHeight: 1.5 }}>
                        <strong>Why the term discount looks small:</strong> discounts ({rates}) apply only to revenue above the {fmt$(floor)} floor. Here {fmt$(subtotal)} is discountable, {fmt$(aboveFloor)} of it above floor — so the headline {(headlineRate * 100).toFixed(0)}% becomes an effective {(effectiveRate * 100).toFixed(1)}%. Pitch as “up to {(headlineRate * 100).toFixed(0)}% off” or focus on TCV.
                      </div>
                    ) : (
                      <details style={{ marginTop: 12, fontSize: v3.font.size.small, color: v3.color.muted }}>
                        <summary style={{ cursor: 'pointer' }}>How are these discounts calculated?</summary>
                        <div style={{ marginTop: 6, lineHeight: 1.5 }}>Contract discounts ({rates}) apply to the {fmt$(subtotal)}/mo discountable revenue above the {fmt$(floor)} floor. {fmt$(aboveFloor)} is above floor here, so the discount is close to the headline rate.</div>
                      </details>
                    );
                  })()}
                </SubCard>
              )}
              <SubCard eyebrow="Out-of-scope rate schedule" hint="lock before sending — tracked in “Ready to send?”">
                <div id="oos-rate-card">
                  <MarketRateCard quoteId={existingQuote?.id} clientZip={clientZip} fallbackMarket={selectedMkt}
                    onRatesAccepted={(rates, suggestedTier, analysis) => {
                      if (rates) setAcceptedRates(rates);
                      if (analysis?.pricing_multiplier != null) { setPendingMultiplier({ multiplier: analysis.pricing_multiplier, tier: analysis.market_tier, city: analysis.city, state: analysis.state }); setShowMktRecommend(true); }
                      if (suggestedTier) setAcceptedMktTier(suggestedTier);
                      if (analysis) setMarketAnalysis({ ...analysis, rates: rates || analysis.rates });
                    }} />
                </div>
              </SubCard>
            </div>
          );
        case 'infrastructure':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Infrastructure</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Counts that drive per-unit pricing and onboarding fees.</p></div>
              <SubCard eyebrow="People">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <BigNumber label="Users" value={inputs.users} onChange={v => set('users', v)} />
                  <BigNumber label="Shared mailboxes" value={inputs.sharedMailboxes} onChange={v => set('sharedMailboxes', v)} />
                </div>
              </SubCard>
              <SubCard eyebrow="Devices">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <BigNumber label="Workstations" value={inputs.workstations} onChange={v => set('workstations', v)} />
                  <BigNumber label="Total devices" value={inputs.endpoints} onChange={v => set('endpoints', v)} />
                  <BigNumber label="Mobile devices" value={inputs.mobileDevices || 0} onChange={v => set('mobileDevices', v)} />
                  <BigNumber label="Servers" value={inputs.servers} onChange={v => set('servers', v)} />
                </div>
              </SubCard>
              <SubCard eyebrow="Sites & cloud">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
                  <BigNumber label="Locations" value={inputs.locations} onChange={v => set('locations', v)} />
                  <BigNumber label="Cloud tenants" value={inputs.cloudTenants} onChange={v => set('cloudTenants', v)} />
                  <BigNumber label="Domains" value={inputs.domains} onChange={v => set('domains', v)} />
                  <BigNumber label="Vendors" value={inputs.vendors} onChange={v => set('vendors', v)} />
                </div>
              </SubCard>
              <SubCard eyebrow="Risk & compliance">
                <Grid2>
                  <Fld lbl="Threat Profile" sub="oversight tier"><SI v={inputs.industryRisk} s={v => set('industryRisk', v)} opts={[['none', 'None'], ['low', 'Standard'], ['medium', 'Elevated'], ['high', 'Critical']]} /></Fld>
                  <Fld lbl="Compliance" sub="adds program product"><SI v={inputs.compliance} s={v => set('compliance', v)} opts={[['none', 'None'], ['hipaa', 'HIPAA'], ['soc2', 'SOC 2'], ['pci', 'PCI DSS'], ['cmmc', 'CMMC']]} /></Fld>
                  <Fld lbl="Complexity"><SI v={inputs.complexity} s={v => set('complexity', v)} opts={[['low', 'Low'], ['medium', 'Medium'], ['high', 'High']]} /></Fld>
                  <Fld lbl="Coverage"><SI v={inputs.requestedCoverage} s={v => set('requestedCoverage', v)} opts={[['business_hours', '8×5'], ['24x5', '24×5'], ['24x7', '24×7']]} /></Fld>
                </Grid2>
              </SubCard>
            </div>
          );
        case 'products':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Products</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Add-on services layered on the package · drives line items + SoS docs.</p></div>
              <SubCard eyebrow="Find a product" hint={`${(inputs.selectedProducts || []).length} selected`}>
                <div style={{ position: 'relative' }}>
                  <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search the catalog — e.g. “KnowBe4”, “SentinelOne”…"
                    style={{ width: '100%', padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, color: v3.color.body, outline: 'none' }} />
                  {productSearch && <button type="button" onClick={() => setProductSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: v3.color.mutedSoft, cursor: 'pointer', fontSize: 16 }}>×</button>}
                </div>
              </SubCard>
              {unselectedRecommended.length > 0 && (
                <SubCard eyebrow={`⚠ Compliance recommendations — ${({ hipaa: 'HIPAA', soc2: 'SOC 2', pci: 'PCI DSS', cmmc: 'CMMC' }[inputs.compliance] || 'Compliance')}`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {unselectedRecommended.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: v3.color.status.amberBg, borderRadius: v3.radius.input }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: v3.font.size.label, fontWeight: 700, color: v3.color.navy }}>{p.name}</div>
                          {p.recommendation_reason && <div style={{ fontSize: v3.font.size.small, color: v3.color.status.amberText, marginTop: 1 }}>{p.recommendation_reason}</div>}
                        </div>
                        <V3Button variant="dark" size="sm" onClick={() => setInputs(prev => ({ ...prev, selectedProducts: [...(prev.selectedProducts || []), p.id] }))}>+ Add</V3Button>
                      </div>
                    ))}
                  </div>
                </SubCard>
              )}
              {selectedPkg && (packageIncludes.length > 0 || includesLoading) && (
                <SubCard eyebrow="Package includes">
                  <PackageIncludes packageName={selectedPkg.name} includes={packageIncludes} excludedIds={excludedIncludeIds} loading={includesLoading}
                    includedItemsCost={result?.includedItemsCost} includeSwaps={includeSwaps} availableProducts={products}
                    onToggleExclude={(includeId) => { setExcludedIncludeIds(prev => { const wasExcluded = prev.includes(includeId); if (wasExcluded) { setIncludeSwaps(swaps => { if (!(includeId in swaps)) return swaps; const next = { ...swaps }; delete next[includeId]; return next; }); return prev.filter(id => id !== includeId); } return [...prev, includeId]; }); }}
                    onSwap={(includeId, substituteProductId) => { setExcludedIncludeIds(prev => prev.includes(includeId) ? prev : [...prev, includeId]); setIncludeSwaps(prev => ({ ...prev, [includeId]: substituteProductId })); }}
                    onUnswap={(includeId) => { setIncludeSwaps(prev => { if (!(includeId in prev)) return prev; const next = { ...prev }; delete next[includeId]; return next; }); }} />
                </SubCard>
              )}
              {Object.entries(productsByCategory).map(([cat, catProducts]) => {
                const excludedSet = new Set(excludedIncludeIds || []);
                const activeIncludeProductIds = new Set(packageIncludes.filter(i => !excludedSet.has(i.id)).map(i => i.product_id));
                const swapSubIds = new Set(Object.values(includeSwaps || {}).filter(Boolean));
                const q = productSearch.trim().toLowerCase();
                const visibleProducts = catProducts.filter(p => !activeIncludeProductIds.has(p.id) && !swapSubIds.has(p.id) && !p.internal_only
                  && (!q || (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)));
                if (visibleProducts.length === 0) return null;
                return (
                  <SubCard key={cat} eyebrow={cat}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {visibleProducts.map(p => {
                        const sel = isSelected(p.id);
                        return (
                          <div key={p.id} onClick={() => toggleProduct(p.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: v3.radius.input, cursor: 'pointer',
                              border: `1px solid ${sel ? v3.color.inkBorder : v3.color.border}`, background: sel ? v3.color.inkBg : v3.color.card }}>
                            <div style={{ width: 22, height: 14, borderRadius: 7, background: sel ? v3.color.ink : v3.color.faint, position: 'relative', flexShrink: 0 }}>
                              <div style={{ position: 'absolute', top: 2, left: sel ? 10 : 2, width: 10, height: 10, borderRadius: '50%', background: '#fff', transition: 'left .1s' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: v3.font.size.label, fontWeight: 600, color: sel ? v3.color.ink : v3.color.body }}>{p.name}</div>
                              {p.description && <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft }}>{p.description}</div>}
                            </div>
                            {sel && p.qty_driver === 'manual' && (
                              <input type="number" min="0" onClick={e => e.stopPropagation()} value={parseInt(inputs.manualQuantities?.[p.id] || 0) || ''} placeholder="qty"
                                onChange={e => { const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0); setInputs(prev => ({ ...prev, manualQuantities: { ...(prev.manualQuantities || {}), [p.id]: next } })); }}
                                style={{ width: 56, padding: '4px 6px', border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontFamily: v3.font.mono, textAlign: 'center' }} />
                            )}
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: v3.font.size.small, fontFamily: v3.font.mono, fontWeight: 600, color: v3.color.body }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'license' : p.qty_driver}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </SubCard>
                );
              })}
              {result?.itsoMRR > 0 && result?.itsoLabel && (
                <SubCard eyebrow="Strategic advisory" hint="auto-derived from Threat Profile">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: v3.font.size.label, fontWeight: 700, color: v3.color.ink, marginBottom: 3 }}>{result.itsoLabel}</div>
                      <div style={{ fontSize: v3.font.size.small, color: v3.color.muted, lineHeight: 1.5 }}>Senior advisory time scaled to the client's risk profile (vCIO, executive reporting, governance). Adjust via Threat Profile in Infrastructure.</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: v3.font.mono, color: v3.color.ink }}>{fmt$(result.itsoMRR)}</div>
                      <div style={{ fontSize: v3.font.size.micro, color: v3.color.mutedSoft, textTransform: 'uppercase' }}>/month</div>
                    </div>
                  </div>
                </SubCard>
              )}
              {selectedPkg && selectedPkg.flex_time_model !== 'all_inclusive' && (
                <SubCard eyebrow="Flex time block">
                  <FlexTimeMeter pkg={selectedPkg} workstations={inputs.workstations} users={inputs.users} addonHours={flexHours} />
                  <div style={{ marginTop: 12 }}>
                    <FlexTimeSelector remoteRate={flexBlockRate} settings={settings} selectedHours={flexHours} onChange={hrs => setFlexHours(hrs)}
                      onApply={async (hrs) => {
                        setFlexHours(hrs);
                        if (!existingQuote?.id) { setFlexSaveMsg('⚠ Save the quote first, then apply the Flex Time block.'); setTimeout(() => setFlexSaveMsg(''), 4500); return; }
                        try {
                          const updatedInputs = { ...inputs, proposalName, flexHours: hrs || null };
                          const { error: upErr } = await supabase.from('quotes').update({ inputs: updatedInputs }).eq('id', existingQuote.id);
                          if (upErr) throw upErr;
                          await saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: updatedInputs, totals: { finalMRR: effectiveFinalMRR }, lineItems: [], profile, note: hrs ? `Flex Time block applied: ${hrs} hours` : 'Flex Time block removed' });
                          setFlexSaveMsg(hrs ? `✓ ${hrs}-hour Flex Time block applied and saved` : '✓ Flex Time block removed and saved');
                          setTimeout(() => setFlexSaveMsg(''), 3500);
                        } catch (e) { setFlexSaveMsg(`✗ Save failed: ${e?.message || 'unknown error'} — please try again`); setTimeout(() => setFlexSaveMsg(''), 6000); }
                      }}
                      mode="managed" packageModel={selectedPkg?.flex_time_model || 'none'} includedMinsPerWS={selectedPkg?.flex_included_mins_per_ws || 0} workstations={inputs.workstations || 0} repCommissionRate={repProfile?.commission_rate ?? null} />
                    {flexSaveMsg && <div style={{ marginTop: 8, padding: '8px 12px', fontSize: 11, fontWeight: 600, textAlign: 'center', borderRadius: 4, color: flexSaveMsg.startsWith('✓') ? '#065f46' : flexSaveMsg.startsWith('⚠') ? '#92400e' : '#991b1b', background: flexSaveMsg.startsWith('✓') ? '#d1fae5' : flexSaveMsg.startsWith('⚠') ? '#fef3c7' : '#fee2e2' }}>{flexSaveMsg}</div>}
                  </div>
                </SubCard>
              )}
              {result && result.finalMRR <= result.floor + 0.01 && result.riskAdjMRR < result.floor && (
                <div style={{ padding: '10px 14px', background: v3.color.status.amberBg, border: `1px solid ${v3.color.status.amberText}33`, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.amberText }}>
                  ⚠ Price set by minimum commitment floor ({fmt$(result.floor)}). Rate-based would be {fmt$(result.riskAdjMRR)}.
                </div>
              )}
              {result && (
                <SubCard eyebrow="Monthly recurring — breakdown">
                  <SH l="Managed IT Services" />
                  <LI lbl={`WS (${inputs.workstations} × $${selectedPkg?.ws_rate})`} v={result.wB} ind/>
                  <LI lbl={`US (${inputs.users} × $${selectedPkg?.user_rate})`} v={result.uB} ind/>
                  <LI lbl="Servers" v={result.sB} ind/>
                  <LI lbl="Locations" v={result.lB} ind/>
                  <LI lbl="Cloud tenants" v={result.tB} ind/>
                  <LI lbl="Additional vendors" v={result.vB} ind/>
                  <LI lbl="Coverage uplift" v={result.covU} ind/>
                  {result.eB > 0 && <LI lbl="Log Storage & SIEM" v={result.eB} ind/>}
                  <LI lbl="Add-on services" v={result.addonRevenue} ind/>
                  {result.itsoMRR > 0 && <LI lbl="ITSO Advisory" v={result.itsoMRR} ind/>}
                  {result.discount < 0 && <LI lbl="Contract discount" v={result.discount} ind/>}
                  <LI lbl="Final monthly recurring" v={effectiveFinalMRR} hi/>
                </SubCard>
              )}
              {products && products.length > 0 && (
                <SubCard eyebrow="Recommended services not selected" hint="breach-defense record on the proposal">
                  <RecommendedDeclinedEditor products={products} declinedItems={declinedItems} onChange={setDeclinedItems} selectedProductIds={inputs.selectedProducts || []} />
                </SubCard>
              )}
            </div>
          );
        case 'onboarding':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Onboarding</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>One-time fee, incentives, and any discounts against the upfront charges.</p></div>
              {result && (
                <SubCard eyebrow="Onboarding fee">
                  <LI lbl="Programming & configuration" v={result.onboarding} bold/>
                  {obIncentive?.effectiveFee != null && obIncentive.mode === 'discount' && (
                    <LI lbl={`Onboarding incentive (${obIncentive.discountPct}% off)`} v={-(result.onboarding - obIncentive.effectiveFee)} ind/>
                  )}
                  <LI lbl="Net onboarding fee" v={obIncentive?.effectiveFee ?? result.onboarding} hi/>
                  <div style={{ marginTop: 12 }}>
                    <OnboardingIncentive fee={result.onboarding} marketTier={selectedMkt?.tier_key} contractTerm={inputs.contractTerm} initialValue={obIncentive}
                      onChange={inc => {
                        const sig = inc ? `${inc.mode || 'none'}:${inc.effectiveFee ?? ''}:${inc.discountPct ?? ''}:${inc.splitMonths ?? ''}` : 'null';
                        const prevSig = obIncentive ? `${obIncentive.mode || 'none'}:${obIncentive.effectiveFee ?? ''}:${obIncentive.discountPct ?? ''}:${obIncentive.splitMonths ?? ''}` : 'null';
                        if (sig === prevSig) return;
                        setObIncentive(inc);
                      }} />
                  </div>
                </SubCard>
              )}
              <SubCard eyebrow="Discounts">
                <ManualDiscounts value={manualDiscounts} onChange={setManualDiscounts} baseMRR={result?.finalMRR || 0} />
                <div style={{ marginTop: 10 }}>
                  <OneTimeDiscounts value={oneTimeDiscounts} onChange={setOneTimeDiscounts} bases={oneTimeBases} />
                </div>
              </SubCard>
              <SubCard eyebrow="Agreement effective date">
                <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />
              </SubCard>
              {/* v3.9.1 fast-follow: Payment Methods (restored from classic) */}
              <SubCard eyebrow="Payment methods">
                {[
                  ['ACH / EFT', achFee === 0 ? 'Free' : `+$${achFee}`, achFee === 0 ? v3.color.status.greenText : v3.color.status.amberText],
                  ['Check', checkFee > 0 ? `+$${checkFee} admin fee` : 'Free', checkFee > 0 ? v3.color.status.amberText : v3.color.status.greenText],
                  ['Credit Card', `+${(ccSurcharge * 100).toFixed(0)}% surcharge`, v3.color.status.redText],
                ].map(([k, val, co]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: v3.font.size.label }}>
                    <span style={{ color: v3.color.bodySoft }}>{k}</span>
                    <span style={{ fontWeight: 700, color: co }}>{val}</span>
                  </div>
                ))}
                {result && ccSurcharge > 0 && (
                  <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, marginTop: 6, borderTop: `1px solid ${v3.color.borderSoft}`, paddingTop: 6 }}>
                    CC surcharge on this quote: ~{fmt$0(discountedFinalMRR * ccSurcharge)}/mo · ACH/EFT recommended
                  </div>
                )}
              </SubCard>
              {/* v3.9.1 fast-follow: Deal Summary (restored from classic) */}
              {result && (
                <SubCard eyebrow="Deal summary">
                  {[
                    ['Quote #', existingQuote?.quote_number || 'Unsaved'],
                    proposalName && ['Proposal', proposalName],
                    ['Business', recipientBiz || '—'],
                    recipientContact && ['Contact', recipientContact],
                    ['Package', selectedPkg?.name || '—'],
                    ['Market', selectedMkt?.name || '—'],
                    ['Contract', `${inputs.contractTerm} months`],
                    ['Monthly MRR', fmt$0(discountedFinalMRR)],
                    ['Onboarding', fmt$0(obIncentive?.effectiveFee ?? result.onboarding)],
                    ['Total Contract Value', fmt$0(discountedFinalMRR * inputs.contractTerm + (obIncentive?.effectiveFee ?? result.onboarding))],
                    hubDealId && ['HubSpot Deal', hubDealName || `#${hubDealId}`],
                  ].filter(Boolean).map(([k, val]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', fontSize: v3.font.size.label, borderBottom: `1px solid ${v3.color.borderSoft}` }}>
                      <span style={{ color: v3.color.muted }}>{k}</span>
                      <span style={{ fontWeight: 600, color: v3.color.body, fontFamily: (typeof val === 'string' && val.startsWith('$')) ? v3.font.mono : 'inherit', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{val}</span>
                    </div>
                  ))}
                </SubCard>
              )}
              {existingQuote?.id && (
                <SubCard eyebrow="Early termination fee">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ flex: 1, fontSize: v3.font.size.body, color: v3.color.muted }}>Compute an ETF and issue a signable letter for this client.</span>
                    <V3Button variant="secondary" size="sm" onClick={() => setShowEtfModal(true)}>💰 Calculate ETF →</V3Button>
                  </div>
                </SubCard>
              )}
            </div>
          );
        case 'terms':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Special terms</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Addendums attached to the agreement.</p></div>
              <SubCard eyebrow="Addendums">
                <div style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginBottom: 12 }}>{addendums.length} addendum{addendums.length === 1 ? '' : 's'} attached.</div>
                <V3Button variant="secondary" size="sm" onClick={() => setAddendumsModalOpen(true)}>Edit addendums</V3Button>
              </SubCard>
            </div>
          );
        case 'documents':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Documents</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Agreements and files sent for this quote. Send the Service Order from the proposal banner above; signed copies appear here.</p></div>
              <QuoteDocuments quoteId={existingQuote?.id} />
            </div>
          );
        case 'notes':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Notes</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Internal context and the note log for this quote.</p></div>
              <SubCard eyebrow="Deal notes">
                <Fld lbl="Deal description / notes"><TI value={dealDescription} onChange={setDealDescription} placeholder="Internal context — not shown to the client" /></Fld>
              </SubCard>
              <QuoteNotes quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} hubDealId={hubDealId} />
            </div>
          );
        case 'engagement':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>Engagement</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>How the client has interacted with the sent proposal and Service Order (live).</p></div>
              <EngagementPanel quoteId={existingQuote?.id} />
            </div>
          );
        case 'history':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
              <div><h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>History</h2>
                <p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6 }}>Saved versions of this quote.</p></div>
              <QuoteHistory quoteId={existingQuote?.id} />
            </div>
          );
        default:
          return null;
      }
    };

    const locked = isQuoteLocked(existingQuote);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {isReadOnly && (
          <div style={{ padding: '8px 24px', background: v3.color.status.amberBg, color: v3.color.status.amberText, fontSize: 12, fontWeight: 600 }}>
            🔒 View only — your account no longer has permission to edit Managed IT quotes.
          </div>
        )}
        {locked && (
          <LockedQuoteBanner notes={dealDescription} onNotesChange={setDealDescription} onSave={saveQuote} saving={saving} reason={QUOTE_LOCK_REASON} />
        )}
        <WorkspaceShell header={<>{v3Header}{v3ProposalBanner}</>} sections={v3Sections} activeId={activeSection} onSelect={setFocusedSection} rail={v3Rail}>
          {locked && activeSection !== 'documents'
            ? <div style={{ pointerEvents: 'none', opacity: 0.55 }} aria-disabled="true">{renderV3Focus()}</div>
            : renderV3Focus()}
        </WorkspaceShell>

        {/* Modals + toasts reused verbatim from the legacy layout (same state/handlers). */}
        {proposalModalOpen && activeProposal && (() => {
          const url = proposalPublicUrl(activeProposal.public_token);
          return (
            <div onClick={() => setProposalModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 30, 60, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 10, padding: 24, width: 'min(560px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: v3.color.navy }}>✓ Proposal Sent</h3>
                <div style={{ background: v3.color.cardAlt, border: `1px solid ${v3.color.border}`, borderRadius: 6, padding: '10px 12px', fontFamily: v3.font.mono, fontSize: 11, color: v3.color.ink, wordBreak: 'break-all', marginBottom: 14 }}>{url}</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <V3Button variant="secondary" size="sm" onClick={() => { navigator.clipboard?.writeText(url); }}>Copy URL</V3Button>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 12px', background: v3.color.navy, color: '#fff', borderRadius: v3.radius.input, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Open ↗</a>
                  <V3Button variant="dark" size="sm" onClick={() => setProposalModalOpen(false)}>Close</V3Button>
                </div>
              </div>
            </div>
          );
        })()}
        {proposalSendMsg && !proposalModalOpen && (
          <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '10px 14px', background: v3.color.status.redBg, border: `1px solid ${v3.color.status.redText}33`, borderRadius: 6, fontSize: 12, color: v3.color.status.redText, fontWeight: 600, zIndex: 150, maxWidth: 320 }}>{proposalSendMsg}</div>
        )}
        {showAssumptions && (
          <AssumptionsModal onClose={() => setShowAssumptions(false)} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} recipientContact={recipientContact} inputs={inputs} pkg={selectedPkg} products={products} settings={settings} complianceKey={complianceKey} onSave={(a) => { setAssumptionsStory(a.story || ''); setAssumptionsSavedAt(a.savedAt || null); }} />
        )}
        <AddendumsModal open={addendumsModalOpen} onClose={() => setAddendumsModalOpen(false)} addendums={addendums} templates={addendumTemplates} onSave={setAddendums} />
        <ServiceOrderPreviewModal open={soPreviewModalOpen} onClose={() => setSoPreviewModalOpen(false)} quoteId={existingQuote?.id} docType="managed_it_quote" clientBiz={recipientBiz} />
        {v3UnlockModal}
        {v3LockRatesModal}
        {showEtfModal && (
          <ETFCalculatorModal onClose={() => setShowEtfModal(false)} defaultMRR={result?.finalMRR || ''} defaultTerm={inputs?.contractTerm || 36} defaultClientName={recipientBiz} defaultClientAddress={recipientAddress} defaultClientContact={recipientContact} defaultClientEmail={recipientEmail} defaultContractReference={existingQuote?.quote_number || ''} quoteId={existingQuote?.id} hubDealId={hubDealId} />
        )}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>

      {/* ── LEFT PANEL ── */}
      <div style={{ width:345, flexShrink:0, background:'white', borderRight:'1px solid #e5e7eb', overflowY:'auto', padding:'12px 14px' }}>

        {/* v3.5.67: read-only banner — visible when the user no longer has
            Managed IT permission but is viewing an existing quote. They can
            view the quote but Save/Status changes will be refused. */}
        {isReadOnly && (
          <div style={{ marginBottom:8, padding:'8px 10px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, fontSize:11, color:'#92400e', fontWeight:600 }}>
            🔒 View only — your account no longer has permission to edit Managed IT quotes. Contact an admin to restore access.
          </div>
        )}

        {/* Quote number badge */}
        {existingQuote && (
          <div style={{ marginBottom:8, padding:'5px 8px', background:'#f0f7ff', borderRadius:5, fontSize:11, color:'#1e40af', fontWeight:600, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>{existingQuote.quote_number}</span>
            <span style={{ fontSize:10, padding:'1px 6px', borderRadius:3, background: quoteStatus==='won'?'#dcfce7':quoteStatus==='sent'?'#dbeafe':quoteStatus==='lost'?'#fee2e2':'#f3f4f6', color: quoteStatus==='won'?'#166534':quoteStatus==='sent'?'#1e40af':quoteStatus==='lost'?'#991b1b':'#6b7280', fontWeight:600 }}>{quoteStatus}</span>
          </div>
        )}

        {/* ── HubSpot connection ── */}
        <HubSpotConnect
          dealId={hubDealId}
          dealUrl={hubDealUrl}
          dealName={hubDealName}
          description={dealDescription}
          onDescriptionChange={setDealDescription}
          quoteNumber={existingQuote?.quote_number}
          mrr={effectiveFinalMRR || result?.finalMRR}
          contractValue={result ? effectiveFinalMRR * inputs.contractTerm + result.onboarding : 0}
          packageName={selectedPkg?.name}
          contractTerm={inputs.contractTerm}
          existingQuoteId={existingQuote?.id}
          clientName={recipientBiz}
          onConnect={full => {
            setHubDealId(full.dealId);
            setHubDealUrl(full.dealUrl);
            setHubDealName(full.deal.dealname);
            if (full.company) {
              if (full.company.name) setRecipientBiz(full.company.name);
              const addr = [full.company.address, full.company.address2, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
              if (addr) setRecipientAddress(addr);
              if (full.company.zip) { setClientZip(full.company.zip); const zr = lookupZip(full.company.zip); setZipResult(zr); if (zr) applyZip(zr); }
            } else {
              const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
              if (extracted) setRecipientBiz(extracted);
            }
            if (full.contact) {
              const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
              if (name) setRecipientContact(name);
              if (full.contact.email) setRecipientEmail(full.contact.email);
            }
            if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Managed IT Services — ${full.company?.name || full.deal.dealname}`);
          }}
          onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
          onSync={syncToDeal}
        />
        <SPTConnect
          proposalId={sptProposalId}
          quoteId={existingQuote?.id}
          clientName={recipientBiz}
          quoteNumber={existingQuote?.quote_number}
          settings={settings}
          onConnect={(pid) => setSptProposalId(pid)}
          onDisconnect={() => setSptProposalId(null)}
        />

        {/* ── Client / Proposal fields ── */}
        <Sec t="Proposal Details" c="#0f1e3c">
          {/* Rep selector */}
          <Fld lbl="Assigned Sales Rep">
            <RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} padding="4px 7px" fontSize={10} includeTextColor={false} />
          </Fld>
          <Fld lbl="Proposal Name">
            <TI value={proposalName} onChange={setProposalName} placeholder="e.g. FerrumIT Managed IT — Acme Corp" />
          </Fld>
          <Fld lbl="Recipient Business Name">
            <TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp" />
          </Fld>
          <Grid2>
            <Fld lbl="Contact Name">
              <TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith" />
            </Fld>
            <Fld lbl="Contact Email">
              <TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com" />
            </Fld>
          </Grid2>
          <Fld lbl="Business Address">
            <TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601" />
          </Fld>
          {/* v3.5.99.7.3: phone/mobile auto-pulled from HubSpot contact (full.contact.phone, full.contact.mobilephone). */}
          <Grid2>
            <Fld lbl="Office Phone">
              <TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567" />
            </Fld>
            <Fld lbl="Mobile Phone">
              <TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321" />
            </Fld>
          </Grid2>
          {/* v3.5.99.7.6: manual full-overwrite push to HubSpot */}
          {(hubContactId || hubCompanyId) && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={pushAllToHubspot} disabled={hubPushing}
                style={{ padding: '4px 10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: hubPushing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                {hubPushing ? '⏳ Pushing...' : '↑ Push to HubSpot'}
              </button>
              <span style={{ fontSize: 9, color: '#0c4a6e' }}>Overwrites HubSpot contact + company with values above</span>
              {hubPushMsg && <span style={{ fontSize: 10, color: '#075985', flex: 1, textAlign: 'right', minWidth: 0 }}>{hubPushMsg}</span>}
            </div>
          )}
          {/* v3.5.99.0: Internal Notification CCs — comma-separated email list
              CC'd on the proposal-approved email when the client signs off.
              Per-quote override; leave blank to use Admin → Notifications
              global default. NOT visible to the customer. */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#92400e', marginBottom: 4 }}>🔒 Internal Notifications</div>
            <Fld lbl="CC on Approval (comma-separated)">
              <TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com" />
            </Fld>
            <div style={{ fontSize: 9, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>Leave blank to use the global default from Admin → Notifications. Not visible to the customer.</div>
          </div>
        </Sec>

        {/* ── Zip / Location ── */}
        <div style={{ background:'#f0f7ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'8px 10px', marginBottom:10 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'#1e40af', marginBottom:5 }}>📍 Client Location</div>
          <div style={{ display:'flex', gap:5, alignItems:'center' }}>
            <input value={clientZip} onChange={e => handleZipChange(e.target.value)} placeholder="Zip code — auto-filled from address"
              style={{ flex:1, padding:'5px 8px', border:'1px solid #93c5fd', borderRadius:4, fontSize:12, outline:'none', fontFamily:'DM Mono, monospace', fontWeight:600 }} />
            {zipResult && !zipApplied && (
              <button onClick={() => applyZip()} style={{ padding:'5px 8px', background:tierColor(zipResult.tier), color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:700, whiteSpace:'nowrap', cursor:'pointer' }}>Apply →</button>
            )}
          </div>
          {zipResult && (
            <div style={{ marginTop:5, padding:'4px 7px', borderRadius:4, background:tierBg(zipResult.tier), fontSize:10 }}>
              <span style={{ fontWeight:700, color:tierColor(zipResult.tier) }}>{zipResult.name||`ZIP ${zipResult.zip}`} → {marketTiers.find(t=>t.tier_key===zipResult.tier)?.name}</span>
              {zipApplied && <span style={{ marginLeft:6, color:'#166534', fontWeight:700 }}>✓ Applied</span>}
            </div>
          )}
        </div>

        {/* Market tier */}
        <Sec t="Market Tier" c="#0f1e3c">

          {/* AI recommendation banner */}
          {showMktRecommend && pendingMultiplier && (
            <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:5, padding:'8px 10px', marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#1e40af', marginBottom:3 }}>
                📊 AI Market Recommendation
              </div>
              <div style={{ fontSize:10, color:'#1e40af', marginBottom:6 }}>
                <strong>{pendingMultiplier.city}, {pendingMultiplier.state}</strong> — {pendingMultiplier.tier?.charAt(0).toUpperCase() + pendingMultiplier.tier?.slice(1)} market
                · <strong>{pendingMultiplier.multiplier === 1 ? 'No adjustment' : pendingMultiplier.multiplier > 1 ? `+${Math.round((pendingMultiplier.multiplier - 1) * 100)}% pricing` : `-${Math.round((1 - pendingMultiplier.multiplier) * 100)}% pricing`}</strong>
                {' '}<span style={{ fontSize:9, color:'#6b7280' }}>(multiplier: {pendingMultiplier.multiplier}×)</span>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => {
                  setAiMultiplier(pendingMultiplier.multiplier);
                  setAiMultiplierTier(pendingMultiplier.tier);
                  setShowMktRecommend(false);
                }} style={{ padding:'4px 12px', background:'#1e40af', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:700, cursor:'pointer' }}>
                  Apply AI Rate
                </button>
                <button onClick={() => setShowMktRecommend(false)}
                  style={{ padding:'4px 8px', background:'white', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, color:'#6b7280', cursor:'pointer' }}>
                  Keep Manual
                </button>
              </div>
            </div>
          )}

          {/* Market status badge (v3.5.37) — replaces the previous "AI rate active"
              indicator. Shows reps which system is currently driving market pricing
              (Market Intelligence vs static tier baseline) so the dropdown's role
              is unambiguous. */}
          <div style={{ marginBottom: 8 }}>
            <MarketStatusBadge
              aiMultiplier={aiMultiplier}
              aiMultiplierTier={aiMultiplierTier}
              selectedMkt={selectedMkt}
              marketCity={marketCity}
              marketState={marketState}
              locked={!!pricingSnapshot}
              onRunAnalysis={aiMultiplier != null ? (() => {
                // Refresh path — clear current MI and let the rep re-run via MarketRateCard
                setAiMultiplier(null);
                setAiMultiplierTier(null);
              }) : undefined}
            />
          </div>

          {marketTiers.map(t => (
            <div key={t.id} onClick={() => { setSelectedMkt(t); setAiMultiplier(null); setAiMultiplierTier(null); }} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedMkt?.id===t.id?'2':'1'}px solid ${selectedMkt?.id===t.id?tierColor(t.tier_key):'#e5e7eb'}`, background:selectedMkt?.id===t.id?tierBg(t.tier_key):'white', display:'flex', justifyContent:'space-between', alignItems:'center', opacity: aiMultiplier != null ? 0.55 : 1 }}>
              <span style={{ fontSize:10, fontWeight:700, color:tierColor(t.tier_key) }}>{staticTierToMILabel(t.tier_key)} <span style={{ fontWeight:400, color:'#9ca3af', fontSize:9 }}>({t.name})</span></span>
              <span style={{ fontSize:9, color:'#6b7280', fontFamily:'DM Mono, monospace' }}>classification</span>
            </div>
          ))}
        </Sec>

        {/* Package */}
        <Sec t="Managed IT Package" c="#2563eb">
          {packages.map(p => (
            <div key={p.id} onClick={() => setSelectedPkg(p)} style={{ padding:'6px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedPkg?.id===p.id?'2':'1'}px solid ${selectedPkg?.id===p.id?'#2563eb':'#e5e7eb'}`, background:selectedPkg?.id===p.id?'#eff6ff':'white' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:10, fontWeight:700, color:selectedPkg?.id===p.id?'#1e40af':'#374151' }}>{p.name}</span>
                <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280', background:'#f3f4f6', padding:'1px 4px', borderRadius:3 }}>${p.ws_rate}/WS · ${p.user_rate}/US</span>
              </div>
              <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>{p.ideal_desc}</div>
            </div>
          ))}
          {result?.recommended && result.recommended!==selectedPkg?.name && (
            <div style={{ padding:'4px 7px', background:'#fefce8', border:'1px solid #fde68a', borderRadius:3, fontSize:9, color:'#92400e', marginTop:4 }}>
              ⚡ Recommended: <strong>{result.recommended}</strong>
            </div>
          )}
          {/* v3.5.86.2.1: contract term opts derived from pricing_settings
              via getContractTermOptions(settings). Calc engine has read the
              same settings keys since v3.5.x; this brings the dropdown LABEL
              into agreement with the math so admin changes to
              contract_disc_12/24/36 surface in the picker immediately. */}
          <Fld lbl="Contract Term" s={{ marginTop:8 }}><SI v={inputs.contractTerm} s={v=>set('contractTerm',+v)} opts={getContractTermOptions(settings)}/></Fld>
          <Fld lbl="Quote Status" s={{ marginTop:4 }}><SI v={quoteStatus} s={setQuoteStatus} opts={[['draft','Draft'],['in_review','In Review'],['approved','Approved'],['sent','Sent'],['won','Won'],['lost','Lost'],['expired','Expired']]}/></Fld>
        </Sec>

        {/* People & Devices */}
        <Sec t="People & Devices" c="#7c3aed">
          <Grid2>
            <Fld lbl="Users" sub="humans"><NI v={inputs.users} s={v=>set('users',v)}/></Fld>
            <Fld lbl="Shared Mailboxes" sub="billing@ etc"><NI v={inputs.sharedMailboxes} s={v=>set('sharedMailboxes',v)}/></Fld>
            <Fld lbl="Workstations" sub="managed"><NI v={inputs.workstations} s={v=>set('workstations',v)}/></Fld>
            <Fld lbl="Total Devices" sub="density/SIEM"><NI v={inputs.endpoints} s={v=>set('endpoints',v)}/></Fld>
            <Fld lbl="Mobile Devices" sub="phones + tablets"><NI v={inputs.mobileDevices||0} s={v=>set('mobileDevices',v)}/></Fld>
          </Grid2>
        </Sec>

        {/* Infrastructure */}
        <Sec t="Infrastructure" c="#0891b2">
          <Grid2>
            <Fld lbl="Servers"><NI v={inputs.servers} s={v=>set('servers',v)}/></Fld>
            <Fld lbl="Locations" sub="all billed"><NI v={inputs.locations} s={v=>set('locations',v)}/></Fld>
            <Fld lbl="Cloud Tenants" sub="M365/GW/Azure"><NI v={inputs.cloudTenants} s={v=>set('cloudTenants',v)}/></Fld>
            {/* v3.5.96.0: Domains slot — after Cloud Tenants per locked UI position. Tracked
                as an infrastructure item; drives per-domain product/cost-component qty
                resolution and per-domain onboarding fee (default $99, admin-editable). */}
            {/* v3.5.99.7.11: DomainsInfo bubble — clarifies which domains
                we bill for (fully managed) vs which we don't (parked,
                forwarded-only). Same modal pattern as ThreatProfileInfo. */}
            <Fld lbl="Domains" sub="DNS/SSL/etc.">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ flex: 1 }}><NI v={inputs.domains} s={v=>set('domains',v)}/></div>
                <DomainsInfo />
              </div>
            </Fld>
            <Fld lbl="Vendors/Carriers"><NI v={inputs.vendors} s={v=>set('vendors',v)}/></Fld>
          </Grid2>
        </Sec>

        {/* Risk */}
        <Sec t="Risk & Compliance" c="#dc2626">
          <Grid2>
            {/* v3.5.59: 'Industry Risk' renamed to 'Threat Profile'. Value labels
                Low/Medium/High → Standard/Elevated/Critical (data keys preserved
                so settings — itso_risk_uplift_* and itso_label_* — keep working). */}
            <Fld lbl="Threat Profile" sub="oversight tier">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ flex: 1 }}><SI v={inputs.industryRisk} s={v=>set('industryRisk',v)} opts={[['none','None'],['low','Standard'],['medium','Elevated'],['high','Critical']]}/></div>
                <ThreatProfileInfo />
              </div>
            </Fld>
            {/* v3.5.59: Compliance expanded from 3 → 5 values. Each framework
                drives recommendation of its matching Compliance Program product
                (HIPAA / SOC 2 / PCI DSS / CMMC). The compliance_mult_* settings
                are seeded at 1.0 so revenue comes from the product line item
                rather than a hidden multiplier. */}
            <Fld lbl="Compliance" sub="adds program product"><SI v={inputs.compliance} s={v=>set('compliance',v)} opts={[['none','None'],['hipaa','HIPAA'],['soc2','SOC 2'],['pci','PCI DSS'],['cmmc','CMMC']]}/></Fld>
            <Fld lbl="Complexity"><SI v={inputs.complexity} s={v=>set('complexity',v)} opts={[['low','Low'],['medium','Medium'],['high','High']]}/></Fld>
            <Fld lbl="Coverage"><SI v={inputs.requestedCoverage} s={v=>set('requestedCoverage',v)} opts={[['business_hours','8×5'],['24x5','24×5'],['24x7','24×7']]}/></Fld>
          </Grid2>
        </Sec>

        {/* Package Includes (v3.5.31) — bundled products shown above add-ons */}
        {selectedPkg && (packageIncludes.length > 0 || includesLoading) && (
          <PackageIncludes
            packageName={selectedPkg.name}
            includes={packageIncludes}
            excludedIds={excludedIncludeIds}
            loading={includesLoading}
            includedItemsCost={result?.includedItemsCost}
            includeSwaps={includeSwaps}
            availableProducts={products}
            onToggleExclude={(includeId) => {
              // When re-checking an excluded include, also clear any swap mapping
              // — the rep is restoring the original, not keeping the substitute alongside.
              setExcludedIncludeIds(prev => {
                const wasExcluded = prev.includes(includeId);
                if (wasExcluded) {
                  // Re-checking: clear swap if any
                  setIncludeSwaps(swaps => {
                    if (!(includeId in swaps)) return swaps;
                    const next = { ...swaps };
                    delete next[includeId];
                    return next;
                  });
                  return prev.filter(id => id !== includeId);
                }
                return [...prev, includeId];
              });
            }}
            onSwap={(includeId, substituteProductId) => {
              // Swap = exclude original + map to substitute. Maintain invariant.
              setExcludedIncludeIds(prev => prev.includes(includeId) ? prev : [...prev, includeId]);
              setIncludeSwaps(prev => ({ ...prev, [includeId]: substituteProductId }));
            }}
            onUnswap={(includeId) => {
              // Removing substitute leaves the include excluded (rep must explicitly re-check
              // to restore it). This matches the "exclude → swap → unswap → still excluded"
              // mental model the rep started with.
              setIncludeSwaps(prev => {
                if (!(includeId in prev)) return prev;
                const next = { ...prev };
                delete next[includeId];
                return next;
              });
            }}
          />
        )}

        {/* Add-on Products from DB — included + swap-substitute products are filtered
            out so reps can't double-add. (v3.5.31 / v3.5.32 / v3.5.33) */}
        {Object.entries(productsByCategory).map(([cat, catProducts]) => {
          // Hide products that are bundled into the selected package AND not excluded by rep
          const excludedSet = new Set(excludedIncludeIds || []);
          const activeIncludeProductIds = new Set(
            packageIncludes
              .filter(i => !excludedSet.has(i.id))
              .map(i => i.product_id)
          );
          // Also hide products that are currently chosen as swap substitutes
          // (they appear in the swapped row inside the includes section)
          const swapSubIds = new Set(Object.values(includeSwaps || {}).filter(Boolean));
          const visibleProducts = catProducts.filter(p => !activeIncludeProductIds.has(p.id) && !swapSubIds.has(p.id) && !p.internal_only);
          if (visibleProducts.length === 0) return null;
          return (
          <Sec key={cat} t={cat} c={catColor(cat)}>
            {/* v3.5.58: Executive Reporting Required toggle removed — replaced
                by the new Strategic Advisory derived card rendered below the
                product loop. The flat-rate vCIO/ITSO products were retired in
                the v3.5.58 SQL migration; the Strategic Advisory category
                will now show as empty and skip rendering (visibleProducts === 0). */}
            {visibleProducts.map(p => {
              const gm = p.sell_price > 0 ? (1-p.cost_price/p.sell_price) : 0;
              const isExclusive = !!p.exclusive_group;
              const sel = isSelected(p.id);
              const isRecommended = recommendedProducts.some(r => r.id === p.id);
              const compColor = isRecommended && !sel ? '#92400e' : null;
              return (
                <div key={p.id} onClick={() => toggleProduct(p.id)}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2,
                    border:`1px solid ${sel?'#93c5fd': isRecommended ? '#fde68a' :'#e5e7eb'}`,
                    background:sel?'#eff6ff': isRecommended ? '#fffbeb' :'white' }}>
                  {isExclusive
                    ? <div style={{ width:13, height:13, borderRadius:'50%', border:`2px solid ${sel?'#2563eb':'#d1d5db'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {sel && <div style={{ width:5, height:5, borderRadius:'50%', background:'#2563eb' }}/>}
                      </div>
                    : <div style={{ width:22, height:13, borderRadius:7, background:sel?'#2563eb':'#d1d5db', position:'relative', flexShrink:0 }}>
                        <div style={{ position:'absolute', top:2, left:sel?11:2, width:9, height:9, borderRadius:'50%', background:'white', transition:'left .1s' }}/>
                      </div>
                  }
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                      <div style={{ fontSize:10, fontWeight:600, color:sel?'#1e40af':'#374151' }}>{p.name}</div>
                      {isRecommended && !sel && (
                        <span style={{ fontSize:7, fontWeight:700, padding:'1px 4px', borderRadius:2, background:'#fde68a', color:'#92400e' }}>
                          {/* v3.5.59: badge shows specific framework now that compliance is 5-value */}
                          ⚠ {({ hipaa:'HIPAA', soc2:'SOC 2', pci:'PCI DSS', cmmc:'CMMC', moderate:'HIPAA/SOC2', high:'PCI/CMMC' }[inputs.compliance] || 'Compliance')}
                        </span>
                      )}
                      {isRecommended && sel && (
                        <span style={{ fontSize:7, fontWeight:700, padding:'1px 4px', borderRadius:2, background:'#dcfce7', color:'#166534' }}>
                          ✓ Compliance
                        </span>
                      )}
                    </div>
                    {p.description && <div style={{ fontSize:8, color:'#9ca3af' }}>{p.description}</div>}
                    {isRecommended && !sel && p.recommendation_reason && (
                      <div style={{ fontSize:8, color:'#92400e', fontStyle:'italic', marginTop:1 }}>{p.recommendation_reason.substring(0,80)}{p.recommendation_reason.length>80?'…':''}</div>
                    )}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, display:'flex', alignItems:'center', gap:6 }}>
                    {sel && p.qty_driver === 'manual' && (() => {
                      const qty = parseInt(inputs.manualQuantities?.[p.id] || 0);
                      return (
                        <div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:1 }}>
                          <input type="number" min="0" value={qty || ''} placeholder="qty"
                            onChange={e=>{
                              const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
                              setInputs(prev => ({ ...prev, manualQuantities: { ...(prev.manualQuantities || {}), [p.id]: next } }));
                            }}
                            style={{ width:54, padding:'2px 5px', border:`1px solid ${qty > 0 ? '#93c5fd' : '#fbbf24'}`, borderRadius:3, fontSize:10, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none', background: qty > 0 ? 'white' : '#fffbeb' }}/>
                          {qty === 0 && <span style={{ fontSize:8, color:'#92400e', fontWeight:600 }}>set qty</span>}
                        </div>
                      );
                    })()}
                    <div>
                      <div style={{ fontSize:10, fontFamily:'DM Mono, monospace', fontWeight:600, color:'#374151' }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'license' : p.qty_driver}</div>
                      <div style={{ fontSize:8, color:'#9ca3af' }}>{(gm*100).toFixed(0)}% GM</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Sec>
          );
        })}

        {/* ── Strategic Advisory derived card (v3.5.58) ─────────────────────
            Replaces the prior Strategic Advisory product list (flat-rate vCIO
            radio buttons) and the Executive Reporting Required toggle. The
            ITSO Advisory line is now derived from the Industry Risk dropdown
            in the Risk & Compliance section above — no manual product
            selection. Card hides itself when uplift rate is 0 (Industry Risk
            = None), so reps only see it when there's something to show.
        */}
        {result?.itsoMRR > 0 && result?.itsoLabel && (
          <Sec t="Strategic Advisory" c="#7c3aed">
            <div style={{
              padding: '10px 12px',
              background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)',
              border: '1px solid #c4b5fd',
              borderRadius: 6,
              marginTop: 4,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', marginBottom: 3 }}>
                    {result.itsoLabel}
                  </div>
                  <div style={{ fontSize: 9, color: '#6d28d9', lineHeight: 1.5, marginBottom: 5 }}>
                    Auto-derived from Threat Profile = <strong>{({ none:'None', low:'Standard', medium:'Elevated', high:'Critical' }[inputs.industryRisk || 'none'])}</strong>.
                    {' '}Senior advisory time scaled to your client's risk profile (vCIO, executive
                    reporting, governance, security strategy review). Adjust by changing Threat Profile.
                  </div>
                  <div style={{ fontSize: 9, color: '#7c3aed', fontFamily: 'DM Mono, monospace', lineHeight: 1.5 }}>
                    {((result.itsoUpliftRate || 0) * 100).toFixed(0)}% × {fmt$(result.itsoBaseMRR)} labor base
                    {result.itsoHours > 0 && (
                      <> · ≈ {result.itsoHours.toFixed(1)} senior hrs/mo of advisory time</>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'DM Mono, monospace', color: '#5b21b6' }}>
                    {fmt$(result.itsoMRR)}
                  </div>
                  <div style={{ fontSize: 8, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>
                    /month
                  </div>
                </div>
              </div>
            </div>
          </Sec>
        )}

        {/* v3.5.81: Recommended-but-Declined section. Rep flags any service
            they recommended that the client elected not to include. These
            render as a clearly marked "Not Selected" section on the public
            proposal page — the breach-defense paper trail. Only visible
            when there's a product catalog to pick from (otherwise editor
            is empty + non-functional). */}
        {products && products.length > 0 && (
          <Sec t="Recommended Services Not Selected" c="#92400e">
            <RecommendedDeclinedEditor
              products={products}
              declinedItems={declinedItems}
              onChange={setDeclinedItems}
              selectedProductIds={inputs.selectedProducts || []}
            />
          </Sec>
        )}



        {/* Save / Export */}
        <QuoteSaveBar
          onSave={saveQuote}
          existingQuote={existingQuote}
          quoteStatus={quoteStatus}
          setQuoteStatus={setQuoteStatus}
          quoteType="quotes"
          saving={saving}
          saveMsg={saveMsg}
          saveButtonPadding="6px"
          wrapperStyle={{ padding: 10 }}
          reviewQuote={existingQuote ? { ...existingQuote, status: quoteStatus, inputs: { ...inputs, proposalName, recipientContact, recipientEmail } } : null}
          leadingButtons={result && (
            <>
              {/* v3.5.81.6: Customer Story & Assumptions entry point. Sits
                  immediately before Send Proposal so the rep workflow reads
                  left-to-right: capture the story → send the proposal. The
                  status indicator (✓ green or ⚠ amber) tells the rep at a
                  glance whether the gate will pass when they click Send. */}
              <button
                onClick={() => setShowAssumptions(true)}
                disabled={!existingQuote?.id}
                title={!existingQuote?.id ? 'Save the quote first' : (assumptionsComplete ? 'Customer Story captured — click to review or edit' : 'Customer Story is required before sending a proposal')}
                style={{
                  padding:'6px 10px',
                  background: assumptionsComplete ? '#f0fdf4' : '#fffbeb',
                  border: `1px solid ${assumptionsComplete ? '#bbf7d0' : '#fde68a'}`,
                  borderRadius:4, fontSize:10, fontWeight:600,
                  color: assumptionsComplete ? '#166534' : '#92400e',
                  cursor: !existingQuote?.id ? 'not-allowed' : 'pointer',
                  opacity: !existingQuote?.id ? 0.55 : 1,
                }}>
                {assumptionsComplete ? '✓' : '⚠'} Customer Story
              </button>
              {/* v3.5.81: Send Proposal — creates a public token-gated proposal
                  page that the rep distributes to the prospect. Disabled until
                  the quote has been saved (we need the FK).
                  v3.5.81.6: also disabled when assumptionsComplete is false —
                  the customer narrative is the legal record we point at for
                  breach defense. The button still works on click (it opens
                  the modal); the disable here is purely visual feedback. */}
              <button
                onClick={() => {
                  // v3.5.83.2: Lock-Rates-First UI guard. If OOS rates aren't
                  // locked yet, open the confirmation modal instead of firing
                  // the send. The modal lets the rep choose to lock now,
                  // override and send anyway, or cancel.
                  if (!ratesLocked) {
                    setShowLockRatesModal('send_proposal');
                    return;
                  }
                  sendProposal();
                }}
                disabled={proposalSending || !existingQuote?.id || !assumptionsComplete}
                title={
                  !existingQuote?.id ? 'Save the quote first' :
                  !assumptionsComplete ? 'Customer Story is required before sending — click ⚠ Customer Story above' :
                  !ratesLocked ? 'OOS rates aren\u2019t locked yet — clicking will prompt to lock first' :
                  ''
                }
                style={{
                  padding:'6px 10px',
                  background: proposalSending ? '#fef3c7' : (!ratesLocked && existingQuote?.id ? '#fef3c7' : '#fff5f6'),
                  border: !ratesLocked && existingQuote?.id ? '1px solid #f59e0b' : '1px solid #fbcfd5',
                  borderRadius:4, fontSize:10, color:'#9b1c2a', fontWeight:600,
                  cursor: (proposalSending || !existingQuote?.id || !assumptionsComplete) ? 'not-allowed' : 'pointer',
                  opacity: (proposalSending || !existingQuote?.id || !assumptionsComplete) ? 0.55 : 1,
                }}>
                {!ratesLocked && existingQuote?.id ? '\u26A0 ' : ''}
                {proposalSending ? 'Sending\u2026' : (activeProposal && activeProposal.status !== 'superseded' ? 'Resend Proposal' : 'Send Proposal')}
              </button>
              <button onClick={exportSPT}
                style={{ padding:'6px 8px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:4, fontSize:10, color:'#166534', fontWeight:600, cursor:'pointer' }}>
                Export JSON
              </button>
            </>
          )}
          extraButtons={
            <button
              onClick={() => navigate('/bundle/new', { state: { fromQuote: {
                type: 'it',
                clientName: recipientBiz, clientZip,
                marketTier: selectedMkt?.tier_key,
                packageName: selectedPkg?.name,
                proposalName, recipientContact, recipientEmail, recipientAddress, notificationCc, addendums, flexHours: flexHours || null,
                notes: dealDescription,
                hubDealId, hubDealUrl, hubDealName,
                inputs: { ...inputs },
              }}})}
              style={{ padding:'6px 10px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:4, fontSize:11, color:'#166534', fontWeight:600, cursor:'pointer' }}>
              📦 Bundle with Voice
            </button>
          }
        />
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc', minWidth:0 }}>
        <ReviewBanner
          quote={{ ...existingQuote, status: quoteStatus, hubspot_deal_id: hubDealId }}
          quoteType="quotes"
          onStatusChange={s => setQuoteStatus(s)}
        />

        {/* v3.5.81: Proposal status banner. Visible whenever an active
            proposal exists for this quote. State-aware:
              sent_for_review → blue, "waiting for client"
              viewed          → amber, with first/last view timestamps
              approved        → green, with "Send Service Order" CTA
              declined        → red
              expired         → grey, prompting resend */}
        {activeProposal && activeProposal.status !== 'superseded' && (() => {
          const s = activeProposal.status;
          const palette = s === 'approved'  ? { bg: '#15803d', sub: 'rgba(255,255,255,0.85)', accent: '#bbf7d0' }
                       : s === 'declined'   ? { bg: '#991b1b', sub: 'rgba(255,255,255,0.85)', accent: '#fecaca' }
                       : s === 'expired'    ? { bg: '#6b7280', sub: 'rgba(255,255,255,0.85)', accent: '#e5e7eb' }
                       : s === 'viewed'     ? { bg: '#92400e', sub: 'rgba(255,255,255,0.85)', accent: '#fde68a' }
                       :                      { bg: '#1e40af', sub: 'rgba(255,255,255,0.85)', accent: '#bfdbfe' };
          const url = proposalPublicUrl(activeProposal.public_token);
          return (
            <div style={{ padding: '8px 14px', background: palette.bg, color: 'white', borderBottom: '1px solid rgba(0,0,0,0.1)', fontSize: 11 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14 }}>
                    {s === 'approved' ? '✓' : s === 'declined' ? '✗' : s === 'expired' ? '⏰' : s === 'viewed' ? '👁' : '📨'}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, letterSpacing: '0.02em' }}>
                      {s === 'approved'        ? `Approved by ${activeProposal.approver_name || 'client'}` :
                       s === 'declined'        ? 'Proposal declined' :
                       s === 'expired'         ? 'Proposal expired — resend to refresh' :
                       s === 'viewed'          ? `Client viewed ${activeProposal.view_count || 1}× — last ${new Date(activeProposal.last_viewed_at).toLocaleDateString()}` :
                                                 'Proposal sent — awaiting client review'}
                    </div>
                    <div style={{ fontSize: 10, color: palette.sub, marginTop: 2 }}>
                      Sent {new Date(activeProposal.sent_at).toLocaleDateString()}
                      {activeProposal.expires_at && s !== 'approved' && s !== 'declined' && ` · expires ${new Date(activeProposal.expires_at).toLocaleDateString()}`}
                      {s === 'approved' && activeProposal.approved_at && ` · ${new Date(activeProposal.approved_at).toLocaleString()}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={() => navigator.clipboard?.writeText(url).then(() => setProposalSendMsg('URL copied')).catch(()=>{})}
                    style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 3, cursor: 'pointer' }}>
                    Copy URL
                  </button>
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: 'white', color: palette.bg, border: '1px solid white', borderRadius: 3, textDecoration: 'none' }}>
                    ↗ Open
                  </a>
                  {s !== 'declined' && s !== 'expired' && (() => {
                    // v3.5.99.7.26: the Send Service Order CTA now appears for
                    // ANY active proposal state (sent_for_review / viewed /
                    // approved) — not only after the client clicks Approve.
                    // Clients found the separate "approve the proposal" step
                    // confusing; the rep can now send the Service Order for
                    // signature as soon as the client has seen the proposal and
                    // verbally agreed to proceed. The client then signs the
                    // actual Service Order (the binding document) rather than a
                    // confusing intermediate acceptance. Terminal states
                    // (declined / expired) still hide the CTA. Voice / Bundle /
                    // FlexIT already allow this via their quoteStatus-gated
                    // signature cards; this brings Managed IT in line.
                    //
                    // v3.5.81.1: derive service-order state from the
                    // existingQuote.inputs.signwellDocuments[] array. The
                    // auto-fire path (proposal.js) pushes a new entry with
                    // type='managed_it_quote' and triggered_by='proposal_approval'.
                    // Match on proposal_id to be safe against multi-resend
                    // history (older entries from prior proposals on the
                    // same quote could otherwise win).
                    //
                    // v3.5.82.4.1.2: triggered_by clause expanded to include
                    // 'manual_send' (records pushed by the new sendServiceOrder
                    // endpoint introduced in v3.5.82.4.1.1). Without this, the
                    // manual-send path would correctly persist the SignWell
                    // document but the UI would never transition out of the
                    // yellow "Sent ✓ refreshing..." state because the find()
                    // looked only for 'proposal_approval'-flagged records.
                    // Both triggers represent the same downstream state (a
                    // SignWell doc exists and is awaiting signature), so the
                    // indicator should treat them identically.
                    const swDocs = (existingQuote?.inputs?.signwellDocuments) || [];
                    // v3.5.99.7.27: take the LATEST matching doc (entries are
                    // appended on each send) so a resend's fresh document drives
                    // the indicator rather than the original stale one.
                    const swDocMatches = swDocs.filter(d =>
                      d.type === 'managed_it_quote' &&
                      (d.triggered_by === 'proposal_approval' || d.triggered_by === 'manual_send') &&
                      d.proposal_id === activeProposal.id
                    );
                    const swDoc = swDocMatches.length ? swDocMatches[swDocMatches.length - 1] : null;
                    if (!swDoc) {
                      // Auto-fire either disabled (proposal_autofire_enabled
                      // is 'false' in pricing_settings — the default) or
                      // failed (e.g. v3.5.82.4.1 rates_not_locked precondition
                      // rejected). v3.5.82.4.1.1: replaced the inert <span>
                      // with a real <button> wired to handleSendServiceOrder.
                      // The button POSTs to /sendServiceOrder which mirrors
                      // server-side maybeAutoFireServiceOrder. Error states
                      // surface the upstream `error` + `detail` so the rep
                      // sees actionable messages (e.g. "Out-of-Scope Rate
                      // Schedule must be locked..." with detail telling them
                      // which card to click).
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              // v3.5.83.2: Lock-Rates-First UI guard. Same
                              // pattern as Send Proposal — gate the click on
                              // ratesLocked and route through the modal when
                              // unlocked. The backend block (v3.5.82.4.1)
                              // would catch this anyway with HTTP 400, but
                              // the modal saves a round-trip and gives the
                              // rep a clearer path forward.
                              if (!ratesLocked) {
                                setShowLockRatesModal('send_service_order');
                                return;
                              }
                              handleSendServiceOrder();
                            }}
                            disabled={soSending || soSuccess}
                            title={soSending
                              ? 'Generating Service Order...'
                              : soSuccess
                                ? 'Service Order sent successfully — refreshing...'
                                : !ratesLocked
                                  ? 'OOS rates aren\u2019t locked yet — clicking will prompt to lock first'
                                  : 'Click to manually generate and send the Service Order via SignWell'}
                            style={{
                              padding: '4px 10px',
                              fontSize: 10,
                              fontWeight: 700,
                              background: soSending ? '#e0e7ff' : soSuccess ? '#d1fae5' : '#fef3c7',
                              color:      soSending ? '#3730a3' : soSuccess ? '#065f46' : '#92400e',
                              border: !ratesLocked && !soSending && !soSuccess ? '1px solid #f59e0b' : 'none',
                              borderRadius: 3,
                              cursor: (soSending || soSuccess) ? 'wait' : 'pointer',
                              opacity: (soSending || soSuccess) ? 0.85 : 1,
                            }}>
                            {!ratesLocked && !soSending && !soSuccess ? '\u26A0 ' : ''}
                            {soSending ? 'Sending...' : soSuccess ? 'Sent ✓ refreshing...' : 'Send Service Order \u2192'}
                          </button>
                          {soError && (
                            <span style={{
                              marginLeft: 8,
                              padding: '4px 10px',
                              fontSize: 10,
                              fontWeight: 600,
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: 3,
                              maxWidth: 360,
                              display: 'inline-block',
                              lineHeight: 1.4,
                            }}>
                              {soError.message}
                              {soError.detail && (
                                <span style={{ display: 'block', fontWeight: 400, marginTop: 2, fontSize: 9 }}>
                                  {soError.detail}
                                </span>
                              )}
                            </span>
                          )}
                        </>
                      );
                    }
                    const swStatus = swDoc.status || 'sent';
                    const swSigned = swStatus === 'signed' || swStatus === 'completed' || !!swDoc.signed_at;
                    const swDeclined = swStatus === 'declined';
                    const label = swDeclined ? 'Service Order Declined ✗'
                                : swSigned    ? 'Service Order Signed ✓'
                                :               'Service Order Sent ✓';
                    const tip = swDeclined ? 'Client declined the service order. Contact them or send a revised version.'
                              : swSigned    ? `Signed by ${swDoc.client_name || 'client'}.`
                              :               `Awaiting signature. Sent to ${swDoc.client_email || 'client'}${swDoc.test_mode ? ' (test mode)' : ''}.`;
                    // v3.5.99.7.27: a sent Service Order can now be resent.
                    // Resending re-fires handleSendServiceOrder, which builds a
                    // FRESH SignWell document with the quote's current content
                    // (e.g. an updated effective date or live vs test mode) and
                    // re-emails the client. Offered for any non-signed state
                    // (awaiting signature or declined). Same rates-locked guard
                    // as the first send.
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span title={tip}
                          style={{
                            padding: '4px 10px',
                            fontSize: 10,
                            fontWeight: 700,
                            background: swDeclined ? '#fee2e2' : 'white',
                            color:      swDeclined ? '#991b1b' : palette.bg,
                            borderRadius: 3,
                          }}>
                          {label}
                        </span>
                        {!swSigned && (
                          <button
                            type="button"
                            onClick={() => {
                              if (!ratesLocked) { setShowLockRatesModal('send_service_order'); return; }
                              handleSendServiceOrder();
                            }}
                            disabled={soSending || soSuccess}
                            title={swDoc.test_mode
                              ? 'Resend as a new Service Order (the prior one was sent in test mode). Builds a fresh SignWell document and re-emails the client.'
                              : 'Resend the Service Order — builds a fresh SignWell document with current details and re-emails the client.'}
                            style={{
                              padding: '4px 10px',
                              fontSize: 10,
                              fontWeight: 700,
                              background: soSending ? '#e0e7ff' : 'rgba(255,255,255,0.9)',
                              color:      soSending ? '#3730a3' : palette.bg,
                              border: '1px solid rgba(255,255,255,0.6)',
                              borderRadius: 3,
                              cursor: (soSending || soSuccess) ? 'wait' : 'pointer',
                            }}>
                            {soSending ? 'Resending...' : soSuccess ? 'Sent ✓ refreshing...' : '↻ Resend'}
                          </button>
                        )}
                        {soError && (
                          <span style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: '#fee2e2', color: '#991b1b', borderRadius: 3, maxWidth: 360, lineHeight: 1.4 }}>
                            {soError.message}
                            {soError.detail && <span style={{ display: 'block', fontWeight: 400, marginTop: 2, fontSize: 9 }}>{soError.detail}</span>}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Price Lock Banner ── */}
        {pricingSnapshot && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 14px', background:'#1e3a5f', borderBottom:'1px solid #2d4f7a' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14 }}>🔒</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#93c5fd' }}>Prices locked</span>
                <span style={{ fontSize:10, color:'#64748b', marginLeft:6 }}>
                  as of {priceLockDate ? new Date(priceLockDate).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : 'approval'} — rates frozen at {pricingSnapshot.package?.name}
                </span>
              </div>
            </div>
            {(profile?.id === repId || profile?.role === 'admin') && (
              <button onClick={() => setShowUnlockModal(true)}
                style={{ fontSize:10, padding:'3px 10px', background:'rgba(255,255,255,0.1)', color:'#93c5fd', border:'1px solid #2d4f7a', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
                Unlock Pricing
              </button>
            )}
          </div>
        )}

        {/* ── v3.5.83.2: Lock-Rates-First Banner ──
            Renders when the quote is saved but OOS rates haven't been locked
            yet. Only shows pre-approval (suppressed once pricingSnapshot
            exists, since locking after approval would be too late anyway).
            Provides a "Jump to OOS Rates" button so the rep can scroll
            straight to the MarketRateCard's Accept All Rates button. */}
        {existingQuote?.id && !pricingSnapshot && !ratesLocked && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'8px 14px', background:'#fef3c7', borderBottom:'1px solid #fbbf24',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:14 }}>⚠️</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#92400e' }}>Lock OOS rates before sending</span>
                <span style={{ fontSize:10, color:'#78350f', marginLeft:6 }}>
                  Click <strong>Accept All Rates</strong> on the Out-of-Scope Rate Schedule card to capture current pricing for this client&rsquo;s market. Required before Service Order generation.
                </span>
              </div>
            </div>
            <button onClick={scrollToOosRateCard}
              style={{ fontSize:10, padding:'4px 10px', background:'#92400e', color:'white', border:'none', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
              Jump to OOS Rates ↓
            </button>
          </div>
        )}

        {/* ── Unlock Confirmation Modal ── */}
        {showUnlockModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 10px' }}>⚠ Unlock Pricing</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>
                This quote's pricing was locked when it was approved. Unlocking will allow rates to update based on current package pricing.
              </p>
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:5, padding:'8px 12px', fontSize:11, color:'#991b1b', marginBottom:18 }}>
                If you have already sent this quote to the client or exported it to Smart Pricing Table, unlocking may cause price discrepancies. Consider creating a new quote revision instead.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={() => setShowUnlockModal(false)}
                  style={{ padding:'7px 16px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={async () => {
                  setPricingSnapshot(null); setPriceLockDate(null); setShowUnlockModal(false);
                  if (existingQuote?.id) {
                    await supabase.from('quotes').update({ pricing_snapshot: null, price_locked_at: null, price_locked_by: null }).eq('id', existingQuote.id);
                    await saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: { ...inputs, proposalName }, totals: { finalMRR: result?.finalMRR }, lineItems: [], profile, note: 'Pricing unlocked — rates now reflect current package pricing' });
                  }
                }} style={{ padding:'7px 18px', background:'#dc2626', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Unlock & Recalculate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── v3.5.83.2: Lock-Rates-First Confirmation Modal ──
            Renders when the rep clicks Send Proposal or Send Service Order
            while OOS rates aren't locked. Three actions:
              - "Lock Rates Now"   → close + scroll to MarketRateCard
              - "Send Anyway"      → close + proceed with the original action
                                     (rare override case — rep has business
                                     reason to send pre-lock, e.g. internal
                                     review pass)
              - "Cancel"           → just close
            showLockRatesModal value tracks WHICH action triggered it so
            "Send Anyway" knows what to fire. */}
        {showLockRatesModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:480, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#92400e', margin:'0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>
                The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. {showLockRatesModal === 'send_proposal'
                  ? 'If you send the proposal now and the client approves, Service Order generation will fail until rates are locked.'
                  : 'The Service Order can\u2019t be generated until OOS rates are locked.'}
              </p>
              <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:5, padding:'10px 12px', fontSize:11, color:'#78350f', marginBottom:18, lineHeight:1.5 }}>
                <strong>Recommended:</strong> click <em>Lock Rates Now</em>, scroll to the Out-of-Scope Rate Schedule card, and click <em>Accept All Rates</em>. Then return here and click {showLockRatesModal === 'send_proposal' ? 'Send Proposal' : 'Send Service Order'} again.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap' }}>
                <button onClick={() => setShowLockRatesModal(null)}
                  style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => {
                  // "Send Anyway" — close modal and fire the original action.
                  // The backend block (v3.5.82.4.1) will still reject Service
                  // Order with rates_not_locked if applicable; this override
                  // is more meaningful for Send Proposal where the rep might
                  // legitimately want the client to see the proposal before
                  // rates are finalized.
                  const action = showLockRatesModal;
                  setShowLockRatesModal(null);
                  if (action === 'send_proposal') sendProposal();
                  else if (action === 'send_service_order') handleSendServiceOrder();
                }} style={{ padding:'7px 14px', background:'#fee2e2', border:'1px solid #fca5a5', color:'#991b1b', borderRadius:5, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Send Anyway
                </button>
                <button onClick={() => {
                  setShowLockRatesModal(null);
                  // Allow modal to close before scrolling so the page is
                  // fully visible during the smooth scroll animation.
                  setTimeout(scrollToOosRateCard, 50);
                }} style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Lock Rates Now ↓
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
        {!result
          ? <div style={{ textAlign:'center', color:'#9ca3af', marginTop:80, fontSize:12 }}>Enter client details to generate a quote</div>
          : <div className="fade-in">
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div>
                  <h2 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c' }}>{proposalName || recipientBiz || 'Quote Preview'}</h2>
                  <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>
                    {recipientBiz && <span>{recipientBiz} · </span>}
                    {selectedPkg?.name} · {selectedMkt?.name} · {inputs.contractTerm}-month term
                    {result.mktMult<1 && <span style={{ color:'#2563eb', marginLeft:4 }}>({Math.round((1-result.mktMult)*100)}% market pricing adjustment)</span>}
                  </div>
                  {recipientContact && <div style={{ fontSize:10, color:'#9ca3af', marginTop:1 }}>Contact: {recipientContact}{recipientEmail?` · ${recipientEmail}`:''}</div>}
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:8, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em' }}>Final Monthly MRR</div>
                  {/* v3.5.94.3: bind to effectiveFinalMRR (= result.finalMRR +
                      flexBlockMRR; computed line 1027) so the header reflects
                      the flex-inclusive monthly that customers see on the
                      proposal and that the term cards / KPI strip / multi-
                      term TCV row all already display. Pre-deploy this
                      element read result.finalMRR directly, omitting the
                      flex block — visible regression on quote
                      f18f9cfb-bf50-407c-a044-5facbc5619d0 (header showed
                      $507 while the selected 36-mo term card correctly
                      showed $1,243/mo for the same quote, with a $737/mo
                      Flex Time block explicitly noted in the banner).
                      The v3.5.92.0 changelog claimed the header was
                      already flex-inclusive — either that claim was wrong
                      or this is a regression that landed somewhere between
                      .92.0 and now. Either way, the fix is identical. */}
                  <div style={{ fontSize:22, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(effectiveFinalMRR)}</div>
                </div>
              </div>

              {/* ── Multi-term pricing preview — full width above KPI cards ── */}
              {multiTermResults && (
                <div style={{ background:'#f0f4ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'10px 12px', marginBottom:8 }}>
                  <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#1e40af', marginBottom:8 }}>
                    📋 Term Comparison — click to switch
                  </div>
                  {flexBlockMRR > 0 && (
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8, padding:'4px 8px', background:'#fff7ed', borderRadius:4, border:'1px solid #fed7aa' }}>
                      <span style={{ fontSize:9, color:'#c2410c', fontWeight:600 }}>⏱ Includes {flexBlock?.hours}hr Flex Time block (+{fmt$0(flexBlockMRR)}/mo)</span>
                      <span style={{ fontSize:8, color:'#9ca3af' }}>— added to all term totals below</span>
                    </div>
                  )}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6 }}>
                    {multiTermResults.map(({ term, result: r }) => {
                      const isCurrent = term === inputs.contractTerm;
                      const termMRR = r.finalMRR + flexBlockMRR;
                      const savings = multiTermResults[0].result
                        ? (r.finalMRR + flexBlockMRR) - (multiTermResults[0].result.finalMRR + flexBlockMRR)
                        : 0;
                      return (
                        <div key={term} onClick={() => set('contractTerm', term)} style={{ cursor:'pointer', borderRadius:5, padding:'8px 10px',
                          border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? '#2563eb' : '#dbeafe'}`,
                          background: isCurrent ? '#2563eb' : 'white', transition:'all 0.1s' }}>
                          <div style={{ fontSize:9, fontWeight:700, color: isCurrent ? '#bfdbfe' : '#6b7280' }}>{term}-MONTH</div>
                          <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color: isCurrent ? 'white' : '#0f1e3c', lineHeight:1.2 }}>
                            {fmt$0(termMRR)}
                          </div>
                          <div style={{ fontSize:9, color: isCurrent ? '#bfdbfe' : '#6b7280' }}>/mo</div>
                          {flexBlockMRR > 0 && (
                            <div style={{ fontSize:7, color: isCurrent ? 'rgba(255,255,255,0.6)' : '#f97316', marginTop:1 }}>
                              incl. {flexBlock?.hours}hr flex block
                            </div>
                          )}
                          {term !== 12 && savings !== 0 && (
                            <div style={{ fontSize:8, fontWeight:700, color: isCurrent ? '#bfdbfe' : savings < 0 ? '#166534' : '#dc2626', marginTop:2 }}>
                              {savings < 0 ? `${fmt$0(Math.abs(savings))}/mo savings` : `+${fmt$0(savings)}/mo`}
                            </div>
                          )}
                          {term === 12 && <div style={{ fontSize:8, color: isCurrent ? '#bfdbfe' : '#9ca3af', marginTop:2 }}>base rate</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize:8, color:'#93c5fd', marginTop:6 }}>
                    TCV: {multiTermResults.map(({term: t, result: r}) => `${t}mo = ${fmt$0((r.finalMRR + flexBlockMRR)*t + r.onboarding)}`).join(' · ')}
                  </div>

                  {/* Discount math explainer (v3.5.36) — shows contextual explanation
                      of why the term discount looks the way it does. When the floor is
                      meaningfully constraining the discount (effective < ~70% of headline),
                      the explainer surfaces prominently. Otherwise it's a subtle
                      collapsible link reps can expand on demand. */}
                  {(() => {
                    // Use 36mo result as the canonical reference for floor-vs-discount math
                    const r36 = multiTermResults.find(t => t.term === 36)?.result;
                    if (!r36) return null;
                    const subtotal = r36.discountableSubtotal || 0;
                    const floor    = r36.floor || 0;
                    const headlineRate = r36.discRate || 0;
                    const aboveFloor = Math.max(subtotal - floor, 0);
                    if (subtotal <= 0 || headlineRate <= 0) return null;
                    const effectiveRate = aboveFloor > 0 ? (aboveFloor * headlineRate) / subtotal : 0;
                    // Floor is "meaningfully constraining" when effective is well under headline
                    const floorConstraining = headlineRate > 0 && effectiveRate < headlineRate * 0.70;

                    // Build the rates-by-term map from settings for the explainer
                    const ratesByTerm = multiTermResults.map(({ term, result: r }) => ({
                      term, rate: (r?.discRate || 0)
                    }));

                    if (floorConstraining) {
                      // Prominent info box — the rep needs to understand this NOW
                      return (
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: 4 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#854d0e', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
                            ⓘ Why the term discount looks small on this quote
                          </div>
                          <div style={{ fontSize: 10, color: '#713f12', lineHeight: 1.5 }}>
                            Contract discounts ({ratesByTerm.map(r => `${r.term}mo: ${(r.rate * 100).toFixed(0)}%`).join(', ')}) only apply to revenue <strong>above</strong> the {fmt$(floor)} minimum commitment floor.
                            On this quote, discountable revenue is {fmt$(subtotal)}, of which only <strong>{fmt$(aboveFloor)}</strong> sits above the floor — so the headline {(headlineRate * 100).toFixed(0)}% becomes an effective {(effectiveRate * 100).toFixed(1)}%.
                            <span style={{ display: 'block', marginTop: 4, fontStyle: 'italic', color: '#854d0e' }}>
                              Pitch as "<strong>up to {(headlineRate * 100).toFixed(0)}%</strong> off" or focus on TCV savings rather than monthly %.
                            </span>
                          </div>
                        </div>
                      );
                    }

                    // Floor not meaningfully constraining — subtle collapsible link
                    return (
                      <details style={{ marginTop: 8, fontSize: 9, color: '#6b7280' }}>
                        <summary style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}>
                          ⓘ How are these discounts calculated?
                        </summary>
                        <div style={{ marginTop: 4, padding: '6px 8px', background: '#f9fafb', borderRadius: 3, lineHeight: 1.5 }}>
                          Contract discounts are applied to the discountable revenue ({fmt$(subtotal)}/mo on this quote) above the {fmt$(floor)} minimum commitment floor.
                          Headline rates: {ratesByTerm.map(r => `${r.term}mo ${(r.rate * 100).toFixed(0)}%`).join(' · ')}.
                          On this quote, {fmt$(aboveFloor)} is above floor, so the discount is close to the headline rate.
                        </div>
                      </details>
                    );
                  })()}
                </div>
              )}

              {/* ── Compliance recommendations — full width above KPI cards ── */}
              {unselectedRecommended.length > 0 && (
                <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:6, padding:'9px 12px', marginBottom:8 }}>
                  <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#92400e', marginBottom:6 }}>
                    {/* v3.5.59: framework-specific label from 5-value dropdown */}
                    ⚠ Compliance Recommendations — {({ hipaa:'HIPAA', soc2:'SOC 2', pci:'PCI DSS', cmmc:'CMMC', moderate:'HIPAA/SOC 2', high:'PCI/CMMC' }[inputs.compliance] || 'Compliance')}
                  </div>
                  {unselectedRecommended.map(p => (
                    <div key={p.id} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:5, padding:'6px 8px', background:'white', borderRadius:4, border:'1px solid #fde68a' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#0f1e3c' }}>{p.name}</div>
                        {p.recommendation_reason && <div style={{ fontSize:9, color:'#78350f', marginTop:1 }}>{p.recommendation_reason}</div>}
                      </div>
                      <button onClick={() => setInputs(prev => ({ ...prev, selectedProducts: [...(prev.selectedProducts||[]), p.id] }))}
                        style={{ padding:'3px 8px', background:'#d97706', color:'white', border:'none', borderRadius:3, fontSize:9, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* KPI cards — 4 inline */}
              {(() => {
                // v3.5.58: extract effectiveGM into a single computation that
                // uses getBurdenedRate (settings-driven). When a flex block is
                // active, total cost includes flex block labor at the blended
                // burdened rate (NOT senior — flex is delivery, not advisory).
                // ITSO advisory cost is already in result.totalCost via the
                // engine, so no extra term needed for it here.
                const burdenedRate = getBurdenedRate(settings);
                const effectiveGM = flexBlock
                  ? 1 - (result.totalCost + (flexBlock.hours * burdenedRate)) / effectiveFinalMRR
                  : result.impliedGM;
                return (
                  <>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7, marginBottom:10 }}>
                      {[
                        ['Monthly MRR',         fmt$0(effectiveFinalMRR),                                                      '#0f1e3c', '#f0f4ff'],
                        ['One-Time Onboarding', fmt$0(obIncentive?.effectiveFee ?? result.onboarding),                          '#0f766e', '#f0fdf4'],
                        ['Implied GM',          fmtPct(effectiveGM),                                                            gmColor(effectiveGM), gmBg(effectiveGM)],
                        ['Contract TCV',        fmt$0(effectiveFinalMRR*inputs.contractTerm+(obIncentive?.effectiveFee??result.onboarding)), '#6d28d9', '#faf5ff'],
                      ].map(([l,v,co,bg])=>(
                        <div key={l} style={{ background:bg, borderRadius:5, padding:'7px 6px', textAlign:'center' }}>
                          <div style={{ fontSize:7, fontWeight:600, color:'#6b7280', letterSpacing:'.05em', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                          <div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:co }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    {/* GM Target warning badge (v3.5.34) — shown when implied GM diverges
                        from the target_gross_margin setting. Renders muted on locked quotes
                        since the deal is done and advice is no longer actionable. */}
                    <div style={{ marginBottom: 10 }}>
                      <GMBadge currentGM={effectiveGM} targetGM={parseFloat(settings?.target_gross_margin)} locked={!!pricingSnapshot} />
                    </div>
                  </>
                );
              })()}

              {result.finalMRR<=result.floor+.01&&result.riskAdjMRR<result.floor&&(
                <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'6px 9px', marginBottom:8, fontSize:9, color:'#92400e' }}>
                  ⚠ Price set by minimum commitment floor ({fmt$(result.floor)}). Rate-based: {fmt$(result.riskAdjMRR)}
                </div>
              )}

              {/* ── Flex Time Card ── */}
              <FlexTimeMeter
                pkg={selectedPkg}
                workstations={inputs.workstations}
                users={inputs.users}
                addonHours={flexHours}
              />

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {/* Line items */}
                <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Monthly Recurring Revenue</div>
                  <SH l="Managed IT Services"/>
                  <LI lbl={`WS (${inputs.workstations} × $${selectedPkg?.ws_rate})`} v={result.wB} ind/>
                  <LI lbl={`US (${inputs.users} × $${selectedPkg?.user_rate})`} v={result.uB} ind/>
                  <LI lbl="Servers" v={result.sB} ind/>
                  <LI lbl="Locations" v={result.lB} ind/>
                  <LI lbl="Cloud tenants" v={result.tB} ind/>
                  <LI lbl="Additional vendors" v={result.vB} ind/>
                  <LI lbl="Coverage uplift" v={result.covU} ind/>
                  {/* v3.5.61: Log Storage & SIEM Uplift — only renders when
                      excess devices exist (eB > 0). Inline math shows excess
                      device count, per-device rate, and density tier so reps
                      can articulate the charge instead of pointing at a
                      bracketed multiplier. xEp can be fractional (workstations
                      x 1.25 doesn't always land on integers); we display 1
                      decimal only when needed. */}
                  {result.eB > 0 && (() => {
                    const xEp = result.xEp || 0;
                    const xEpStr = xEp % 1 === 0 ? xEp.toFixed(0) : xEp.toFixed(1);
                    return (
                      <LI lbl={`Log Storage & SIEM (${xEpStr} dev × $${result.epRate} — ${result.epTier})`} v={result.eB} ind/>
                    );
                  })()}
                  <LI lbl="Managed IT Subtotal" v={result.itSubtotal} bold/>
                  {result.lineItems.length>0&&(()=>{
                    const byC=result.lineItems.reduce((a,li)=>{(a[li.category]=a[li.category]||[]).push(li);return a},{});
                    return Object.entries(byC).map(([cat,items])=>(
                      <span key={cat}>
                        <SH l={cat}/>
                        {items.map(li=>{
                          const costDiffers = li.cost_qty_driver && li.cost_qty_driver !== li.qty_driver;
                          return (
                            <span key={li.product_id}>
                              <LI lbl={`${li.product_name} (${li.qty} × $${li.sell_price})`} v={li.revenue} ind/>
                              {costDiffers && <div style={{fontSize:8,color:'#9ca3af',marginLeft:16,marginBottom:2}}>cost basis: {li.cost_qty} {li.cost_qty_driver}s × ${li.cost_price}</div>}
                            </span>
                          );
                        })}
                      </span>
                    ));
                  })()}
                  {result.addonRevenue>0&&<LI lbl="Add-ons Subtotal" v={result.addonRevenue} bold/>}
                  <div style={{ margin:'5px 0', borderTop:'2px solid #0f1e3c' }}/>
                  <LI lbl="Operational Subtotal" v={result.opSubtotal} bold/>
                  {/* v3.5.58: relabeled — RISK_M was removed from compMult; the
                      multiplier now reflects Compliance × Complexity only.
                      Industry Risk drives the visible ITSO Advisory line below. */}
                  {result.compMult!==1&&<LI lbl={`Compliance × Complexity multiplier: ${result.compMult.toFixed(2)}×`} v={result.riskAdjMRR} muted/>}
                  {/* v3.5.58: ITSO Advisory line — appears when Industry Risk
                      drives a non-zero uplift. itsoMRR is the post-discount
                      net (so it tracks what the customer actually pays). */}
                  {result.itsoMRR > 0 && result.itsoLabel && (
                    <LI lbl={`${result.itsoLabel} (${((result.itsoUpliftRate || 0) * 100).toFixed(0)}% × labor base)`} v={result.itsoMRR} ind/>
                  )}
                  {result.discount<0&&<LI lbl={`${Math.round(result.discRate*100)}% contract discount`} v={result.discount} ind/>}
                  {flexBlock && <LI lbl={`Flex Time — ${flexBlock.hours}hr block (${flexBlock.discountPct}% off T&M)`} v={flexBlock.blockPrice} ind/>}
                  <LI lbl={flexBlockMRR > 0 ? `✦ Final MRR (incl. ${flexBlock.hours}hr flex block)` : '✦ Final Monthly MRR'} v={effectiveFinalMRR} hi/>
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* Onboarding */}
                  <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>One-Time Onboarding</div>
                    <LI lbl={`Users (${inputs.users} × $35)`} v={inputs.users*35} ind/>
                    <LI lbl={`Workstations (${inputs.workstations} × $20)`} v={inputs.workstations*20} ind/>
                    <LI lbl={`Servers (${inputs.servers} × $250)`} v={inputs.servers*250} ind/>
                    <LI lbl={`Locations (${inputs.locations} × $450)`} v={inputs.locations*450} ind/>
                    {result.onboardingIsMinimum&&<div style={{ fontSize:8, color:'#9ca3af', padding:'1px 2px' }}>$500 minimum applied</div>}
                    <div style={{ margin:'4px 0', borderTop:'2px solid #0f766e' }}/>
                    {/* v3.5.81.10: discount-aware Total line. Previously the
                        card showed just the gross Total Onboarding Fee with
                        no acknowledgement of an applied incentive — reps
                        had to read the green banner below the card to know
                        the effective fee, which broke down when they
                        clicked Modify (banner went away) or when the
                        component was in mid-edit picker state for any
                        reason. Now the card itself shows the gross total,
                        the discount line, and the net (effective) fee
                        when an incentive is applied; falls back to the
                        single-line gross when no incentive. obIncentive
                        is the source of truth — independent of the
                        OnboardingIncentive component's accepted/mode
                        internal state. */}
                    {obIncentive?.effectiveFee != null && obIncentive.effectiveFee !== result.onboarding ? (
                      <>
                        <LI lbl="Subtotal — Standard Onboarding Fee" v={result.onboarding} muted/>
                        <LI
                          lbl={
                            obIncentive.mode === 'discount'
                              ? (obIncentive.discountPct === 100
                                  ? 'Onboarding Waived (incentive applied)'
                                  : `Less ${obIncentive.discountPct || 0}% Onboarding Incentive`)
                              : obIncentive.mode === 'split'
                                ? `Split into ${obIncentive.splitMonths || 0}-month installments`
                                : 'Less Onboarding Incentive'
                          }
                          v={-(result.onboarding - obIncentive.effectiveFee)}
                          ind
                        />
                        <LI lbl="Net Onboarding Fee" v={obIncentive.effectiveFee} hi/>
                      </>
                    ) : (
                      <LI lbl="Total Onboarding Fee" v={result.onboarding} hi/>
                    )}
                  </div>
                  <OnboardingIncentive
                    fee={result.onboarding}
                    marketTier={acceptedMktTier || ({ major_metro:'premium', mid_market:'standard', small_market:'adjusted', rural:'secondary' }[zipResult?.tier] || zipResult?.tier || 'secondary')}
                    contractTerm={inputs.contractTerm}
                    initialValue={obIncentive}
                    onChange={inc => {
                      // v3.5.73: removed the `&& inc?.mode` portion of the gate
                      // so the No-Incentive case (mode=null) ALSO writes a
                      // version snapshot. The fallback label string at line
                      // 1504 ('Onboarding: no incentive selected') was always
                      // present but unreachable — the previous gate excluded
                      // mode=null, so reps got no visible feedback when
                      // declining the incentive.
                      //
                      // v3.5.81.2: that gate-removal accidentally exposed a
                      // pre-existing latent bug in OnboardingIncentive.js —
                      // the component's emit useEffect (lines 130-143) and
                      // the fee-bucket-reset useEffect (lines 120-127) BOTH
                      // fire onChange on every fee change AND on initial
                      // mount, not just on user-driven mode/pct changes.
                      // The pre-v3.5.73 gate masked these spurious fires;
                      // post-v3.5.73, every quote re-render wrote a
                      // duplicate "no incentive selected" revision (7+ per
                      // session in heavy testing). Fixed via signature
                      // dedup below.
                      //
                      // v3.5.81.4: TWO additional bugs in this onChange path.
                      //
                      // Bug 1 — Modify click silently wipes parent state.
                      // Clicking Modify on the green accepted-incentive
                      // banner sets accepted=false in the component, which
                      // makes its emit useEffect fire the `!mode || !accepted`
                      // branch with mode=null. The previous code ran
                      // setObIncentive(inc) unconditionally before any
                      // sig-check, so opening the picker to inspect or
                      // re-edit instantly clobbered the parent's obIncentive
                      // state to null. If the rep then clicked the main
                      // Save button without re-applying an incentive, the
                      // saveQuote payload at line 538 read obIncentive
                      // (null) and persisted null — wiping the saved
                      // selection. This matched the symptom Shaun reported
                      // ('changed to no incentive, then to 50% off, still
                      // not saving'): every Modify-mediated transition
                      // erased the in-memory state, and the next main-save
                      // committed the erasure.
                      //
                      // Fix: distinguish DEFINITIVE emits (mode is one of
                      // 'split' / 'discount' / 'none' — the rep has reached
                      // a committed state) from MID-EDIT emits (mode is
                      // null — rep is in the picker). Mid-edit emits do
                      // nothing: no setObIncentive, no auto-save, no audit.
                      // The component's internal state (mode/splitMonths/
                      // discountPct/accepted) tracks the picker progress;
                      // the parent only learns about committed states.
                      //
                      // Bug 2 — main-Save dependency.
                      // Even with bug 1 fixed, the rep had to remember to
                      // click the main Save button after changing the
                      // incentive. The audit log entry made it LOOK saved.
                      // saveQuote was the only path that wrote to the
                      // quotes.onboarding_incentive column; saveQuoteVersion
                      // (which fired on every onChange) only wrote to the
                      // separate quote_versions history table. Reps would
                      // see a revision-history entry, navigate away, reopen,
                      // and the change would be gone — quote_versions has
                      // no impact on what loads on reopen.
                      //
                      // Fix: auto-save definitive changes directly to the
                      // quotes.onboarding_incentive column. Mirrors the
                      // saveQuote() shape exactly. The main Save button
                      // remains as a backstop — if the auto-save fails
                      // (network error, RLS reject), saveQuote will pick
                      // up the same React state and persist on next click.
                      const isDefinitive = inc?.mode === 'split' || inc?.mode === 'discount' || inc?.mode === 'none';
                      if (!isDefinitive) return; // mid-edit — preserve obIncentive, no writes

                      const sigOf = i => {
                        if (!i || !i.mode || i.mode === 'none') return 'no-incentive';
                        return `${i.mode}|${i.splitMonths || 0}|${i.discountPct || 0}`;
                      };
                      const prevSig = sigOf(obIncentive);
                      const newSig  = sigOf(inc);
                      setObIncentive(inc);
                      if (prevSig === newSig) return; // no functional change — skip writes

                      if (existingQuote?.id) {
                        // v3.5.81.4: auto-save to quotes.onboarding_incentive
                        // immediately. Shape mirrors saveQuote() at line 538.
                        // Fire-and-forget — failures log to console; the
                        // main Save button remains as a backstop because
                        // saveQuote reads the same React state.
                        const incentivePayload = {
                          mode:           inc.mode,
                          effectiveFee:   inc.effectiveFee,
                          monthlyAdd:     inc.monthlyAdd     || 0,
                          splitMonths:    inc.splitMonths    || 0,
                          discountPct:    inc.discountPct    || 0,
                          discountAmount: inc.discountAmount || 0,
                          fullFee:        result?.onboarding ?? null,
                        };
                        supabase.from('quotes')
                          .update({ onboarding_incentive: incentivePayload })
                          .eq('id', existingQuote.id)
                          .then(({ error }) => {
                            if (error) console.error('Onboarding incentive auto-save failed:', error);
                          });

                        const label = inc?.mode === 'split'
                          ? `Onboarding: split over ${inc.splitMonths} months (+${fmt$0(inc.monthlyAdd)}/mo)`
                          : inc?.mode === 'discount'
                            ? (inc.discountPct === 100
                                ? `Onboarding: full waive applied — effective fee $0`
                                : `Onboarding: ${inc.discountPct}% discount applied — effective fee ${fmt$0(inc.effectiveFee)}`)
                            : 'Onboarding: no incentive selected — full fee applies';
                        saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: { ...inputs, proposalName }, totals: { finalMRR: discountedFinalMRR, onboarding: inc?.effectiveFee ?? result.onboarding }, lineItems: [], profile, note: label });
                      }
                    }}
                  />

                  {/* v3.5.99.7.23: Manual discount lines (% or $/mo + note) */}
                  <ManualDiscounts
                    value={manualDiscounts}
                    onChange={setManualDiscounts}
                    baseMRR={result.finalMRR}
                  />
                  {manualDiscountResolved.total > 0 && (() => {
                    const targetGM = parseFloat(settings?.target_gross_margin);
                    const totalCostWithFlex = result.totalCost + (flexBlock ? flexBlock.hours * burdenedRate : 0);
                    const liveGM = effectiveFinalMRR > 0 ? 1 - totalCostWithFlex / effectiveFinalMRR : 0;
                    if (!isFinite(targetGM) || liveGM >= targetGM) return null;
                    return (
                      <div style={{ marginTop: 6, fontSize: 10, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 8px' }}>
                        ⚠ Manual discount drops gross margin below target ({fmtPct(liveGM)} &lt; {fmtPct(targetGM)}). You can still send — review scope if this wasn&rsquo;t intended.
                      </div>
                    );
                  })()}

                  {/* v3.7.8: one-time (non-recurring) discounts. Managed IT has
                      only the Onboarding bucket (post-incentive base). */}
                  <OneTimeDiscounts
                    value={oneTimeDiscounts}
                    onChange={setOneTimeDiscounts}
                    bases={oneTimeBases}
                  />

                  {/* v3.5.99.7.24: optional back-dated agreement effective date */}
                  <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />

                  {/* Cost model */}
                  {(() => {
                    // Flex block delivery cost: blended burden × block hours.
                    // v3.5.58: getBurdenedRate helper replaces inline `|| 125` fallback
                    // (default updated to $80, matching pricing_settings actual).
                    const burdenedRate   = getBurdenedRate(settings);
                    const flexLaborCost  = flexBlock ? flexBlock.hours * burdenedRate : 0;
                    const flexCommission = flexBlock && repProfile?.commission_rate
                      ? flexBlock.blockPrice * repProfile.commission_rate : 0;
                    const totalCostWithFlex  = result.totalCost + flexLaborCost;
                    const effectiveGM = effectiveFinalMRR > 0 ? 1 - totalCostWithFlex / effectiveFinalMRR : 0;
                    const effectiveCommission = result.commission + flexCommission;
                    const netAfterCommissionFlex = effectiveFinalMRR - effectiveCommission;
                    const gmAfterCommission = effectiveFinalMRR > 0 ? (netAfterCommissionFlex - totalCostWithFlex) / effectiveFinalMRR : 0;
                    const gcFlex = gmColor(effectiveGM);
                    const gbFlex = gmBg(effectiveGM);

                    return (
                      <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Cost Model — Monthly Recurring</div>
                        {/* v3.5.39 / v3.5.41: Tooling / stack with per-tool breakdown.
                            v3.5.57: simplified — components is the only path now,
                            so the legacy per-driver branch and the path indicator
                            were removed. */}
                        <LI lbl="Tooling / stack" v={result.toolingCost} bold/>
                        {Array.isArray(result.toolingTools) && result.toolingTools.length > 0 && (
                          <>
                            {result.toolingTools
                              .filter(t => t.total > 0 || t.qty > 0)
                              .map((tool, i) => (
                                <LI key={i}
                                  lbl={`  ${tool.name} (${tool.qty.toFixed(tool.qty < 10 ? 2 : 0)} × ${fmt$(tool.rate)})`}
                                  v={tool.total} ind muted/>
                              ))
                            }
                            <div style={{ fontSize: 8, color: '#9ca3af', fontStyle: 'italic', marginTop: 1, marginBottom: 4, paddingLeft: 8 }}>
                              ↳ from {result.toolingTools.length} cost components
                            </div>
                          </>
                        )}
                        <LI lbl={`Labor (${result.svcHrs.toFixed(1)} hrs × ${fmt$(burdenedRate)}/hr)`} v={result.svcCost} ind/>
                        <LI lbl="Add-on delivery cost" v={result.addonCost} ind/>
                        {/* v3.5.58: ITSO senior advisory hours flow into the cost
                            panel as a separate line so the senior-staff allocation
                            is visible. Hours are derived from itsoMRR ÷ senior
                            burdened rate; rendered only when an ITSO uplift is
                            active (Industry Risk > None). */}
                        {result.itsoMRR > 0 && result.itsoLabel && (
                          <LI lbl={`${result.itsoLabel} labor (${result.itsoHours.toFixed(1)} hrs × ${fmt$(getSeniorBurdenedRate(settings))}/hr cost rate)`} v={result.itsoCost} ind/>
                        )}
                        {flexBlock && (
                          <LI lbl={`Flex Time labor (${flexBlock.hours} hrs × ${fmt$(burdenedRate)}/hr)`} v={flexLaborCost} ind/>
                        )}
                        <LI lbl="Estimated Total Cost" v={totalCostWithFlex} bold/>
                        <div style={{ borderTop:'1px dashed #e5e7eb', margin:'5px 0' }}/>
                        {result.protectedAddonRevenue > 0 && (
                          <LI lbl="Protected product revenue (MSRP)" v={result.protectedAddonRevenue} ind muted/>
                        )}
                        {effectiveCommission > 0 && (
                          <LI lbl={`Commission — ${repProfile?.full_name || repProfile?.email?.split('@')[0] || 'Rep'} (${fmtPct(result.commissionRate)} on ${fmt$0(result.commissionBase + (flexBlock?.blockPrice||0))} commissionable MRR)`} v={-effectiveCommission} ind/>
                        )}
                        {effectiveCommission > 0 && (
                          <LI lbl="Net MRR after commission" v={netAfterCommissionFlex} bold/>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 6px', background:gbFlex, borderRadius:4, marginTop:4 }}>
                          <span style={{ fontSize:10, fontWeight:700, color:gcFlex }}>Implied Gross Margin</span>
                          <span style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:gcFlex }}>{fmtPct(effectiveGM)}</span>
                        </div>
                        {/* v3.5.39: explicit footnote that GM is recurring-only */}
                        <div style={{ fontSize:8, color:'#9ca3af', marginTop:3, fontStyle:'italic' }}>
                          Excludes onboarding and one-time fees — recurring revenue and recurring cost only
                        </div>
                        {effectiveCommission > 0 && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 6px', background:'#f8fafc', borderRadius:4, marginTop:3 }}>
                            <span style={{ fontSize:9, color:'#6b7280' }}>GM after commission</span>
                            <span style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color: gmAfterCommission > 0 ? '#166534' : '#dc2626' }}>
                              {fmtPct(gmAfterCommission)}
                            </span>
                          </div>
                        )}
                        {effectiveGM < 0.40 && <div style={{ marginTop:4, fontSize:9, color:'#92400e', background:'#fef3c7', padding:'3px 5px', borderRadius:3 }}>⚠ Below 40% — review scope or package.</div>}
                        {flexBlock && (
                          <div style={{ marginTop:4, fontSize:9, color:'#6b7280', fontStyle:'italic' }}>
                            * Includes {flexBlock.hours}hr flex block at {fmt$0(flexBlock.blockPrice)}/mo revenue and {fmt$0(flexLaborCost)}/mo burdened cost
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Payment surcharge notice */}
                  <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6, padding:'8px 11px' }}>
                    <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6b7280', marginBottom:5 }}>💳 Payment Methods</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
                        <span style={{ color:'#374151' }}>ACH / EFT</span>
                        <span style={{ fontWeight:700, color:'#166534' }}>{achFee === 0 ? 'Free' : `+$${achFee}`}</span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
                        <span style={{ color:'#374151' }}>Check</span>
                        <span style={{ fontWeight:700, color:checkFee > 0 ? '#92400e' : '#166534' }}>{checkFee > 0 ? `+$${checkFee} admin fee` : 'Free'}</span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
                        <span style={{ color:'#374151' }}>Credit Card</span>
                        <span style={{ fontWeight:700, color:'#dc2626' }}>+{(ccSurcharge*100).toFixed(0)}% surcharge</span>
                      </div>
                    </div>
                    {ccSurcharge > 0 && (
                      <div style={{ fontSize:8, color:'#9ca3af', marginTop:5, borderTop:'1px solid #f1f5f9', paddingTop:4 }}>
                        CC surcharge on this quote: ~{fmt$0(discountedFinalMRR * ccSurcharge)}/mo · ACH/EFT recommended
                      </div>
                    )}
                  </div>

                  {/* Deal summary */}
                  <div style={{ background:'#0f1e3c', borderRadius:6, padding:11 }}>
                    <div style={{ fontSize:8, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'#475569', marginBottom:6 }}>Deal Summary</div>
                    {[
                      ['Quote #', existingQuote?.quote_number||'Unsaved'],
                      proposalName&&['Proposal', proposalName],
                      ['Business Name', recipientBiz],
                      recipientContact&&['Contact', recipientContact],
                      ['Package', selectedPkg?.name],
                      ['Market', selectedMkt?.name],
                      ['Contract', `${inputs.contractTerm} months`],
                      ['Monthly MRR', fmt$0(discountedFinalMRR)],
                      ['Onboarding', fmt$0(result.onboarding)],
                      ['Total Contract Value', fmt$0(discountedFinalMRR*inputs.contractTerm+(obIncentive?.effectiveFee ?? result.onboarding))],
                      hubDealId&&['HubSpot Deal', hubDealName||`#${hubDealId}`],
                    ].filter(Boolean).map(([k,v])=>(
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #1e3a5f' }}>
                        <span style={{ fontSize:9, color:'#64748b' }}>{k}</span>
                        <span style={{ fontSize:9, fontWeight:600, color:'white', fontFamily:typeof v==='number'||(typeof v==='string'&&v.startsWith('$'))?'DM Mono, monospace':'inherit', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* Market Rate Analysis */}
                  {/* v3.5.83.2: id="oos-rate-card" anchor for the Lock-Rates-First
                      UI guard's "Lock Rates Now" action and banner button. The
                      scrollToOosRateCard helper targets this wrapper so reps
                      can jump straight to the Accept All Rates button without
                      hunting through the page. */}
                  <div id="oos-rate-card">
                  <MarketRateCard
                    quoteId={existingQuote?.id}
                    clientZip={clientZip}
                    fallbackMarket={selectedMkt}
                    onRatesAccepted={(rates, suggestedTier, analysis) => {
                      // Capture the accepted rates so flex block + FlexTimeSelector use them
                      // (and so the snapshot can persist them on lock).
                      if (rates) setAcceptedRates(rates);
                      if (analysis?.pricing_multiplier != null) {
                        setPendingMultiplier({
                          multiplier: analysis.pricing_multiplier,
                          tier: analysis.market_tier,
                          city: analysis.city,
                          state: analysis.state,
                        });
                        setShowMktRecommend(true);
                      }
                      if (suggestedTier) setAcceptedMktTier(suggestedTier);
                      // v3.5.43: keep marketAnalysis in sync so DocumentsPanel/RateSheet
                      // shows the just-accepted rates immediately, not the stale ones
                      // from before acceptance.
                      if (analysis) {
                        setMarketAnalysis({
                          ...analysis,
                          rates: rates || analysis.rates,
                        });
                      }
                    }}
                  />
                  </div>
                  {/* end oos-rate-card wrapper (v3.5.83.2) */}

                  {/* Flex Time Add-On */}
                  {selectedPkg && selectedPkg.flex_time_model !== 'all_inclusive' && (
                    <>
                    <FlexTimeSelector
                      remoteRate={flexBlockRate}
                      settings={settings}
                      selectedHours={flexHours}
                      onChange={hrs => setFlexHours(hrs)}
                      onApply={async (hrs) => {
                        setFlexHours(hrs);
                        // v3.5.94.5: three feedback paths added per the
                        // May 12 2026 UX bug report. Pre-deploy this handler
                        // (a) silently no-op'd if existingQuote.id was
                        // missing (the rep saw no feedback at all on a
                        // brand-new unsaved quote — apply just updated
                        // React state); (b) had no try/catch around the
                        // supabase update, so a DB error left the user in
                        // an indeterminate state; (c) wrote to setSaveMsg
                        // which renders in QuoteSaveBar at the bottom of
                        // the page, off-screen from where the rep clicks.
                        // Post-deploy: separate flexSaveMsg state renders
                        // a banner directly beneath this component (see
                        // below) with success / warning / error variants.
                        if (!existingQuote?.id) {
                          setFlexSaveMsg('⚠ Save the quote first, then apply the Flex Time block.');
                          setTimeout(() => setFlexSaveMsg(''), 4500);
                          return;
                        }
                        try {
                          const updatedInputs = { ...inputs, proposalName, flexHours: hrs || null };
                          const { error: upErr } = await supabase.from('quotes').update({ inputs: updatedInputs }).eq('id', existingQuote.id);
                          if (upErr) throw upErr;
                          await saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: updatedInputs, totals: { finalMRR: effectiveFinalMRR }, lineItems: [], profile, note: hrs ? `Flex Time block applied: ${hrs} hours` : 'Flex Time block removed' });
                          setFlexSaveMsg(hrs ? `✓ ${hrs}-hour Flex Time block applied and saved` : '✓ Flex Time block removed and saved');
                          setSaveMsg('✓ Flex Time saved');  // kept for the save bar if the rep scrolls there
                          setTimeout(() => setFlexSaveMsg(''), 3500);
                          setTimeout(() => setSaveMsg(''), 2500);
                        } catch (e) {
                          console.error('Flex Time save failed:', e);
                          setFlexSaveMsg(`✗ Save failed: ${e?.message || 'unknown error'} — please try again`);
                          setTimeout(() => setFlexSaveMsg(''), 6000);
                        }
                      }}
                      mode="managed"
                      packageModel={selectedPkg?.flex_time_model || 'none'}
                      includedMinsPerWS={selectedPkg?.flex_included_mins_per_ws || 0}
                      workstations={inputs.workstations || 0}
                      repCommissionRate={repProfile?.commission_rate ?? null}
                    />
                    {/* v3.5.94.5: local feedback banner. Sits directly under
                        the FlexTimeSelector so the rep sees confirmation in
                        their viewport when they click Apply & Save. Color-
                        coded by leading glyph: ✓ green, ⚠ amber, ✗ red.
                        Auto-dismisses on a timer (3.5s for success, 4.5s for
                        warning, 6s for error — errors held longer so the rep
                        has time to read the message before it disappears). */}
                    {flexSaveMsg && (
                      <div style={{
                        marginTop: 8,
                        padding: '8px 12px',
                        fontSize: 11,
                        fontWeight: 600,
                        textAlign: 'center',
                        borderRadius: 4,
                        color:      flexSaveMsg.startsWith('✓') ? '#065f46' : flexSaveMsg.startsWith('⚠') ? '#92400e' : '#991b1b',
                        background: flexSaveMsg.startsWith('✓') ? '#d1fae5' : flexSaveMsg.startsWith('⚠') ? '#fef3c7' : '#fee2e2',
                        border: `1px solid ${flexSaveMsg.startsWith('✓') ? '#6ee7b7' : flexSaveMsg.startsWith('⚠') ? '#fcd34d' : '#fca5a5'}`,
                      }}>
                        {flexSaveMsg}
                      </div>
                    )}
                    </>
                  )}

                  {/* Documents panel
                      v3.5.43: marketAnalysis is loaded from the rate sheet (if
                      locked) or from market_rate_analyses by zip. The previous
                      selectedMkt-synthesized object always passed rates: {}
                      because market_tiers has no rates column, making the
                      rate sheet always show fallback BASE_RATES. */}
                  {/* v3.5.81.9: Stripe Prepayment was charging the FULL onboarding
                      amount even when the rep applied a discount incentive (50%
                      discount → still charging $1,085 instead of $542.50). The
                      Stripe panel rendered inside DocumentsPanel reads its
                      prepayAmount prop verbatim. We were passing result.onboarding
                      (the raw engine output) without applying the rep's
                      accepted incentive on top. obIncentive.effectiveFee is the
                      post-incentive amount the customer actually owes — that's
                      what Stripe should charge. Falls back to result.onboarding
                      when no incentive is applied. */}
                  <DocumentsPanel
                    analysis={marketAnalysis || (selectedMkt ? { city: marketCity, state: marketState, market_tier: selectedMkt.tier_key, pricing_multiplier: aiMultiplier || 1, rates: {} } : null)}
                    settings={settings}
                    clientName={recipientBiz}
                    recipientContact={recipientContact}
                    quoteId={existingQuote?.id}
                    quoteNumber={existingQuote?.quote_number}
                    sptProposalId={sptProposalId}
                    onSPTLinked={(pid) => setSptProposalId(pid)}
                    inputs={inputs}
                    pkg={selectedPkg}
                    products={products}
                    complianceKey={complianceKey}
                    result={result}
                    obIncentive={obIncentive}
                    quoteType="managed-it"
                    clientEmail={recipientEmail}
                    prepayAmount={oneTimeResolved.finalNRC}
                    hubspotDealId={hubDealId}
                    /* v3.5.86.3.2: page-driven Customer Story integration. The
                       page owns the AssumptionsModal mount; DocumentsPanel
                       just renders the doc card and routes the click back
                       here. assumptionsComplete drives the ✓/⚠ styling so the
                       card and the page-level Customer Story button stay in
                       lockstep. */
                    onOpenAssumptions={() => setShowAssumptions(true)}
              onOpenAddendums={() => setAddendumsModalOpen(true)}
              addendumsCount={addendums.length}
              onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)}
                    assumptionsSaved={assumptionsComplete}
                  />

                  {/* v3.5.99.6: client engagement panel — auto-hides on quotes with no proposal/SO activity yet */}
                  <EngagementPanel quoteId={existingQuote?.id} />
                  {/* v3.5.99.7.13: ETF Calculator — visible only on saved quotes
                      (existingQuote?.id present means the quote was loaded/saved). */}
                  {existingQuote?.id && (
                    <button onClick={() => setShowEtfModal(true)}
                      style={{ width:'100%', marginBottom:10, padding:'8px 12px', background:'white', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, color:'#0f1e3c', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:14 }}>💰</span>
                      <span style={{ flex:1 }}>Calculate Early Termination Fee</span>
                      <span style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>→</span>
                    </button>
                  )}
                  {showEtfModal && (
                    <ETFCalculatorModal
                      onClose={() => setShowEtfModal(false)}
                      defaultMRR={result?.finalMRR || ''}
                      defaultTerm={inputs?.contractTerm || 36}
                      defaultClientName={recipientBiz}
                      defaultClientAddress={recipientAddress}
                      defaultClientContact={recipientContact}
                      defaultClientEmail={recipientEmail}
                      defaultContractReference={existingQuote?.quote_number || ''}
                      quoteId={existingQuote?.id}
                      hubDealId={hubDealId}
                    />
                  )}

                  {/* Quote Notes Log */}
                  <QuoteNotes
                    quoteId={existingQuote?.id}
                    quoteNumber={existingQuote?.quote_number}
                    clientName={recipientBiz}
                    hubDealId={hubDealId}
                  />

                  {/* Revision History */}
                  <QuoteHistory quoteId={existingQuote?.id} />

                </div>
              </div>
            </div>
        }
        </div>{/* end inner scroll */}
      </div>

      {/* ── HUBSPOT MODAL ── */}

      {/* v3.5.81: Proposal URL modal. Shown immediately after sendProposal()
          succeeds. Surfaces the public URL with Copy + Open actions. The
          modal is dismissible — once closed, the URL is still always
          accessible via the proposal status banner above the right panel. */}
      {proposalModalOpen && activeProposal && (() => {
        const url = proposalPublicUrl(activeProposal.public_token);
        return (
          <div onClick={() => setProposalModalOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15, 30, 60, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: 'white', borderRadius: 10, padding: 24, width: 'min(560px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: 16, background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700 }}>✓</div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f1e3c' }}>Proposal Sent</h3>
              </div>
              <p style={{ fontSize: 12, color: '#374151', margin: '0 0 14px', lineHeight: 1.6 }}>
                Share this URL with <strong>{recipientBiz}</strong>. The proposal is valid for {activeProposal.validity_days || 30} days.
                When the client approves, you&rsquo;ll see a green banner on this quote with a <em>Send Service Order</em> action.
              </p>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', fontFamily: 'DM Mono, monospace', fontSize: 11, color: '#1e40af', wordBreak: 'break-all', marginBottom: 14 }}>
                {url}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { navigator.clipboard?.writeText(url); }}
                  style={{ padding: '8px 14px', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: 5, fontSize: 12, color: '#1e40af', fontWeight: 600, cursor: 'pointer' }}>
                  Copy URL
                </button>
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{ padding: '8px 14px', background: '#0f1e3c', color: 'white', border: '1px solid #0f1e3c', borderRadius: 5, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                  Open in New Tab ↗
                </a>
                <button onClick={() => setProposalModalOpen(false)}
                  style={{ padding: '8px 14px', background: 'white', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* v3.5.81: Send error toast. Only visible if sendProposal sets a non-empty
          message and the modal isn't open. Self-dismisses after 5 seconds. */}
      {proposalSendMsg && !proposalModalOpen && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '10px 14px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#991b1b', fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 150, maxWidth: 320 }}>
          {proposalSendMsg}
        </div>
      )}

      {/* v3.5.81.6: Customer Story & Assumptions modal. Renders only when
          showAssumptions is set. The modal handles its own data load (reads
          quote.inputs.assumptions on mount) and save (writes back to the
          same path); we pass an onSave callback so the local
          assumptionsStory state updates immediately on save, which means
          the Send Proposal gate flips green without a page reload. */}
      {showAssumptions && (
        <AssumptionsModal
          onClose={() => setShowAssumptions(false)}
          quoteId={existingQuote?.id}
          quoteNumber={existingQuote?.quote_number}
          clientName={recipientBiz}
          recipientContact={recipientContact}
          inputs={inputs}
          pkg={selectedPkg}
          products={products}
          settings={settings}
          complianceKey={complianceKey}
          onSave={(a) => {
            setAssumptionsStory(a.story || '');
            setAssumptionsSavedAt(a.savedAt || null);
          }}
        />
      )}
      {/* v3.5.99.4: Special Terms (Addendums) modal — mounted unconditionally,
          internal `open` prop controls visibility. */}
      <AddendumsModal
        open={addendumsModalOpen}
        onClose={() => setAddendumsModalOpen(false)}
        addendums={addendums}
        templates={addendumTemplates}
        onSave={setAddendums}
      />
      {/* v3.5.99.5: Service Order preview modal — controlled by the Preview button
          on the Documents card. docType is hardcoded per quote page since each
          page knows its own quote type. */}
      <ServiceOrderPreviewModal
        open={soPreviewModalOpen}
        onClose={() => setSoPreviewModalOpen(false)}
        quoteId={existingQuote?.id}
        docType="managed_it_quote"
        clientBiz={recipientBiz}
      />
    </div>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────────────
function Sec({t,c,children}){return(<div style={{marginBottom:10}}><div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5,paddingBottom:3,borderBottom:'1px solid #f1f5f9'}}><div style={{width:2,height:11,background:c||'#2563eb',borderRadius:2}}/><span style={{fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'#6b7280'}}>{t}</span></div>{children}</div>);}
function Grid2({children}){return <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>{children}</div>;}
function Fld({lbl,sub,children}){return(<div style={{marginBottom:4}}><label style={{display:'block',fontSize:9,fontWeight:600,color:'#374151',marginBottom:1}}>{lbl}{sub&&<span style={{fontWeight:400,color:'#9ca3af',marginLeft:3,fontSize:9}}>{sub}</span>}</label>{children}</div>);}
function TI({value,onChange,placeholder}){return <input value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder||''} style={{width:'100%',padding:'4px 7px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,outline:'none',color:'#374151'}}/>;}
function NI({v,s}){return <input type="number" value={v} min={0} onChange={e=>s(+e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,fontFamily:'DM Mono, monospace',color:'#1e3a5f',background:'#eff6ff',fontWeight:600,outline:'none'}}/>;}
function SI({v,s,opts}){return <select value={v} onChange={e=>s(e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,background:'white',outline:'none',color:'#374151'}}>{opts.map(([a,b])=><option key={a} value={a}>{b}</option>)}</select>;}
function Tog({on,set,lbl,sub}){return(<div onClick={()=>set(!on)} style={{display:'flex',alignItems:'center',gap:7,padding:'5px 7px',borderRadius:4,cursor:'pointer',border:`1px solid ${on?'#93c5fd':'#e5e7eb'}`,background:on?'#eff6ff':'white',marginBottom:2}}><div style={{width:24,height:14,borderRadius:7,flexShrink:0,background:on?'#2563eb':'#d1d5db',position:'relative'}}><div style={{position:'absolute',top:2,left:on?12:2,width:10,height:10,borderRadius:'50%',background:'white',transition:'left .12s'}}/></div><div><span style={{fontSize:10,fontWeight:600,color:on?'#1e40af':'#374151'}}>{lbl}</span>{sub&&<span style={{fontSize:9,color:'#9ca3af',marginLeft:4}}>{sub}</span>}</div></div>);}
function LI({lbl,v,ind,bold,hi,muted}){if(v===0&&!bold&&!hi)return null;return(<div style={{display:'flex',justifyContent:'space-between',padding:hi?'5px 7px':'1px 2px',marginLeft:ind?7:0,borderRadius:hi?4:0,background:hi?'#dcfce7':'transparent',borderTop:bold&&!hi?'1px solid #f3f4f6':'none',marginTop:bold&&!hi?2:0}}><span style={{fontSize:hi?9:8,fontWeight:bold||hi?700:400,color:hi?'#166534':muted?'#9ca3af':bold?'#374151':'#6b7280'}}>{lbl}</span><span style={{fontSize:hi?11:9,fontWeight:bold||hi?700:500,fontFamily:'DM Mono, monospace',color:hi?'#166534':v<0?'#dc2626':bold?'#111827':'#374151'}}>{v<0?`(${fmt$(-v)})`:fmt$(v)}</span></div>);}
function SH({l}){return <div style={{fontSize:8,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#9ca3af',padding:'4px 2px 1px',marginTop:2}}>{l}</div>;}
function catColor(cat){const m={'Cloud & Email Security':'#0891b2','Endpoint Security':'#7c3aed','Backup & Recovery':'#92400e','Security Awareness':'#065f46','SIEM & SOC':'#0891b2','Network & Connectivity':'#0f766e','Strategic Advisory':'#7c3aed'};return m[cat]||'#374151';}
